import { toApiUrl } from '../utils/apiBase';
import { get_embedded_sale_context, get_pos_headers, get_sale_origin_payload } from '../utils/embeddedSale';
// imei de prueba: 864500073106340
import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowBack, Search } from '@material-ui/icons';
import paso2 from '../images/paso2.png';
import paso3 from '../images/paso3.png';
import paso4 from '../images/paso4.png';
import paso5 from '../images/paso5.png';
import Firebase from '../firebase_setup';
import firestore from '../firestore';
import auth from '../auth';
import Spinner, { ContainerSpinner } from '../components/Spinner';
import EnrollmentLoadingOverlay from '../components/EnrollmentLoadingOverlay';
import {
	IphoneEnrollmentInstructions,
	ManagedAndroidEnrollmentInstructions,
	NuovoEnrollmentInstructions,
	DatacultrEnrollmentInstructions,
	EnrollmentIdentity,
	EnrollmentReady,
	LaptopEnrollmentInstructions,
	SecurityPermissionInstructions,
	getEnrollmentError
} from '../components/EnrollmentInstructions';
import { Dialog } from '../components/Dialog';
import Select from '../components/Select';
import {
	AlertTriangle,
	ArrowLeft,
	ArrowRight,
	Camera,
	CircleCheck,
	ClipboardList,
	FileText,
	IdCard,
	Info,
	KeyRound,
	PlusCircle,
	Receipt,
	ScanFace,
	Settings,
	Shield,
	Smartphone,
	Tablet,
	Laptop,
	ShoppingBag,
	ChevronDown,
	Award,
	TrendingUp,
	TicketPercent,
	Calculator,
	QrCode,
	Copy,
	ScanLine
} from 'lucide-react';
import Input from '../components/Input';
import QrImeiScanner from '../components/QrImeiScanner';
import { DatePicker } from '../shadcn/DatePicker';
import Stepper from '../components/Stepper';
import { Button } from '../shadcn/Button';
import { GlobalContext } from '../context/GlobalContext';
import classNames from 'classnames';
import Notice from '../components/Notice';
import { Popover, PopoverContent, PopoverTrigger } from '../shadcn/Popover';
import {
	apply_financing_overrides,
	calculate_user_financing_options,
	get_customer_financing_overrides,
	GetTogglablePromotions
} from '../hooks/firebase';
import { Switch } from '../shadcn/Switch';
import { Slider } from '../shadcn/Slider';
import * as Accordion from '@radix-ui/react-accordion';
import Logo from '../components/Logo';
import { callAPI, unsafeAPI } from '../hooks/api';
import { format_currency, format_identification_with_prefix } from '../utils/formatters';
import { getActiveImpersonationSession, getImpersonationHeaders } from '../utils/impersonation';
import { QRCodeSVG } from 'qrcode.react';
import LivenessDetection from './liveness-detection';
import copy from 'copy-to-clipboard';

// Helper functions for level theming
const getLevelName = (fullName) => {
	if (!fullName) return 'azul';
	const name = fullName.toLowerCase();
	if (name.includes('plata')) return 'plata';
	if (name.includes('oro')) return 'oro';
	if (name.includes('platino')) return 'platino';
	return 'azul'; // Default
};

const getLevelColor = (fullName) => {
	const level = getLevelName(fullName);
	const colors = {
		azul: 'hsl(184, 73%, 42%)',
		plata: '#727578',
		oro: '#D4AF37',
		platino: '#6D7684'
	};
	return colors[level] || colors.azul;
};

const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/bmp', 'image/heic'];
const SUPPORTED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'heic'];
const SUPPORTED_IMAGE_LABEL = SUPPORTED_IMAGE_EXTENSIONS.map((ext) => ext.toUpperCase()).join(', ');
const ACCEPTED_IMAGE_TYPES_ATTR = [...SUPPORTED_IMAGE_TYPES, ...SUPPORTED_IMAGE_EXTENSIONS.map((ext) => `.${ext}`)].join(',');
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

const isSupportedImageFormat = (file) => {
	if (!file) return false;
	const mimeType = file.type?.toLowerCase();
	if (mimeType && SUPPORTED_IMAGE_TYPES.includes(mimeType)) return true;

	const extension = file.name?.split('.').pop()?.toLowerCase();
	return SUPPORTED_IMAGE_EXTENSIONS.includes(extension || '');
};

const validateImageFile = (file) => {
	if (!file) {
		return 'No se seleccionó ninguna imagen.';
	}

	if (!isSupportedImageFormat(file)) {
		return `Formato no soportado. Utilice imágenes ${SUPPORTED_IMAGE_LABEL}.`;
	}

	if (file.size > MAX_IMAGE_SIZE_BYTES) {
		return 'La imagen debe pesar 5MB o menos.';
	}

	return null;
};

// Store financing options may optionally include levels; missing/empty levels keeps the legacy all-level behavior.
const getStoreFinancingOptionLevels = (option) => {
	if (!option) return [];

	if (Array.isArray(option.levels)) return option.levels.filter(Boolean);
	if (typeof option.levels === 'string') return [option.levels];
	if (typeof option.level === 'string') return [option.level];

	return [];
};

const storeFinancingOptionMatchesProduct = (option, productType) =>
	Array.isArray(option?.product_types) && option.product_types.includes(productType);

const storeFinancingOptionMatchesLevel = (option, level) => {
	const levels = getStoreFinancingOptionLevels(option);
	return levels.length === 0 || (!!level && levels.includes(level));
};

const isAppleBrand = (brand) => ['apple', 'iphone'].includes(String(brand || '').trim().toLowerCase());

const getRiskBrandOptions = (brands, country) => {
	const baseBrands = Array.isArray(brands) ? brands.filter(Boolean) : [];
	const shouldIncludeApple = country === 'rd' && !baseBrands.some(isAppleBrand);
	const brandsWithApple = shouldIncludeApple ? [...baseBrands, 'Apple'] : baseBrands;

	return brandsWithApple.toSorted().map((brand) => ({
		label: isAppleBrand(brand) ? 'Apple (iPhone)' : brand,
		value: brand
	}));
};

const findStoreFinancingOption = (options, productType, level, fieldName) => {
	if (!Array.isArray(options)) return null;

	const productOptions = options.filter((option) => storeFinancingOptionMatchesProduct(option, productType));
	const eligibleOptions = fieldName ? productOptions.filter((option) => option?.[fieldName]) : productOptions;
	const levelSpecificOption = eligibleOptions.find(
		(option) => getStoreFinancingOptionLevels(option).length > 0 && storeFinancingOptionMatchesLevel(option, level)
	);

	return levelSpecificOption || eligibleOptions.find((option) => getStoreFinancingOptionLevels(option).length === 0) || null;
};

// Mirrors the backend's apply_elite_benefit (registration validates sales against it):
// elite options are added or improve existing factors, except when the store disabled
// financing entirely (explicitly empty list).
const mergeElitePaymentOptions = (paymentOptions, eliteBenefit) => {
	if (!eliteBenefit?.additional_payment_options?.length || paymentOptions?.length === 0) return paymentOptions;
	const optionsByInstallments = new Map((paymentOptions || []).map((option) => [option.installments, option]));
	for (const option of eliteBenefit.additional_payment_options) {
		if (!option?.installments || !option?.factor) continue;
		const existingOption = optionsByInstallments.get(option.installments);
		optionsByInstallments.set(option.installments, {
			installments: option.installments,
			factor: existingOption?.factor ? Math.min(existingOption.factor, option.factor) : option.factor,
			elite: true
		});
	}
	return Array.from(optionsByInstallments.values()).sort((a, b) => a.installments - b.installments);
};

const resolveStorePaymentOptions = (storeOptions, fallbackPaymentOptions) => {
	if (!Array.isArray(storeOptions?.payment_options)) return null;
	if (storeOptions.payment_options.length === 0) return [];

	const fallbackOptionsByInstallments = new Map(
		(fallbackPaymentOptions || [])
			.filter((option) => option?.installments && option?.factor)
			.map((option) => [option.installments, option])
	);
	const paymentOptions = storeOptions.payment_options.reduce((acc, option) => {
		if (!option?.installments) return acc;

		const fallbackOption = fallbackOptionsByInstallments.get(option.installments);
		const factor = option.factor || fallbackOption?.factor;
		if (!factor) return acc;

		acc.push({
			...option,
			factor
		});
		return acc;
	}, []);

	return paymentOptions.length > 0 ? paymentOptions : null;
};

const shouldCompressImage = (file) => {
	if (!file) return false;
	const mimeType = file.type?.toLowerCase();
	if (mimeType === 'image/heic' || mimeType === 'image/heif') return false;

	const extension = file.name?.split('.').pop()?.toLowerCase();
	if (extension === 'heic' || extension === 'heif') return false;

	return true;
};

const normalizeDailyLimitStoreName = (value = '') =>
	value
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/-/g, '')
		.replace(/[”’@'"`,.)(+\/\s]/g, '')
		.replace(/&/g, 'and');

const NewSale = ({ simulator = false, store_recharge_coupon = false }) => {
	const location = useLocation();
	const embedded = !!get_embedded_sale_context(location);
	const locationState = location.state || {};
	const pre_registered_device_id = locationState.pre_registered_device_id || null;

	// Fix for 31st of month
	let currentDay = new Date().getDate();
	if (currentDay === 31) {
		currentDay = 30;
	}

	const { userData, LevelsConfig, CountrySettings, internalCountry, apkUrl } = useContext(GlobalContext);
	const impersonationHeaders = getImpersonationHeaders(getActiveImpersonationSession(userData?.email));
	const uidPrefix = CountrySettings?.uid_prefix || '';

	// Master store data
	const storeUser = userData?.email;
	const storeEmail = userData?.master_store?.email;
	const subStoreList =
		userData?.master_user === true
			? userData?.master_store?.sub_stores || []
			: userData?.store_name
				? [userData?.store_name]
				: [];
	const country = internalCountry?.get() || userData?.master_store?.country;
	const storeName = userData?.master_store?.store_name;
	const selfFinanced = userData?.master_store?.self_financed === true ? true : false;
	const allowIphone = userData?.master_store?.allow_iphone === true ? true : false;
	const allowLaptop = userData?.master_store?.allow_laptop === true ? true : false;
	const allowOnline = userData?.master_store?.allow_online === true ? true : false;
	const isAccountInactive = !userData?.master_store?.is_active ? true : false;
	const [deviceManagementProvider, setDeviceManagementProvider] = useState('tonic');
	const [enrollmentReady, setEnrollmentReady] = useState(false);
	const [installationSession, setInstallationSession] = useState(null);
	const enrollmentRequest = useRef(false);
	const storeFinancingOptions = userData?.master_store?.additional_financing_options || userData?.additional_financing_options;
	const disableSalePhone2fa = CountrySettings?.feature_flags?.disable_sale_phone_2fa === true;
	const onlineSalesBetaAccess =
		userData?.online_sales_beta_access === true && userData?.master_store?.online_sales_beta_access === true ? true : false;

	// Other states
	const [cneData, setCneData] = useState({});
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [email, setEmail] = useState('');
	const [email2, setEmail2] = useState('');
	const [cedula, setCedula] = useState('');
	const [phoneNumber, setPhoneNumber] = useState('');
	const [birthDate, setBirthDate] = useState('');
	const [address, setAddress] = useState('');
	const [gender, setGender] = useState('');
	const [formError, setFormError] = useState('');
	const [cedulaPhoto, setCedulaPhoto] = useState('');
	const [cedulaPhotoBack, setCedulaPhotoBack] = useState('');
	const [userPhoto, setUserPhoto] = useState('');
	const [cedulaVerified, setCedulaVerified] = useState(false);
	const [showSpinner, setShowSpinner] = useState(false);
	const [city, setCity] = useState('');
	const [anexoAccepted, setAnexoAccepted] = useState(false);
	const [timeLeft, setTimeLeft] = useState(0);
	const [messType, setMessType] = useState(country === 'peru' || country === 'bolivia' ? 'whatsapp' : 'sms');
	useEffect(() => {
		if (country === 'peru' || country === 'bolivia') {
			setMessType('whatsapp');
		} else {
			setMessType('sms');
		}
	}, [country]);
	const [monthlySalary, setMonthlySalary] = useState('');
	const [savings, setSavings] = useState('');
	const [educationLevel, setEducationLevel] = useState('');
	const [subStore, setSubStore] = useState('');
	const [eliteFinancing, setEliteFinancing] = useState(null);
	const [eliteFinancingBranch, setEliteFinancingBranch] = useState('');
	const [eliteFinancingLoading, setEliteFinancingLoading] = useState(false);
	const [eliteFinancingError, setEliteFinancingError] = useState(false);
	const [firstDeviceManufacturer, setFirstDeviceManufacturer] = useState('');
	const [firstDevicePrice, setFirstDevicePrice] = useState('');
	useEffect(() => {
		if (subStoreList?.length === 1 && !subStore) setSubStore(subStoreList[0]);
		// eslint-disable-next-line
	}, [subStoreList]);

	useEffect(() => {
		let cancelled = false;
		setEliteFinancing(null);
		setEliteFinancingBranch(subStore);
		setEliteFinancingError(false);
		if (!subStore) {
			setEliteFinancingLoading(false);
			return () => {
				cancelled = true;
			};
		}
		setEliteFinancingLoading(true);

		void callAPI(
			'https://api.krece.app/storeprofile/elite_financing_options/',
			'GET',
			{ sub_store: subStore },
			userData,
			country
		).then(([data, error]) => {
			if (cancelled) return;
			setEliteFinancing(error ? null : data);
			setEliteFinancingError(!!error);
			setEliteFinancingLoading(false);
		});

		return () => {
			cancelled = true;
		};
	}, [country, subStore, userData]);
	// Ready once the fetch for the selected branch settles. A failed fetch counts as ready
	// with no elite benefits (fail open): the sale proceeds on standard terms instead of
	// blocking the revenue path on this endpoint's availability.
	const eliteFinancingReady =
		!!subStore && eliteFinancingBranch === subStore && !eliteFinancingLoading && (eliteFinancingError || !!eliteFinancing);
	const [showTokenField, setShowTokenField] = useState(false);
	const [phoneToken, setPhoneToken] = useState('');
	const [loadingButton, setLoadingButton] = useState(false);
	const [tokenVerified, setTokenVerified] = useState(false);
	const [cneIdentified, setCneIdentified] = useState(false);
	const [cedulaExtranjera, setCedulaExtranjera] = useState(false);
	const [loginError, setLoginError] = useState('');
	const [token, setToken] = useState(process.env.REACT_APP_API_TOKEN);
	const [deviceID, setDeviceID] = useState('');
	const [imeiNumber, setImeiNumber] = useState('');
	const [confNumber, setConfNumber] = useState('');
	const [imeiToRegister, setImeiToRegister] = useState('');
	const [imeiToRegister2, setImeiToRegister2] = useState('');
	const [showQrScanner, setShowQrScanner] = useState(false);
	const [tokenGenerated, setTokenGenerated] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSearchingConfirmationNumber, setIsSearchingConfirmationNumber] = useState(false);
	const [bypassCode, setBypassCode] = useState('');
	const [bypassCodeLoading, setBypassCodeLoading] = useState(false);
	const [bypassCodeError, setBypassCodeError] = useState('');
	const [loadingText, setLoadingText] = useState(false);
	const [loadingSubText, setLoadingSubText] = useState(false);
	const [showImei, setShowImei] = useState(false);
	const [showConfNumber, setShowConfNumber] = useState(false);
	const [page, setPage] = useState(simulator ? 4 : 2);
	useEffect(() => setEnrollmentReady(false), [page]);
	useEffect(() => {
		setBypassCode('');
		setBypassCodeError('');
	}, [imeiToRegister]);
	const [documentType, setDocumentType] = useState('');
	const [dialogMessage, setDialogMessage] = useState('');
	const [showDialog, setShowDialog] = useState(false);
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [limitIncreaseDialog, setLimitIncreaseDialog] = useState(null);
	const limitIncreaseRetryRef = useRef(null);
	const fileInputRef = useRef(null);
	const fileInputRefBack = useRef(null);

	const resetDocumentPhotos = useCallback(() => {
		setCedulaPhoto('');
		setCedulaPhotoBack('');
		setCedulaVerified(false);
		if (fileInputRef?.current) {
			fileInputRef.current.value = '';
		}
		if (fileInputRefBack?.current) {
			fileInputRefBack.current.value = '';
		}
	}, [fileInputRef, fileInputRefBack]);

	// Reset photos when document type changes
	useEffect(() => {
		if (!documentType) return;
		resetDocumentPhotos();
	}, [documentType, resetDocumentPhotos]);

	// Simulator mode states
	const [manualApprovedCredit, setManualApprovedCredit] = useState(0);
	const [selectedLevel, setSelectedLevel] = useState(simulator ? 'level_1' : '');
	const [simulatorCedula, setSimulatorCedula] = useState('');
	const [isLoadingSimulatorUser, setIsLoadingSimulatorUser] = useState(false);
	const [simulatorUserLoaded, setSimulatorUserLoaded] = useState(false);
	const [simulatorError, setSimulatorError] = useState('');
	useEffect(() => {
		if (!simulator || !country || !selectedLevel) return;

		const credit_line = LevelsConfig?.[country]?.[selectedLevel]?.credit_line;

		setManualApprovedCredit(credit_line || (country === 'peru' ? 700 : country === 'rd' ? 7000 : country === 'bolivia' ? 2700 : 300));
	}, [selectedLevel, country, simulator, LevelsConfig]);

	// Togglable promotions
	const [userDocId, setUserDocId] = useState('');
	const {
		promotions: store_togglable_promotions,
		loaded: promotionsLoaded,
		loadedCustomerDocumentId: promotionsCustomerDocumentId,
		error: promotionsError
	} = GetTogglablePromotions(
		country,
		userData?.email,
		userData?.master_store?.email,
		userDocId || null,
		userData
	);
	const promotionContext = JSON.stringify([country, userData?.email, userData?.master_store?.email, userDocId]);
	const [promotionSelection, setPromotionSelection] = useState(null);
	const selectedPromo = promotionSelection?.context === promotionContext ? promotionSelection.promo : null;
	const setSelectedPromo = useCallback(
		(promo) => setPromotionSelection(promo ? { promo, context: promotionContext } : null),
		[promotionContext]
	);
	const [preRegisteredPromotion, setPreRegisteredPromotion] = useState(null);

	// User data
	const [isContinueApplication, setIsContinueApplication] = useState(false);
	const [registrationStatus, setRegistrationStatus] = useState('');
	const [userCanPurchaseOthers, setUserCanPurchaseOthers] = useState(simulator ? true : false);
	const [userLevel, setUserLevel] = useState(simulator ? selectedLevel : '');
	const [levelPoints, setLevelPoints] = useState(0);
	const [userFinancingOptions, setUserFinancingOptions] = useState(null);
	// The customer's own conditions (Users.financing_overrides) as the backend applies them now
	const [customerFinancingOverrides, setCustomerFinancingOverrides] = useState(null);
	const financingOverridesExpiryRef = useRef(null);
	const financingOverridesExpiredRef = useRef(false);
	const overrideQuoteErrorRef = useRef('');
	const effectiveFinancingOptions = useMemo(
		() => apply_financing_overrides(userFinancingOptions, customerFinancingOverrides),
		[userFinancingOptions, customerFinancingOverrides]
	);
	const [paymentOptions, setPaymentOptions] = useState(null);
	const [downPaymentOptions, setDownPaymentOptions] = useState(null);
	const [dynamicDownPaymentPct, setDynamicDownPaymentPct] = useState(null);
	const [minDownPaymentPct, setMinDownPaymentPct] = useState(null);
	const [loadingVariableFactor, setLoadingVariableFactor] = useState(false);

	// Application approval data
	const [approvedCredit, setApprovedCredit] = useState(simulator ? manualApprovedCredit : 0);
	const [primaryCreditLine, setPrimaryCreditLine] = useState(simulator ? manualApprovedCredit : 0);
	const [secondaryCreditLine, setSecondaryCreditLine] = useState(simulator ? manualApprovedCredit : 0);
	const [maxFinanceableDevicePrice, setMaxFinanceableDevicePrice] = useState(0);
	const [productType, setProductType] = useState('phone');
	const [productManufacturer, setProductManufacturer] = useState('');
	const [productName, setProductName] = useState('');
	const [price, setPrice] = useState(0);
	const [downPaymentPerc, setDownPaymentPerc] = useState(0);
	const [initialPaymentAmount, setInitialPaymentAmount] = useState(0);
	const [installments, setInstallments] = useState(0);
	const [installmentsAmount, setInstallmentsAmount] = useState(0);
	const [devicePrice, setDevicePrice] = useState(0);
	const [totalAmount, setTotalAmount] = useState(0);
	const [discountedAmount, setDiscountedAmount] = useState(0);
	const [discountedPercentage, setDiscountedPercentage] = useState(0);
	const [calculatedFinancingKey, setCalculatedFinancingKey] = useState('');
	const [isOnline, setIsOnline] = useState(false);
	const [savingOnlineSale, setSavingOnlineSale] = useState(false);
	const [onlineSaleRegistered, setOnlineSaleRegistered] = useState(false);

	// Liveness states
	/** @type {[{sessionId: string, documentId: string, qrUrl: string, results?: Object}|null, React.Dispatch<React.SetStateAction<{sessionId: string, documentId: string, qrUrl: string, results?: Object}|null>>]} */
	const [liveness, setLiveness] = useState(null);
	const [hideStepper, setHideStepper] = useState(false);

	// Rate limiting for liveness initialization
	const [livenessRateLimitTimer, setLivenessRateLimitTimer] = useState(0);
	const [isLivenessRateLimited, setIsLivenessRateLimited] = useState(false);
	const disable_liveness_detection = CountrySettings?.biometric_available_methods?.liveness_detection !== true;
	const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname === "5.231.45.74";
	const level1PromoBlockThreshold = Number(CountrySettings?.level_1_min_down_payment_pct_promo_block_threshold);
	const shouldBlockPromotionsForMinDownPayment =
		Number.isFinite(level1PromoBlockThreshold) &&
		level1PromoBlockThreshold >= 0 &&
		userLevel === 'level_1' &&
		minDownPaymentPct !== null &&
		minDownPaymentPct >= level1PromoBlockThreshold;
	const eligibleTogglablePromotions = useMemo(
		() =>
			shouldBlockPromotionsForMinDownPayment
				? []
				: store_togglable_promotions?.filter(
						(promo) =>
							!promo?.levels ||
							promo?.levels?.length === 0 ||
							promo?.levels?.some((level) => level === selectedLevel || level === userLevel)
					) || [],
		[shouldBlockPromotionsForMinDownPayment, store_togglable_promotions, selectedLevel, userLevel]
	);

	// Only current, validated promotions can affect financing or sale payloads.
	const activePromo = selectedPromo?.label
		? eligibleTogglablePromotions.find((promo) => promo?.label === selectedPromo.label) || null
		: null;
	const preRegisteredPromotionReady =
		!pre_registered_device_id ||
		!!(preRegisteredPromotion &&
			preRegisteredPromotion.customerDocumentId === userDocId &&
			preRegisteredPromotion.country === country &&
			(!preRegisteredPromotion.code ||
				(promotionsLoaded && !promotionsError && activePromo?.label === preRegisteredPromotion.code)));
	const preRegisteredPromotionError = preRegisteredPromotionReady
		? ''
		: !preRegisteredPromotion || !promotionsLoaded
			? 'Validando la promoción de la venta. Espere antes de continuar.'
			: promotionsError || 'La promoción guardada ya no está disponible para este cliente. No puede continuar con las condiciones guardadas.';
	const hasAvailableOtherProductCredit =
		simulator ||
		Number(secondaryCreditLine) > 0 ||
		(Number(activePromo?.approved_credit) > 0 && activePromo?.product_types?.includes('other'));
	const canRegisterOtherProduct = userCanPurchaseOthers && hasAvailableOtherProductCredit;
	const otherProductRestrictionMessage = !userCanPurchaseOthers
		? 'El cliente no está habilitado para comprar otros productos.'
		: 'El cliente no tiene cupo disponible para otros productos.';

	// Copy button state
	const [isCopySuccess, setIsCopySuccess] = useState(false);

	useEffect(() => {
		if (!preRegisteredPromotion?.code || !promotionsLoaded || promotionsError) return;
		if (
			preRegisteredPromotion.customerDocumentId === userDocId &&
			preRegisteredPromotion.country === country &&
			promotionsCustomerDocumentId === userDocId
		) {
			const restoredPromo = eligibleTogglablePromotions.find((promo) => promo?.label === preRegisteredPromotion.code);
			if (restoredPromo && selectedPromo !== restoredPromo) setSelectedPromo(restoredPromo);
		}
	}, [
		preRegisteredPromotion,
		promotionsLoaded,
		promotionsError,
		promotionsCustomerDocumentId,
		userDocId,
		country,
		eligibleTogglablePromotions,
		selectedPromo,
		setSelectedPromo
	]);

	useEffect(() => {
		if (promotionSelection && promotionSelection.context !== promotionContext) {
			setPromotionSelection(null);
		} else if (promotionsLoaded && selectedPromo && !activePromo) {
			setPromotionSelection(null);
		}
	}, [promotionSelection, promotionContext, promotionsLoaded, selectedPromo, activePromo]);

	// Rate limit countdown timer
	useEffect(() => {
		let interval = null;

		if (livenessRateLimitTimer > 0) {
			setIsLivenessRateLimited(true);
			interval = setInterval(() => {
				setLivenessRateLimitTimer((prevTimer) => {
					if (prevTimer <= 1) {
						setIsLivenessRateLimited(false);
						return 0;
					}
					return prevTimer - 1;
				});
			}, 1000);
		} else {
			setIsLivenessRateLimited(false);
		}

		return () => {
			if (interval) clearInterval(interval);
		};
	}, [livenessRateLimitTimer]);

	async function initializeLiveness() {
		// Check rate limit
		if (isLivenessRateLimited) {
			setFormError(`Por favor espere ${livenessRateLimitTimer} segundos antes de intentar nuevamente.`);
			return false;
		}

		setIsLoading(true);
		setFormError('');
		try {
			const formattedCedula = format_identification_with_prefix(cedula, uidPrefix);
			const response = await unsafeAPI(
				'GET',
				`https://api.krece.app/authentication/liveness-detection-session/?user_identification_number=${encodeURIComponent(formattedCedula)}`,
				null,
				null,
				null,
				false
			);

			if (response?.success && response?.data?.session_id && response?.data?.document_id) {
				let qrUrl = `${window.location.origin}/liveness-detection?liveness_session_id=${response.data.session_id}&document_id=${response.data.document_id}&user_identification_number=${encodeURIComponent(formattedCedula)}`;

				// Add user identification photo if new user
				if (cedulaPhoto) {
					qrUrl += `&identification_id_url=${encodeURIComponent(cedulaPhoto)}&is_new_user=true`;
				}

				setLiveness({
					sessionId: response.data.session_id,
					documentId: response.data.document_id,
					qrUrl
				});

				setLivenessRateLimitTimer(30);

				return true;
			} else {
				throw new Error();
			}
		} catch (error) {
			console.error('Error initializing liveness:', error);
			setLiveness(null);
			setFormError('Ocurrió un error iniciando la verificación biométrica, por favor intente más tarde.');
			return false;
		} finally {
			setIsLoading(false);
		}
	}

	const [hasCameraAvailable, setHasCameraAvailable] = useState(false);
	const [isCameraFunctional, setIsCameraFunctional] = useState(false);
	const [cameraTestInProgress, setCameraTestInProgress] = useState(false);

	useEffect(() => {
		const checkCameraAvailability = async () => {
			try {
				if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
					const devices = await navigator.mediaDevices.enumerateDevices();
					const hasCamera = devices.some((device) => device.kind === 'videoinput');
					setHasCameraAvailable(hasCamera);
				} else {
					setHasCameraAvailable(false);
				}
			} catch (error) {
				console.error('Error checking camera availability:', error);
				setHasCameraAvailable(false);
			}
		};

		if (!disable_liveness_detection) checkCameraAvailability();
	}, [disable_liveness_detection]);

	const testCameraFunctionality = async () => {
		if (!hasCameraAvailable || cameraTestInProgress || disable_liveness_detection) return false;

		setCameraTestInProgress(true);
		let stream = null;

		try {
			// Request camera permissions and test actual stream with 15s timeout
			const timeoutPromise = new Promise((_, reject) => {
				setTimeout(() => reject(new Error('Camera test timeout')), 15000);
			});

			const cameraPromise = navigator.mediaDevices.getUserMedia({
				video: {
					width: { ideal: 640 },
					height: { ideal: 480 }
				}
			});

			stream = await Promise.race([cameraPromise, timeoutPromise]);

			// Check if stream has video tracks and they're active
			const videoTracks = stream.getVideoTracks();
			const isFunctional = videoTracks.length > 0 && videoTracks[0].readyState === 'live';

			setIsCameraFunctional(isFunctional);
			return isFunctional;
		} catch (error) {
			setIsCameraFunctional(false);
		} finally {
			// Clean up stream
			if (stream) {
				stream?.getTracks()?.forEach((track) => track?.stop?.());
			}
			setCameraTestInProgress(false);
		}
	};

	// Initialize simulator data
	useEffect(() => {
		if (!simulator || simulatorUserLoaded) return;

		if (LevelsConfig && country) {
			// Set dummy financing options for simulator mode
			clearTimeout(financingOverridesExpiryRef.current);
			setCustomerFinancingOverrides(null);
			setUserFinancingOptions(
				calculate_user_financing_options(
					LevelsConfig,
					country,
					selectedLevel,
					userData?.additional_financing_options || null,
					userData?.additional_price_ranges || null
				)
			);

			// Set approved credit
			setPrimaryCreditLine(manualApprovedCredit);
			setSecondaryCreditLine(manualApprovedCredit);

			// Make sure page is always 4 in simulator mode
			setPage(4);
		}
	}, [simulator, LevelsConfig, country, selectedLevel, manualApprovedCredit, userData, simulatorUserLoaded]);

	// Update user financing options when level changes in simulator mode or others is selected
	useEffect(() => {
		if (!simulator || simulatorUserLoaded) return;

		if (LevelsConfig && country && selectedLevel) {
			setUserFinancingOptions(
				calculate_user_financing_options(
					LevelsConfig,
					country,
					selectedLevel,
					userData?.additional_financing_options || null,
					userData?.additional_price_ranges || null
				)
			);
			setUserLevel(selectedLevel);
		}

		// if (productType === 'other') {
		// 	setManualApprovedCredit(country === 'peru' ? 100 : country === 'rd' ? 6000 : 50);
		// } else if (productType !== 'other') {
		// 	setManualApprovedCredit(country === 'peru' ? 700 : country === 'rd' ? 12000 : 300);
		// }
	}, [simulator, selectedLevel, LevelsConfig, country, userData, productType, simulatorUserLoaded]);

	const [preRegAmountFinanced, setPreRegAmountFinanced] = useState(0);
	// Handle pre-registered device
	useEffect(() => {
		const loadPreRegisteredSale = async () => {
			if (!pre_registered_device_id) return;

			setIsLoading(true);
			setFormError('');
			setPreRegisteredPromotion(null);

			try {
				const response = await unsafeAPI(
					'GET',
					`https://api.krece.app/registration/preregistereddevices/?id=${pre_registered_device_id}`,
					null,
					userData,
					country
				);

				if (response?.success && response?.data && typeof response.data === 'object') {
					const data = response.data;

					setProductType(data.device_type);
					setDevicePrice(Number(data.device_price));
					setPrice(Number(data.device_price));
					setInstallments(data.loan_duration * 2);
					setInstallmentsAmount(data.payments_amount);
					setSubStore(data.sub_store || '');
					setProductManufacturer(data.device_manufacturer || '');
					setPreRegAmountFinanced(data.amount_financed);

					let rawCedula = data.identification_number;
					if (uidPrefix && rawCedula.startsWith(uidPrefix)) {
						rawCedula = rawCedula.slice(uidPrefix.length);
					}
					setCedula(rawCedula);

					const customerDocumentId = await handleCheckCedula(null, rawCedula);
					if (!customerDocumentId) throw new Error('No se encontró al usuario de esta venta');
					setPreRegisteredPromotion({ code: data.promotional_code || null, customerDocumentId, country });

					listenToAnexoDocumentByIdentificationNumber(rawCedula);

					setPage(data.device_type === 'laptop' ? 22 : 10);
				} else {
					setFormError(response?.status || 'Error al cargar la venta online.');
				}
			} catch (error) {
				setFormError(
					error?.message === 'No se encontró al usuario de esta venta'
						? 'No se encontró al usuario de esta venta'
						: 'Error de conexión al cargar la venta online.'
				);
			} finally {
				setIsLoading(false);
			}
		};

		loadPreRegisteredSale();
		// eslint-disable-next-line
	}, [pre_registered_device_id]);

	const iphoneFinancingOptionsAvailable = useMemo(() => {
		const storeOptions = findStoreFinancingOption(storeFinancingOptions, 'iphone', userLevel || selectedLevel, 'payment_options');
		const storePaymentOptions = resolveStorePaymentOptions(storeOptions, effectiveFinancingOptions?.iphone?.payment_options);

		return (
			effectiveFinancingOptions?.iphone?.payment_options?.length > 0 ||
			!!storePaymentOptions?.length ||
			activePromo?.product_types?.includes('iphone')
		);
	}, [effectiveFinancingOptions, storeFinancingOptions, activePromo, userLevel, selectedLevel]);

	const laptopFinancingOptionsAvailable = useMemo(() => {
		const storeOptions = findStoreFinancingOption(storeFinancingOptions, 'laptop', userLevel || selectedLevel, 'payment_options');
		const storePaymentOptions = resolveStorePaymentOptions(storeOptions, effectiveFinancingOptions?.laptop?.payment_options);

		return (
			effectiveFinancingOptions?.laptop?.payment_options?.length > 0 ||
			!!storePaymentOptions?.length ||
			activePromo?.product_types?.includes('laptop')
		);
	}, [effectiveFinancingOptions, storeFinancingOptions, activePromo, userLevel, selectedLevel]);

	useEffect(() => {
		if (
			productType !== 'other' &&
			!activePromo?.approved_credit &&
			!primaryCreditLine &&
			secondaryCreditLine > 0 &&
			userCanPurchaseOthers
		) {
			setProductType('other');
		}
	}, [activePromo?.approved_credit, primaryCreditLine, productType, secondaryCreditLine, userCanPurchaseOthers]);

	// Set credit line, payment options and down payment options based on user level, active promos or store settings
	useEffect(() => {
		if (!effectiveFinancingOptions || !productType || pre_registered_device_id) return;

		const selectedCreditLine = productType === 'other' && !activePromo?.approved_credit ? secondaryCreditLine : primaryCreditLine;
		if (!selectedCreditLine && !activePromo?.approved_credit) {
			setApprovedCredit(0);
			setPaymentOptions(null);
			setDownPaymentOptions(null);
			setDownPaymentPerc(0);
			setInstallments(0);
			setMaxFinanceableDevicePrice(0);
			setInitialPaymentAmount(0);
			setInstallmentsAmount(0);
			setTotalAmount(0);
			setDiscountedAmount(0);
			setDiscountedPercentage(0);
			setCalculatedFinancingKey('');
			return;
		}

		if (productType === 'iphone' && !iphoneFinancingOptionsAvailable) {
			setProductType('phone');
			return;
		}

		if (productType === 'laptop' && !laptopFinancingOptionsAvailable) {
			setProductType('phone');
			return;
		}

		// Custom store financing options
		const storePaymentOptions = findStoreFinancingOption(
			storeFinancingOptions,
			productType,
			userLevel || selectedLevel,
			'payment_options'
		);
		const storeDownPaymentOptions = findStoreFinancingOption(
			storeFinancingOptions,
			productType,
			userLevel || selectedLevel,
			'down_payment'
		);
		const resolvedStorePaymentOptions = resolveStorePaymentOptions(
			storePaymentOptions,
			effectiveFinancingOptions[productType].payment_options
		);
		// The customer's own conditions for this product (backend: they take priority over
		// promo, store and Elite; a store that disabled the product still blocks it)
		const productOverride = customerFinancingOverrides?.[productType];
		const storeDisabledProduct = !activePromo && resolvedStorePaymentOptions?.length === 0;

		const eliteBenefit = !activePromo && eliteFinancingReady && eliteFinancing?.benefits_active
			? eliteFinancing?.benefits_by_level?.[userLevel || selectedLevel]?.[productType]
			: null;

		// If promo is set, use it, otherwise use the credit line for the selected product type
		const approved_credit = activePromo?.approved_credit ? activePromo?.approved_credit : selectedCreditLine || 0;
		setApprovedCredit(approved_credit);

		// Set payment options, first check active promo, then store settings, then user settings
		/** @type {{ factor: number; installments: number; }[]} */
		let payment_options = [];
		if (productOverride?.payment_options) {
			payment_options = storeDisabledProduct ? [] : productOverride.payment_options.map((option) => ({ ...option }));
		} else if (activePromo?.payment_options) {
			payment_options = activePromo.payment_options;
		} else if (resolvedStorePaymentOptions) {
			payment_options = mergeElitePaymentOptions(resolvedStorePaymentOptions, eliteBenefit);
		} else {
			payment_options = mergeElitePaymentOptions(effectiveFinancingOptions[productType].payment_options, eliteBenefit);
		}

		payment_options.sort((a, b) => a.installments - b.installments);
		setPaymentOptions(payment_options);

		// Set down payment options, first check active promo, then store settings, then user settings
		const promoHasInitialPercentage =
			activePromo?.initial_percentage !== null && activePromo?.initial_percentage !== undefined;
		/** @type {{ min: number; max: number; granularity: number; }} */
		let down_payment_options = promoHasInitialPercentage
			? { min: activePromo.initial_percentage, max: activePromo.initial_percentage, granularity: 0 }
			: {
					min: (storeDownPaymentOptions?.down_payment?.min ?? effectiveFinancingOptions[productType].down_payment.min ?? 40) / 100,
					max: (storeDownPaymentOptions?.down_payment?.max ?? effectiveFinancingOptions[productType].down_payment.max ?? 40) / 100,
					granularity:
						(storeDownPaymentOptions?.down_payment?.granularity ??
							effectiveFinancingOptions[productType].down_payment.granularity ??
							0) / 100
				};
		const eliteHasDownPayment =
			eliteBenefit?.down_payment_percent !== null && eliteBenefit?.down_payment_percent !== undefined;
		if (!activePromo && eliteHasDownPayment) {
			const eliteDownPayment = eliteBenefit.down_payment_percent / 100;
			down_payment_options.min = Math.min(down_payment_options.min, eliteDownPayment);
			down_payment_options.max = Math.max(down_payment_options.max, eliteDownPayment);
		}

		// Apply user-specific min_down_payment_pct (level_1 only, no promo)
		if (minDownPaymentPct !== null && userLevel === 'level_1' && !activePromo) {
			down_payment_options.min = minDownPaymentPct / 100;
			down_payment_options.max = Math.max(down_payment_options.max, minDownPaymentPct / 100);
		}

		// If API provided down_payment_pct, enforce it as minimum
		if (dynamicDownPaymentPct !== null && !promoHasInitialPercentage) {
			down_payment_options.min = Math.max(down_payment_options.min, dynamicDownPaymentPct);
			down_payment_options.max = Math.max(down_payment_options.max, dynamicDownPaymentPct);
		}

		// The customer's own down payment replaces all of the above; the credit floor below still
		// applies because it is capacity, not pricing
		if (productOverride?.down_payment) {
			down_payment_options = {
				min: productOverride.down_payment.min / 100,
				max: productOverride.down_payment.max / 100,
				granularity: productOverride.down_payment.granularity / 100
			};
		}

		// Handle cases in which the user can't finance the selected price with their minimum down payment perc, update the min
		if (price && approved_credit) {
			const credit = approved_credit === Infinity ? 100000000000 : approved_credit;

			// Calculate minimum down payment percentage needed to finance the selected price
			const minDownPaymentPerc = Math.max(0, 1 - credit / parseInt(price));

			if (minDownPaymentPerc > down_payment_options.min) {
				down_payment_options.min = parseFloat(Math.min(minDownPaymentPerc, 1).toFixed(2));
				down_payment_options.max = parseFloat(Math.max(down_payment_options.max, minDownPaymentPerc).toFixed(2));
			}
		}

		setDownPaymentOptions(down_payment_options);
	}, [
		effectiveFinancingOptions,
		customerFinancingOverrides,
		productType,
		activePromo,
		storeFinancingOptions,
		primaryCreditLine,
		secondaryCreditLine,
		price,
		dynamicDownPaymentPct,
		minDownPaymentPct,
		userLevel,
		selectedLevel,
		pre_registered_device_id,
		eliteFinancing,
		eliteFinancingReady
	]);

	const configuredInstallmentOptions = useMemo(
		() => (paymentOptions || []).map((option) => option.installments).filter((option) => option),
		[paymentOptions]
	);
	const selectedInstallmentIndex = Math.max(0, configuredInstallmentOptions.indexOf(installments));
	const isDownPaymentValid =
		downPaymentOptions !== null &&
		Number.isFinite(downPaymentPerc) &&
		downPaymentPerc >= downPaymentOptions.min &&
		downPaymentPerc <= downPaymentOptions.max;

	// Auto-select best option for the user based on available options (except for RD)
	useEffect(() => {
		if (!paymentOptions || !downPaymentOptions || pre_registered_device_id) return;
		if (paymentOptions.length === 0) {
			setInstallments(0);
			return;
		}

		if (isDownPaymentValid && installments && paymentOptions?.find((option) => option.installments === installments))
			return;

		setDownPaymentPerc(Math.min(downPaymentOptions.min, downPaymentOptions.max));
		if (country !== 'rd') {
			setInstallments(paymentOptions[paymentOptions.length - 1].installments);
		} else {
			setInstallments(paymentOptions[0].installments);
		}
		//eslint-disable-next-line
	}, [paymentOptions, downPaymentOptions]);

	const financingCalculationKey = JSON.stringify({
		productType,
		price,
		downPaymentPerc,
		installments,
		approvedCredit,
		paymentOptions,
		downPaymentOptions,
		activePromo: activePromo?.label || null,
		productManufacturer,
		factorUserDocumentId: userDocId || null,
		factorLevel: userLevel || selectedLevel || 'level_1',
		factorCity: userData?.city || null,
		country,
		customerOverride: customerFinancingOverrides?.[productType] || null
	});
	const isFinancingCalculationReady = calculatedFinancingKey === financingCalculationKey;

	// Calculate sale numbers based on selected product type, price, down payment percentage and installments
	useEffect(() => {
		if (!paymentOptions || !downPaymentOptions || !price || !isDownPaymentValid || !installments || !approvedCredit || pre_registered_device_id)
			return;

		let isCancelled = false;
		const quoteController = new AbortController();

		(async () => {
			let factor = paymentOptions.find((option) => option.installments === installments)?.factor || 1.38;
			let extraInitial = 0;

			// Calculate the maximum product price the user can finance with their approved credit
			const maxFinanceableDevicePrice = Math.round(
				(approvedCredit === Infinity ? 100000000000 : approvedCredit) / (1 - downPaymentPerc)
			);
			if (!isCancelled) {
				setMaxFinanceableDevicePrice(maxFinanceableDevicePrice);
			}

			// If price exceeds financeable amount, calculate the extra initial payment needed
			if (parseInt(price) > maxFinanceableDevicePrice) {
				extraInitial = parseInt(price) - maxFinanceableDevicePrice;
			}

			const initialPaymentAmount =
				extraInitial > 0
					? maxFinanceableDevicePrice * downPaymentPerc + (parseInt(price) - maxFinanceableDevicePrice)
					: parseInt(price) * downPaymentPerc;

			// The customer's own plan is priced by the backend: this is the exact cuota registration
			// accepts (no factor multiplier or level discount applies to it)
			if (customerFinancingOverrides?.[productType]?.payment_options && userDocId) {
				setLoadingVariableFactor(false);
				setDynamicDownPaymentPct(null);
				// Debounced and cancelled with the effect, so typing a price sends one request
				await new Promise((resolve) => setTimeout(resolve, 300));
				if (isCancelled) return;
				const amountFinanced = Math.round(parseInt(price) - initialPaymentAmount);
				const [quoteData, quoteError] = await callAPI(
					'https://api.krece.app/users/financing-overrides/',
					'GET',
					{ user_email: userDocId, product_type: productType, amount_financed: amountFinanced, installments },
					userData,
					country,
					true,
					false,
					quoteController.signal
				);
				if (isCancelled) return;
				if (!quoteError && quoteData && !quoteData.quote) {
					// The conditions ended on the server: reload them and price normally
					await loadCustomerFinancingOverrides(userDocId);
					return;
				}
				if (quoteError || !quoteData?.quote) {
					overrideQuoteErrorRef.current = quoteError || 'No se pudieron calcular las condiciones del cliente. Intente nuevamente.';
					setFormError(overrideQuoteErrorRef.current);
					return;
				}
				setFormError((current) => (current && current === overrideQuoteErrorRef.current ? '' : current));
				const initialAmount = parseInt(price) - amountFinanced;
				setDevicePrice(parseInt(price));
				setInitialPaymentAmount(initialAmount);
				setInstallmentsAmount(quoteData.quote.payment_amount);
				setTotalAmount(quoteData.quote.payment_amount * installments + initialAmount);
				setDiscountedAmount(0);
				setDiscountedPercentage(0);
				setCalculatedFinancingKey(financingCalculationKey);
				return;
			}

			if (userLevel === 'level_1' || simulator) {
				if (!isCancelled) {
					setLoadingVariableFactor(true);
				}
				const startTime = Date.now();

				try {
					const response = await unsafeAPI(
						'POST',
						'https://api.krece.app/users/factormultiplier/',
						{
							user_document_id: userDocId || null,
							device_price: parseInt(price),
							device_type: productType,
							device_manufacturer: productType === 'iphone' ? 'Apple' : productManufacturer || null,
							installments,
							down_payment_percentage: !extraInitial
								? downPaymentPerc
								: Math.round((initialPaymentAmount / parseInt(price)) * 100) / 100,
							...(userData?.city && { city: userData.city }),
							level: userLevel || selectedLevel || 'level_1'
						},
						userData,
						country
					);
					if (!isCancelled && response?.success && response?.data?.factor_multiplier && response?.data?.factor_multiplier !== 1) {
						const change = 1 - response.data.factor_multiplier;
						console.log(`Factor multiplier: ${response.data.factor_multiplier}`);
						factor = change < 0 ? factor + Math.abs(change) : factor - change;
						console.log(`Updated factor: ${factor}`);
					}

					// Handle optional down_payment_pct from API
					if (!isCancelled && response?.success) {
						const apiValue = response.data?.down_payment_pct;

						// Reset if API doesn't provide the field, provides null, or provides 0/false
						if (apiValue === undefined || apiValue === null || apiValue === 0 || apiValue === false) {
							setDynamicDownPaymentPct(null);
							console.log(`API down payment percentage reset (received: ${apiValue})`);
						}
						// Only set if it's a valid positive number
						else {
							const apiDownPaymentPct = parseFloat(apiValue);
							if (!isNaN(apiDownPaymentPct) && apiDownPaymentPct > 0 && apiDownPaymentPct <= 1) {
								setDynamicDownPaymentPct(apiDownPaymentPct);
								console.log(`API down payment percentage: ${apiDownPaymentPct}`);
							} else {
								// Reset for invalid values (NaN, negative, > 1)
								setDynamicDownPaymentPct(null);
								console.log(`API down payment percentage reset (invalid value: ${apiValue})`);
							}
						}
					}
				} catch (error) {
					console.error('Error:', error);
				} finally {
					if (!isCancelled) {
						const elapsedTime = Date.now() - startTime;
						const remainingTime = 500 - elapsedTime;

						if (remainingTime > 0) {
							setTimeout(() => {
								if (!isCancelled) {
									setLoadingVariableFactor(false);
								}
							}, remainingTime);
						} else {
							setLoadingVariableFactor(false);
						}
					}
				}
			}

			if (isCancelled) return;

			const installmentsAmount = ((parseInt(price) - initialPaymentAmount) / installments) * factor;
			const totalAmount =
				(factor !== 1 ? Math.round(installmentsAmount) : installmentsAmount) * installments + initialPaymentAmount;

			setDevicePrice(parseInt(price));
			setInitialPaymentAmount(factor !== 1 ? Math.round(initialPaymentAmount) : initialPaymentAmount);
			setInstallmentsAmount(factor !== 1 ? Math.round(installmentsAmount) : installmentsAmount);
			setTotalAmount(factor !== 1 ? Math.round(totalAmount) : totalAmount);

			setDiscountedAmount(0);
			setDiscountedPercentage(0);
			setCalculatedFinancingKey(financingCalculationKey);

			// Calculate the discounted amount and percentage if the user is not a Krece Azul and there is no active promo or custom store financing options for this product type
			if (
				userFinancingOptions?.level_information?.name !== 'Krece Azul' &&
				!activePromo &&
				!resolveStorePaymentOptions(
					findStoreFinancingOption(storeFinancingOptions, productType, userLevel || selectedLevel, 'payment_options'),
					userFinancingOptions?.[productType]?.payment_options
				)?.length
			) {
				const level1Factor = LevelsConfig?.[country]?.['base_financing_options']
					?.find((option) => option?.product_types?.includes(productType))
					?.payment_options?.find((option) => option?.installments === installments)?.factor;
				if (!level1Factor) return;

				const financedAmount = installmentsAmount * installments;
				const level1FinancedAmount = ((parseInt(price) - initialPaymentAmount) / installments) * level1Factor * installments;

				if (financedAmount < level1FinancedAmount) {
					setDiscountedAmount(Math.abs(financedAmount - level1FinancedAmount));
					setDiscountedPercentage(Math.abs(Math.round(((financedAmount - level1FinancedAmount) / financedAmount) * 100)));
				}
			}
		})();

		return () => {
			isCancelled = true;
			quoteController.abort();
		};
		//eslint-disable-next-line
	}, [financingCalculationKey]);

	// Subscribe to liveness document
	useEffect(() => {
		if (!liveness?.documentId) return;

		let unsubscribe = null;

		try {
			unsubscribe = firestore
				.collection('LivenessSessions')
				.doc(liveness.documentId)
				.onSnapshot(
					(snapshot) => {
						try {
							if (snapshot && snapshot.exists) {
								const data = snapshot.data();

								const isSuccess = data?.valid_user === true && data?.user_authenticated === true && data?.photo_id_url;

								// Remove processing flag when verification is complete
								if ('processing' in data && isSuccess) {
									delete data.processing;
								}

								// Handle success
								if (isSuccess) {
									setUserPhoto(data.photo_id_url);
									setTimeout(() => {
										setShowSpinner(false);
										setCedulaVerified(false);
										if (isContinueApplication) {
											if (registrationStatus === 'registered') {
												setPage(8);
											} else if (
												registrationStatus === 'application_reviewed' ||
												registrationStatus === 'device_enrolled' ||
												registrationStatus === 'device_registered'
											) {
												setPage(4);
											}
										} else {
											setPage(3);
										}
									}, 3000);
								}

								setLiveness((prev) => ({ ...prev, results: data }));
							} else if (!snapshot || !snapshot.exists) {
								setLiveness(null);
								showAlert(
									'Esta sesión de verificación biométrica ha expirado, vaya a la página anterior para iniciar una nueva sesión.'
								);
							}
						} catch (error) {
							setLiveness(null);
							setFormError('Ocurrió un error iniciando la verificación biométrica, por favor intente más tarde.');
							console.error('Error processing liveness snapshot:', error);
						}
					},
					(error) => {
						setLiveness(null);
						setFormError('Ocurrió un error iniciando la verificación biométrica, por favor intente más tarde.');
						console.error('Error listening to liveness document:', error);
					}
				);
		} catch (error) {
			console.error('Error setting up liveness document listener:', error);
		}

		return () => {
			try {
				if (unsubscribe) {
					unsubscribe();
				}
			} catch (error) {
				console.error('Error unsubscribing from liveness document:', error);
			}
		};
		//eslint-disable-next-line
	}, [liveness?.documentId]);

	function listenToAnexoDocumentByIdentificationNumber(cedulaOverride = null) {
		const cedulaToUse = cedulaOverride || cedula;
		const formattedCedula = format_identification_with_prefix(cedulaToUse, uidPrefix);
		firestore
			.collection('Anexos')
			.where('identification_number', '==', formattedCedula)
			.where('terms_accepted', '==', true)
			.where('related_device', '==', null)
			.where('device_type', 'in', ['phone', 'iphone', 'tablet', 'other'])
			.where('status', 'in', ['pending', 'accepted'])
			.onSnapshot((snapshot) => {
				setAnexoAccepted(!snapshot.empty);
			});
	}

	function getNextXDates(x) {
		const dates = [];
		let currentDate = new Date();

		// Calculate the first date as 15 days from the current date
		currentDate.setDate(currentDate.getDate() + 15);

		for (let i = 0; i < x; i++) {
			// Adjust weekends to the following Monday
			while (currentDate.getDay() === 0 || currentDate.getDay() === 6) {
				currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
			}

			dates.push([currentDate.getDate(), getMonthName(currentDate.getMonth() + 1)]);

			// Move ahead by 15 days
			currentDate.setDate(currentDate.getDate() + 15);
		}
		return dates;
	}

	const getMonthName = (monthNumber) => {
		const date = new Date(2000, monthNumber - 1); // Set any year, and subtract 1 from the month number
		const monthName = date.toLocaleString('es-ES', { month: 'short' }); // Get the full month name

		return monthName.charAt(0).toUpperCase() + monthName.slice(1);
	};

	const sendPostRequest = async (data, endpoint, token2, signal) => {
		try {
			const posHeaders = get_pos_headers(toApiUrl(endpoint));
			const response = await fetch(toApiUrl(endpoint), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken(true)}`,
					'x-master-store-email': storeEmail,
					'x-user-email': storeUser,
					...impersonationHeaders,
					...posHeaders
				},
				body: JSON.stringify({ ...data, country: country }),
				...(signal && { signal })
			});
			const responseData = await response.json();
			if (response.status === '401') {
				const tokenData = await requestToken();
				await setToken('Token ' + tokenData.token);
				const resData = await sendPostRequest(data, endpoint, 'Token ' + tokenData.token, signal);
				return resData;
			}
			return responseData;
		} catch (error) {
			console.error('Error:', error.message);
			return null;
		}
	};

	const requestToken = async () => {
		const loginData = {
			username: process.env.REACT_APP_TOKEN_USERNAME,
			password: process.env.REACT_APP_TOKEN_PASSWORD
		};
		try {
			const response = await fetch(toApiUrl('/api/token/'), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...impersonationHeaders,
					...get_pos_headers(toApiUrl('/api/token/'))
				},
				body: JSON.stringify(loginData)
			});

			const responseData = await response.json();
			return responseData;
		} catch (error) {
			console.error('Error:', error);
		}
	};

	const toDailyLimitCount = (value) => {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	};

	const fetchDailyLimitStatus = async () => {
		if (!storeEmail) return { limitReached: false, devicesSold: null, dailyLimit: null };

		const [statusResponse, statusError] = await callAPI(
			'https://api.krece.app/storeprofile/request_daily_limit_increase/',
			'GET',
			{ master_store_email: storeEmail },
			userData,
			country
		);
		if (!statusError) {
			return {
				limitReached: statusResponse?.limit_reached === true,
				devicesSold: toDailyLimitCount(statusResponse?.devices_sold),
				dailyLimit: toDailyLimitCount(statusResponse?.current_limit)
			};
		}

		// Backward-compatible fallback while the read-only backend method deploys.
		const [storeDoc, limitsDoc] = await Promise.all([
			firestore.collection('Stores').doc(storeEmail).get(),
			firestore.collection('History').doc('DailyDeviceLimits').get()
		]);
		const reachedByFlag = storeDoc?.data()?.limit_reached === true;
		const limitsData = limitsDoc?.data() || {};
		const normalizedStoreName = normalizeDailyLimitStoreName(storeName);
		const limitEntry = storeName ? limitsData[normalizedStoreName] || limitsData[storeName] : null;
		const devicesSold = toDailyLimitCount(limitEntry?.devices_sold);
		const dailyLimit = toDailyLimitCount(limitEntry?.daily_limit);
		const reachedByCounter =
			limitEntry != null && devicesSold != null && dailyLimit != null && devicesSold >= dailyLimit;

		return { limitReached: reachedByFlag || reachedByCounter, devicesSold, dailyLimit };
	};

	const showDailyLimitIncreaseDialog = async (retryAction, stats = null) => {
		limitIncreaseRetryRef.current = retryAction;
		setIsLoading(false);
		setLimitIncreaseDialog({
			status: 'prompt',
			newLimit: null,
			devicesSold: stats?.devicesSold ?? null,
			dailyLimit: stats?.dailyLimit ?? null
		});

		if (!stats) {
			const { devicesSold, dailyLimit } = await fetchDailyLimitStatus();
			setLimitIncreaseDialog((current) =>
				current && current.status === 'prompt' ? { ...current, devicesSold, dailyLimit } : current
			);
		}
	};

	const requestDailyLimitIncrease = async () => {
		if (!storeEmail) {
			setLimitIncreaseDialog({ status: 'rejected', newLimit: null });
			return;
		}

		setLimitIncreaseDialog((current) => ({ ...current, status: 'requesting' }));
		const [response, error] = await callAPI(
			'https://api.krece.app/storeprofile/request_daily_limit_increase/',
			'POST',
			{ master_store_email: storeEmail },
			userData,
			country
		);

		if (error || !response?.new_limit) {
			setLimitIncreaseDialog({ status: 'rejected', newLimit: null });
			return;
		}

		setLimitIncreaseDialog((current) => ({ ...current, status: 'approved', newLimit: response.new_limit }));
	};

	const continueAfterLimitIncrease = async () => {
		const retryAction = limitIncreaseRetryRef.current;
		limitIncreaseRetryRef.current = null;
		setLimitIncreaseDialog(null);
		await retryAction?.();
	};

	// Loads the customer's own financing conditions and drops them when they expire
	const loadCustomerFinancingOverrides = async (customerEmail) => {
		clearTimeout(financingOverridesExpiryRef.current);
		financingOverridesExpiredRef.current = false;
		const overrides = customerEmail ? await get_customer_financing_overrides(customerEmail, userData, country) : null;
		setCustomerFinancingOverrides(overrides?.active || null);
		const remainingMs = overrides?.expires_at_ms ? overrides.expires_at_ms - Date.now() : null;
		if (remainingMs !== null && remainingMs > 0 && remainingMs < 2 ** 31 - 1) {
			// Expiry reprices the sale form only; once a contract exists the backend decides
			financingOverridesExpiryRef.current = setTimeout(() => {
				if (pageRef.current <= 4) setCustomerFinancingOverrides(null);
				else financingOverridesExpiredRef.current = true;
			}, remainingMs + 1);
		}
	};
	const pageRef = useRef(page);
	useEffect(() => {
		pageRef.current = page;
		if (page <= 4 && financingOverridesExpiredRef.current) {
			financingOverridesExpiredRef.current = false;
			setCustomerFinancingOverrides(null);
		}
	}, [page]);
	useEffect(() => () => clearTimeout(financingOverridesExpiryRef.current), []);

	const handleCheckCedula = async (e, cedulaOverride = null) => {
		e?.preventDefault();

		if (isAccountInactive) {
			setLoginError(
				'Esta cuenta esta inactiva para realizar ventas con Krece por los momentos, por favor contacte al equipo de Krece para entender el motivo.'
			);
			return;
		}

		const cedulaToCheck = cedulaOverride || cedula;

		if (!cedulaToCheck) {
			setLoginError(`Por favor introduzca ${country === 'peru' ? 'el DNI' : 'la cédula'}`);
			return;
		}
		const cedulaRegex = /^[0-9]+$/;
		if (!cedulaRegex.test(cedulaToCheck)) {
			setLoginError(`Por favor ingrese un número de ${country === 'peru' ? 'DNI' : 'cédula'} válido (sólo números).`);
			return;
		}

		setIsLoading(true);
		setLoginError('');

		try {
			const dailyLimitStatus = await fetchDailyLimitStatus();
			if (dailyLimitStatus.limitReached) {
				showDailyLimitIncreaseDialog(() => handleCheckCedula(null, cedulaToCheck), {
					devicesSold: dailyLimitStatus.devicesSold,
					dailyLimit: dailyLimitStatus.dailyLimit
				});
				return;
			}

			const formattedCedula = format_identification_with_prefix(cedulaToCheck, uidPrefix);
			const usersCollection = firestore.collection('Users');
			const queryUser = usersCollection.where('identification_number', '==', formattedCedula).limit(1);
			const querySnapshot = await queryUser.get();
			const data = querySnapshot.docs?.[0]?.data();

			if (!data) {
				if (pre_registered_device_id) {
					throw new Error('No se encontró al usuario de esta venta');
				}
				setPage(16);
				return;
			} else if (!data?.country) {
				throw new Error('No se pudo obtener el país del usuario');
			}

			setUserDocId(querySnapshot.docs[0].id);
			setEmail(querySnapshot.docs[0].id);
			setRegistrationStatus(data.registration_status);
			// Runs alongside the liveness check below instead of adding a wait
			const customerOverridesLoad = loadCustomerFinancingOverrides(querySnapshot.docs[0].id);

			const clientPoints =
				(data?.devices_paid?.length || 0) * 20 +
				(data?.ontime_payments_ids?.length || 0) * 10 -
				(data?.late_payments_ids?.length || 0) * 10;
			setLevelPoints(clientPoints);
			setUserLevel(data?.level || 'level_1');

			const rawMinDp = Number(data?.min_down_payment_pct);
			setMinDownPaymentPct(rawMinDp > 0 ? Math.round(rawMinDp / 5) * 5 : null);

			const primary_credit_line =
				('available_loan_approved' in data ? data.available_loan_approved : data?.max_loan_approved_3months) || 0;
			const secondary_credit_line =
				('available_loan_approved_secondary' in data
					? data.available_loan_approved_secondary
					: data?.max_loan_approved_3months_secondary) || 0;

			setUserFinancingOptions(
				calculate_user_financing_options(
					LevelsConfig,
					data.country,
					data?.level || 'level_1',
					userData?.additional_financing_options || null,
					userData?.additional_price_ranges || null
				)
			);

			// Check if user is exempt from biometric authentication
			const livenessResponse = await unsafeAPI(
				'GET',
				`https://api.krece.app/administration/lastsuccessfulliveness/?user_email=${encodeURIComponent(querySnapshot.docs[0].id)}`,
				null,
				userData,
				country
			);
			const isBiometricExempt = livenessResponse?.success && livenessResponse?.successful_liveness === true;
			await customerOverridesLoad;

			if (
				data.registration_status === 'registered' ||
				data.registration_status === 'application_reviewed' ||
				data.registration_status === 'device_registered'
			) {
				const response = await sendPostRequest(
					{
						identification_number: format_identification_with_prefix(cedulaToCheck, CountrySettings?.uid_prefix || '')
					},
					'registration/usercheck/',
					token
				);

				setUserCanPurchaseOthers(
					response.data?.other_products_eligibility || userData?.master_store?.allow_any_product === true ? true : false
				);
				setPrimaryCreditLine(response.data?.available_loan_approved ?? primary_credit_line ?? 0);
				setSecondaryCreditLine(response.data?.available_loan_approved_secondary ?? secondary_credit_line ?? 0);
				setIsContinueApplication(true);
				testCameraFunctionality();

				// Skip biometric verification if user is exempt or on localhost
				if (isBiometricExempt || isLocalhost) {
					if (!pre_registered_device_id) setPage(data.registration_status === 'registered' ? 8 : 4);
				} else {
					if (!pre_registered_device_id) setPage(17);
				}
			} else if (data.registration_status === 'device_enrolled') {
				const response = await sendPostRequest(
					{
						identification_number: format_identification_with_prefix(cedulaToCheck, CountrySettings?.uid_prefix || '')
					},
					'registration/usercheck/',
					token
				);
				if (response?.success && response?.data?.user_country === country && response?.data?.can_be_financed) {
					const primary_credit_line =
						('available_loan_approved' in response.data
							? response.data.available_loan_approved
							: response.data?.max_loan_approved_3months) || 0;
					const secondary_credit_line =
						('available_loan_approved_secondary' in response.data
							? response.data.available_loan_approved_secondary
							: response.data?.max_loan_approved_3months_secondary) || 0;
					if (!primary_credit_line && !secondary_credit_line) {
						setLoginError('El usuario no tiene línea de crédito disponible.');
						return;
					}
					setPrimaryCreditLine(primary_credit_line);
					setSecondaryCreditLine(secondary_credit_line);
					setUserCanPurchaseOthers(response.data?.other_products_eligibility ? true : false);
					setIsContinueApplication(true);
					testCameraFunctionality();

					// Skip biometric verification if user is exempt or on localhost
					if (isBiometricExempt || isLocalhost) {
						if (!pre_registered_device_id) setPage(4);
					} else {
						if (!pre_registered_device_id) setPage(17);
					}
				} else {
					setLoginError(response.status);
					return;
				}
			}
			return querySnapshot.docs[0].id;
		} catch {
			setLoginError('Ocurrió un problema, por favor contacte a servicio técnico.');
		} finally {
			setIsLoading(false);
		}
	};

	const handleSimulatorCedulaSubmit = async (e) => {
		e?.preventDefault();

		if (!simulatorCedula) {
			setSimulatorError(`Por favor introduzca ${country === 'peru' ? 'el DNI' : 'la cédula'}`);
			return;
		}

		setIsLoadingSimulatorUser(true);
		setSimulatorError('');

		try {
			const formattedSimulatorCedula = format_identification_with_prefix(simulatorCedula, uidPrefix);
			const usersCollection = firestore.collection('Users');
			const queryUser = usersCollection.where('identification_number', '==', formattedSimulatorCedula).limit(1);
			const querySnapshot = await queryUser.get();
			const data = querySnapshot.docs?.[0]?.data();

			if (!data) {
				setSimulatorError(`No se encontró un usuario con ${country === 'peru' ? 'DNI' : 'cédula'} ${simulatorCedula}`);
				return;
			} else if (!data?.country) {
				throw new Error('No se pudo obtener el país del usuario');
			} else if (data.country !== country) {
				setSimulatorError('El usuario no pertenece al país seleccionado.');
				return;
			}

			// Set user document ID for factormultiplier API call
			setUserDocId(querySnapshot.docs[0].id);
			await loadCustomerFinancingOverrides(querySnapshot.docs[0].id);

			// Set user level
			setUserLevel(data?.level || 'level_1');
			setSelectedLevel(data?.level || 'level_1');
			const clientPoints =
				(data?.devices_paid?.length || 0) * 20 +
				(data?.ontime_payments_ids?.length || 0) * 10 -
				(data?.late_payments_ids?.length || 0) * 10;
			setLevelPoints(clientPoints);

			// Shared credit replaces the legacy secondary line, not the phone line.
			const primary_credit_line = data?.available_loan_approved ?? 0;
			const secondary_credit_line =
				CountrySettings?.feature_flags?.shared_credit_line === true
					? data?.available_personal_loan_credit_line ?? 0
					: data?.available_loan_approved_secondary ?? 0;

			setPrimaryCreditLine(primary_credit_line);
			setSecondaryCreditLine(secondary_credit_line);

			// Set financing options based on user level
			setUserFinancingOptions(
				calculate_user_financing_options(
					LevelsConfig,
					country,
					data?.level || 'level_1',
					userData?.additional_financing_options || null,
					userData?.additional_price_ranges || null
				)
			);

			const rawMinDp = Number(data?.min_down_payment_pct);
			setMinDownPaymentPct(rawMinDp > 0 ? Math.round(rawMinDp / 5) * 5 : null);

			setSimulatorUserLoaded(true);
			setSimulatorError('');
		} catch (error) {
			console.error('Error fetching simulator user:', error);
			setSimulatorError('Error al buscar el usuario. Por favor intente nuevamente.');
		} finally {
			setIsLoadingSimulatorUser(false);
		}
	};

	const handleFaceSubmit = async (userPhoto) => {
		setFormError('');
		if (!userPhoto) {
			setFormError('Por favor suba una imagen.');
			setShowSpinner(false);
			return;
		}

		// Simulating API call delay
		const infoData = {
			identification_number: format_identification_with_prefix(cedula, CountrySettings?.uid_prefix || ''),
			photo_id_url: userPhoto,
			existing_user: false || isContinueApplication
		};
		const response = await sendPostRequest(infoData, 'authentication/face-identification/', token);
		if (response?.success) {
			if (isContinueApplication) {
				if (registrationStatus === 'registered') {
					setCedulaVerified(true);
					setShowSpinner(false);
					setTimeout(() => {
						setPage(8);
						setCedulaVerified(false);
					}, 1000);
				} else if (registrationStatus === 'application_reviewed' || registrationStatus === 'device_enrolled') {
					setCedulaVerified(true);
					setShowSpinner(false);
					setTimeout(() => {
						setPage(4);
						setCedulaVerified(false);
					}, 1000);
				} else if (registrationStatus === 'device_registered') {
					setCedulaVerified(true);
					setShowSpinner(false);
					setTimeout(() => {
						setPage(4);
						setCedulaVerified(false);
					}, 1000);
				}
			} else {
				setCedulaVerified(true);
				setShowSpinner(false);
				if (response.gender) {
					setGender(response.gender);
				}
				setTimeout(() => {
					setPage(3);
					setCedulaVerified(false);
				}, 1000);
			}
			setShowSpinner(false);
		} else if (response?.success === false) {
			setFormError(response.status);
			setShowSpinner(false);
		} else {
			setFormError('Estamos teniendo problemas para conectarnos. Por favor chequee su conexión');
			setShowSpinner(false);
		}
	};

	function getUserInput() {
		function getInputRecursive() {
			const userInput = window.prompt(
				`No pudimos identificar ${country === 'peru' ? 'el DNI' : 'la cédula'} por favor introduzca el número aquí:`
			);
			if (userInput === null) {
				// User clicked cancel or closed the prompt
				return getInputRecursive(); // Ask again
			} else if (!/^[0-9]+$/.test(userInput)) {
				// Invalid input (doesn't match the regex)
				showAlert(`Por favor introduzca un número de ${country === 'peru' ? 'DNI' : 'cédula'} válido.`);
				return getInputRecursive(); // Ask again
			} else {
				// Valid input
				return userInput;
			}
		}

		return getInputRecursive();
	}

	const handleIdentificationSubmit = async (cedulaPhoto, cedulaPhotoBack) => {
		setFormError('');
		if (!cedulaPhoto) {
			setFormError('Por favor suba una imagen.');
			setShowSpinner(false);
			return;
		}
		if (!documentType) {
			setFormError('Seleccione el tipo de documento.');
			setShowSpinner(false);
			return;
		}

		// Check if back photo is required
		const requiresBackPhoto = documentType?.include_back === true;
		if (requiresBackPhoto && !cedulaPhotoBack) {
			setFormError('Por favor suba la foto del reverso de la cédula.');
			setShowSpinner(false);
			return;
		}

		const infoData = {
			identification_id_url: cedulaPhoto,
			document_type: documentType.code,
			document_country: documentType.country,
			...(requiresBackPhoto && { identification_id_url_2: cedulaPhotoBack })
		};
		const response = await sendPostRequest(infoData, 'authentication/document-identification/', token);
		if (response?.success) {
			const userData = response.data;
			if (response.data.identification_number === false || !response.identity_verified) {
				const userInput = getUserInput();
				response.data.identification_number = userInput;
				if (userInput !== cedula) {
					setFormError('La cedula que se subio no coincide con el número de cedula escrito en la pantalla anterior.');
					setShowSpinner(false);
					return;
				}
				setCedula(userInput);
				userData.identification_number = userInput;
			} else {
				if (cedula !== userData.identification_number.replaceAll('-', '')) {
					setFormError('La cedula que se subio no coincide con el número de cedula escrito en la pantalla anterior.');
					setShowSpinner(false);
					return;
				}
			}

			setCedulaExtranjera(userData.cedula_extranjera === true);
			if (userData.cne_identified) {
				setCneIdentified(true);
				setCneData({
					centro: userData.centro,
					direccion: userData.direccion,
					estado: userData.estado,
					municipio: userData.municipio,
					parroquia: userData.parroquia
				});
			}
			setGender(userData.gender ? userData.gender : '');
			setFirstName(userData.first_name ? userData.first_name : '');
			setLastName(userData.last_name ? userData.last_name : '');
			setBirthDate(userData.date_of_birth ? userData.date_of_birth : '');
			const expDate =
				userData.expiration_date && userData.expiration_date !== false
					? userData.expiration_date.split('/').length === 3
						? userData.expiration_date.split('/')[2]
						: userData.expiration_date.split('/')[1]
					: false;
			if (userData.expiration_date && userData.expiration_date !== false && parseInt(new Date().getFullYear()) - expDate.split('/')[1] > 2) {
				setFormError(
					`${country === 'peru' ? 'El DNI' : 'La cédula'} esta expirada más de dos años. No podemos seguir con este registro.`
				);
				setShowSpinner(false);
				return;
			}
			setTimeout(() => {
				setCedulaVerified(false);
				testCameraFunctionality();
				// Skip biometric verification on localhost
				setPage(isLocalhost ? 3 : 17);
			}, 1000);
			setShowSpinner(false);
			setCedulaVerified(true);
		} else if (response?.success === false) {
			setFormError(response?.status || 'No se pudo verificar la identificación, intente nuevamente.');
			setShowSpinner(false);
		} else {
			setFormError('Estamos teniendo problemas para conectarnos. Por favor chequee su conexión');
			setShowSpinner(false);
		}
	};

	const handleInformationSubmit = async (e) => {
		e.preventDefault();

		if (!firstName || !lastName || !email || !email2 || !cedula || !phoneNumber || !birthDate || !address || !gender) {
			setFormError('Por favor complete todas las casillas.');
			return;
		}

		if (isUnder18(birthDate)) {
			setFormError(
				'El cliente tiene menos de 18 años y no es elegible para Krece, si creen que hay un error ajuste la fecha de nacimiento.'
			);
			return;
		}

		if (email !== email2) {
			setFormError('Los emails intoducidos no coinciden.');
			return;
		}

		// Validate email format
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(email)) {
			setFormError('Por favor ingrese un email válido.');
			return;
		}

		// Validate phone number format
		if (country === 'peru') {
			const phoneRegex = /^\d{9}$/;
			if (!phoneRegex.test(phoneNumber)) {
				setFormError('Por favor ingrese un número de teléfono de 9 digitos.');
				return;
			}
		} else {
			const phoneRegex = /^\d{10,11}$/;
			if (!phoneRegex.test(phoneNumber)) {
				setFormError('Por favor ingrese un número de teléfono de 10-11 digitos.');
				return;
			}
		}

		// Validate birth date format
		const birthDateRegex = /^\d{4}-\d{2}-\d{2}$/;
		if (!birthDateRegex.test(birthDate)) {
			setFormError('Por favor ingrese una fecha válida.');
			return;
		}

		//validate cedula format
		const cedulaRegex = /^[0-9]+$/;
		if (!cedulaRegex.test(cedula)) {
			setFormError(`Por favor ingrese un numero de ${country === 'peru' ? 'DNI' : 'cédula'} válido (sólo números).`);
			return;
		}

		if (!tokenVerified && !disableSalePhone2fa) {
			setFormError('Por favor realice la verificación de Token para poder continuar.');
			return;
		}

		// Form validation passed, submit the form or perform other actions
		setFormError('');
		// ...
		setIsLoading(true);

		// Simulating API call delay
		const infoData = {
			email: email,
			first_name: firstName,
			last_name: lastName,
			date_of_birth: birthDate,
			sex: gender,
			address: address,
			identification_number: format_identification_with_prefix(cedula, CountrySettings?.uid_prefix || ''),
			identification_type: documentType?.code || '',
			photo_id_url: userPhoto,
			identification_id_url: cedulaPhoto,
			phone_number: phoneNumber,
			seller_store: storeEmail,
			store_user: storeUser,
			cne_identified: cneIdentified,
			cedula_extranjera: cedulaExtranjera,
			...(store_recharge_coupon && { store_recharge_coupon: true }),
			...cneData
		};
		const response = await sendPostRequest(infoData, 'registration/userregistration/', token);
		if (response?.success) {
			if (!response.user_exists || false) {
				setIsLoading(false);
				setPage(8);
			} else {
				setFormError('Ya existe un usuario con este email.');
				setIsLoading(false);
			}
		} else if (response?.success === false) {
			setFormError(response.status);
			if (!disableSalePhone2fa) setTokenVerified(false);
			setShowTokenField(true);
			setIsLoading(false);
		} else {
			setFormError('Estamos teniendo problemas para conectarnos. Por favor chequee su conexión');
			setIsLoading(false);
		}
	};

	function isUnder18(dateString) {
		// Convert the date string to a Date object
		const birthDate = new Date(dateString);

		// Get the current date
		const currentDate = new Date();

		// Calculate the age
		const age = currentDate.getFullYear() - birthDate.getFullYear();

		// Check if the birthday has occurred this year
		const hasBirthdayOccurred =
			currentDate.getMonth() > birthDate.getMonth() ||
			(currentDate.getMonth() === birthDate.getMonth() && currentDate.getDate() >= birthDate.getDate());

		// Determine if the person is under 18
		const isUnderage = age < 18 || (age === 18 && !hasBirthdayOccurred);

		return isUnderage;
	}

	const handleRegistrationSubmit = (e) => {
		e.preventDefault();

		if (productType === 'other' && !canRegisterOtherProduct) {
			setFormError(otherProductRestrictionMessage);
			return;
		}

		if (!isFinancingCalculationReady) {
			setFormError('Las condiciones de financiamiento se están actualizando. Espere un momento y vuelva a intentarlo.');
			return;
		}

		if (
			!subStore ||
			!price ||
			!installments ||
			!isDownPaymentValid ||
			!productType ||
			(['other'].includes(productType) && !productName) ||
			(['phone', 'tablet', 'laptop'].includes(productType) && !productManufacturer)
		) {
			setFormError('Por favor complete todas las casillas.');
			return;
		}

		if (!activePromo && !eliteFinancingReady) {
			setFormError('Las condiciones de esta sucursal se están actualizando. Espere un momento y vuelva a intentarlo.');
			return;
		}

		if (activePromo && activePromo?.min_price && price < activePromo.min_price) {
			setFormError(`El precio mínimo para la promoción activa es de ${activePromo?.min_price}.`);
			return;
		} else if (
			effectiveFinancingOptions?.[productType]?.price_range?.min &&
			effectiveFinancingOptions?.[productType]?.price_range?.max &&
			(price < effectiveFinancingOptions?.[productType]?.price_range?.min ||
				price > effectiveFinancingOptions?.[productType]?.price_range?.max)
		) {
			setFormError(
				`El precio de venta debe estar entre ${effectiveFinancingOptions?.[productType]?.price_range?.min} y ${effectiveFinancingOptions?.[productType]?.price_range?.max}.`
			);
			return;
		}

		// Form validation passed, submit the form or perform other actions
		setFormError('');
		setPage(6);
	};

	const handleApplicationSubmit = async (e) => {
		e?.preventDefault();
		if (!educationLevel || !monthlySalary || !savings || !city || !firstDeviceManufacturer || !firstDevicePrice) {
			setFormError('Por favor complete todas las casillas.');
			return;
		}

		// Form validation passed, submit the form or perform other actions
		setFormError('');
		// ...
		setIsLoading(true);
		const applicationData = {
			user_document_id: email,
			monthly_income_dollars: monthlySalary,
			savings: savings,
			education_level: educationLevel,
			seller_store: storeEmail,
			store_user: storeUser,
			city: city,
			device_manufacturer: firstDeviceManufacturer,
			first_device_price: parseFloat(firstDevicePrice),
			...(store_recharge_coupon && { store_recharge_coupon: true })
		};

		const response = await sendPostRequest(applicationData, 'registration/reviewuserapplication/', token); //status, success, data
		if (response?.success) {
			if (response?.data?.limit_reached === true) {
				showDailyLimitIncreaseDialog(() => handleApplicationSubmit(null));
				return;
			}
			if (response?.data?.temporary_limit_reached === true) {
				setLoginError('Hay otra venta en proceso usando el último cupo disponible. Intenta nuevamente en unos minutos.');
				setIsLoading(false);
				setPage(2);
				return;
			}
			if (!response?.data?.max_loan_approved_3months) {
				setLoginError('No se puede continuar con el registro, el usuario ha alcanzado el límite de crédito.');
				setIsLoading(false);
				setPage(2);
			} else {
				setPrimaryCreditLine(response.data?.available_loan_approved ?? response.data?.max_loan_approved_3months ?? 0);
				setSecondaryCreditLine(
					response.data?.available_loan_approved_secondary ?? response.data?.max_loan_approved_3months_secondary ?? 0
				);
				setUserDocId(email);
				setEmail(email);
				setLevelPoints(0);
				setUserLevel('level_1');
				await loadCustomerFinancingOverrides(null);
				setUserFinancingOptions(
					calculate_user_financing_options(
						LevelsConfig,
						country,
						'level_1',
						userData?.additional_financing_options || null,
						userData?.additional_price_ranges || null
					)
				);

				const rawMinDp = Number(response.data?.min_down_payment_pct);
				setMinDownPaymentPct(rawMinDp > 0 ? Math.round(rawMinDp / 5) * 5 : null);

				setIsContinueApplication(true);
				setUserCanPurchaseOthers(userData?.master_store?.allow_any_product === true ? true : false);

				setIsLoading(false);
				setPage(4);
			}
		} else if (response?.success === false) {
			setFormError(response.status);
			setIsLoading(false);
		} else {
			setFormError('Estamos teniendo problemas para conectarnos. ');
			setIsLoading(false);
		}
	};

	const generateAnexo = async (e) => {
		if (!preRegisteredPromotionReady) {
			setFormError(preRegisteredPromotionError);
			return;
		}
		setFormError('');
		setIsLoading(true);

		const data = {
			user_document_id: email,
			seller_store: storeEmail,
			store_user: storeUser,
			device_type: productType,
			sub_store: subStore,
			device_imei: imeiToRegister,
			loan_duration: installments / 2,
			payment_amount: installmentsAmount,
			self_financed: selfFinanced,
			device_price: devicePrice.toFixed(2),
			amount_financed: pre_registered_device_id ? preRegAmountFinanced : Math.round(parseInt(price) - initialPaymentAmount),
			loan_payment_cycle: 15,
			...(pre_registered_device_id && { pre_registered_device_id: pre_registered_device_id }	)
		};
		const resp2 = await sendPostRequest(data, 'registration/anexoregistration/', token); //to-do: Necesito mandar la cedula? ese token como se va a guardar en la base de datos?
		if (resp2?.success) {
			showAlert(`
        <div class="text-center">
          <h3 class="text-lg font-semibold text-primary-400 mb-2 light:text-primary-500">¡Contrato Generado!</h3>
          <p class="text-neutral-300">
            Contrato generado en app exitosamente. El usuario debe abrir la app Krece para aceptar el contrato.
          </p>
        </div>
      `);
			setTokenGenerated(true);
			setIsLoading(false);
		} else if (resp2.success === false) {
			setFormError(resp2.status);
			setIsLoading(false);
		} else {
			setFormError('Ocurrió un problema generando el contrato, por favor vuelva a intentar.');
			setIsLoading(false);
		}
	};

	const handleSendDeviceID = async (e) => {
		if (!preRegisteredPromotionReady) {
			setFormError(preRegisteredPromotionError);
			return;
		}
		try {
			setFormError('');
			setLoginError('');
			if (!deviceID) {
				setFormError('Por favor introduzca el código de confirmación.');
				return;
			}
			if (!/^\d{7,}$/.test(deviceID)) {
				setFormError('El número de confirmación debe contener al menos 7 dígitos.');
				return;
			}
			setIsLoading(true);

			const deviceData = {
				...get_sale_origin_payload(),
				device_imei: imeiToRegister,
				user_document_id: email,
				device_id: deviceID,
				seller_store: storeEmail,
				device_price: devicePrice.toFixed(2),
				loan_duration: installments / 2,
				payment_data: {
					loan_payment_cycle: 15,
					payment_amount: installmentsAmount
				},
				device_management_provider: 'nuovo',
				amount_financed: pre_registered_device_id ? preRegAmountFinanced : Math.round(parseInt(price) - initialPaymentAmount),
				sub_store: subStore,
				new_chip_acquired: false,
				store_user: storeUser,
				device_type: productType,
				...(activePromo?.label && { promotional_code: activePromo.label }),
				online_purchase: pre_registered_device_id ? true : isOnline,
				...(productManufacturer && { device_manufacturer: productManufacturer }),
				...(pre_registered_device_id && { pre_registered_device_id: pre_registered_device_id }	)
			};

			const response = await sendPostRequest(deviceData, 'registration/deviceregistration/', token);
			if (response?.success) {
				setPage(15);
				setShowConfNumber(false);
				setShowImei(false);
				setIsLoading(false);
			} else if (response?.status.includes('Error getting device')) {
				setFormError('Número de confirmación incorrecto. Vuelva a intentar con otro número.');
				// The code is checked at sale completion; send the seller back to the field to correct it.
				setPage(14);
				setIsLoading(false);
			} else if (response?.success === false) {
				setFormError(response.status);
				setIsLoading(false);
			} else {
				setFormError('Ocurrió un problema, intente más tarde.');
				setIsLoading(false);
			}
		} catch (error) {
			setFormError('Estamos teniendo problemas para conectarnos.');
			setIsLoading(false);
		}
	};

	// Function to compress an image before uploading
	const compressImage = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = (event) => {
				const img = new Image();
				img.src = event.target.result;
				img.onload = () => {
					const canvas = document.createElement('canvas');

					// Set target width and height (maintain aspect ratio)
					const MAX_WIDTH = 1200;
					const MAX_HEIGHT = 1200;
					let width = img.width;
					let height = img.height;

					// Calculate new dimensions
					if (width > height) {
						if (width > MAX_WIDTH) {
							height *= MAX_WIDTH / width;
							width = MAX_WIDTH;
						}
					} else {
						if (height > MAX_HEIGHT) {
							width *= MAX_HEIGHT / height;
							height = MAX_HEIGHT;
						}
					}

					canvas.width = width;
					canvas.height = height;
					const ctx = canvas.getContext('2d');
					ctx.drawImage(img, 0, 0, width, height);

					// Convert to Blob with reduced quality (0.7 = 70% quality)
					canvas.toBlob(
						(blob) => {
							if (!blob) {
								reject(new Error('compression_failed'));
								return;
							}
							// Create a new file from the compressed blob
							const compressedFile = new File([blob], file.name, {
								type: 'image/jpeg',
								lastModified: Date.now()
							});
							resolve(compressedFile);
						},
						'image/jpeg',
						0.7
					);
				};
				img.onerror = (error) => {
					reject(error);
				};
			};
			reader.onerror = (error) => {
				reject(error);
			};
		});
	};

	const handlePhotoChange = async (event, isBackSide = false) => {
		const inputElement = event?.target;
		try {
			setFormError('');
			setShowSpinner(true);
			const file = inputElement?.files?.[0];

			if (!file) {
				setShowSpinner(false);
				return;
			}

			const validationError = validateImageFile(file);
			if (validationError) {
				showAlert(validationError);
				if (inputElement) inputElement.value = '';
				setShowSpinner(false);
				return;
			}

			const shouldCompress = shouldCompressImage(file);
			let processedFile = file;
			if (shouldCompress) {
				try {
					processedFile = await compressImage(file);
				} catch (error) {
					processedFile = null;
				}
			}

			if (!processedFile) {
				showAlert('No se pudo procesar la imagen. Intente nuevamente con otra foto.');
				if (inputElement) inputElement.value = '';
				setShowSpinner(false);
				return;
			}

			// Create a storage reference with a unique filename
			const storage = Firebase.storage();
			const storageRef = storage.ref();
			const fileName = `${Date.now()}_${file.name}`;
			const fileRef = storageRef.child(`${fileName}`);

			// Upload the compressed file to Firebase Storage
			await fileRef.put(processedFile, {
				cacheControl: 'public,max-age=31536000,immutable'
			});

			const downloadURL = await fileRef.getDownloadURL();

			if (isBackSide) {
				setCedulaPhotoBack(downloadURL);
				// If we need both photos and we have the front, submit both
				const requiresBackPhoto = documentType?.include_back === true;
				if (requiresBackPhoto && cedulaPhoto) {
					handleIdentificationSubmit(cedulaPhoto, downloadURL);
				} else {
					setShowSpinner(false);
				}
			} else {
				setCedulaPhoto(downloadURL);
				// If back photo is not required, submit immediately
				const requiresBackPhoto = documentType?.include_back === true;
				if (requiresBackPhoto) {
					// Wait for back photo
					setShowSpinner(false);
				} else {
					handleIdentificationSubmit(downloadURL, null);
				}
			}
		} catch (error) {
			showAlert('No se pudo subir la imagen. Intente nuevamente.');
			if (inputElement) inputElement.value = '';
			setShowSpinner(false);
		}
	};

	const handlePhotoChange2 = async (event) => {
		const inputElement = event?.target;
		try {
			setFormError('');
			setShowSpinner(true);
			const file = inputElement?.files?.[0];

			if (!file) {
				setShowSpinner(false);
				return;
			}

			const validationError = validateImageFile(file);
			if (validationError) {
				showAlert(validationError);
				if (inputElement) inputElement.value = '';
				setShowSpinner(false);
				return;
			}

			const shouldCompress = shouldCompressImage(file);
			let processedFile = file;
			if (shouldCompress) {
				try {
					processedFile = await compressImage(file);
				} catch (error) {
					processedFile = null;
				}
			}

			if (!processedFile) {
				showAlert('No se pudo procesar la imagen. Intente nuevamente con otra foto.');
				if (inputElement) inputElement.value = '';
				setShowSpinner(false);
				return;
			}

			// Create a storage reference with a unique filename
			const storage = Firebase.storage();
			const storageRef = storage.ref();
			const fileName = `${Date.now()}_${file.name}`;
			const fileRef = storageRef.child(fileName);

			// Upload the compressed file to Firebase Storage
			await fileRef.put(processedFile, {
				cacheControl: 'public,max-age=31536000,immutable'
			});

			const downloadURL = await fileRef.getDownloadURL();
			setUserPhoto(downloadURL);
			handleFaceSubmit(downloadURL);
		} catch (error) {
			showAlert('No se pudo subir la imagen. Intente nuevamente.');
			if (inputElement) inputElement.value = '';
			setShowSpinner(false);
		}
	};

	const handleSaveOnlineSale = async () => {
		if (!onlineSalesBetaAccess) return;

		if (!subStore) {
			setFormError('Por favor seleccione el local de venta.');
			return;
		}

		if (!price || !installments || !productType) {
			setFormError('Por favor complete la información del producto antes de guardar.');
			return;
		}

		if (!Number.isFinite(installmentsAmount) || !Number.isFinite(initialPaymentAmount)) {
			setFormError('No se pudo calcular el plan de pago. Verifique los datos e intente nuevamente.');
			return;
		}

		if (!cedula) {
			setFormError('Por favor ingrese la cédula del cliente.');
			return;
		}

		if (!country) {
			setFormError('No se pudo determinar el país del registro.');
			return;
		}

		const userDocumentId = userDocId || email;
		if (!userDocumentId) {
			setFormError('No se encontró la información del usuario.');
			return;
		}

		if (!devicePrice || !price) {
			setFormError('Ingrese el precio del producto.');
			return;
		}

		try {
			setSavingOnlineSale(true);
			setFormError('');

			const identificationNumber = format_identification_with_prefix(cedula, CountrySettings?.uid_prefix || '');

			const payload = {
				...get_sale_origin_payload(),
				user_document_id: userDocumentId,
				identification_number: identificationNumber,
				seller_store: storeEmail,
				sub_store: subStore,
				store_user: storeUser,
				device_price: devicePrice.toFixed(2),
				loan_duration: installments / 2,
				payment_data: {
					loan_payment_cycle: 15,
					payment_amount: Number(installmentsAmount)
				},
				amount_financed: pre_registered_device_id ? preRegAmountFinanced : Math.round(parseInt(price) - initialPaymentAmount),
				device_type: productType,
				country,
				...(activePromo?.label && { promotional_code: activePromo.label }),
				...(productManufacturer && { device_manufacturer: productManufacturer })
			};

			const response = await unsafeAPI(
				'POST',
				'https://api.krece.app/registration/preregistereddevices/',
				payload,
				userData,
				country
			);

			if (response?.success) {
				setOnlineSaleRegistered(true);
				setFormError('');
			} else if (response?.status) {
				setFormError(response.status);
			} else {
				setFormError('Estamos teniendo problemas para conectarnos. Por favor chequee su conexión');
			}
		} catch (error) {
			setFormError('Estamos teniendo problemas para conectarnos. Por favor chequee su conexión');
		} finally {
			setSavingOnlineSale(false);
		}
	};

	const registerOtherProduct = async () => {
		if (!preRegisteredPromotionReady) {
			setFormError(preRegisteredPromotionError);
			setIsLoading(false);
			return;
		}
		try {
			const deviceData = {
				...get_sale_origin_payload(),
				user_document_id: email,
				seller_store: storeEmail,
				device_price: devicePrice.toFixed(2),
				loan_duration: installments / 2,
				payment_data: {
					loan_payment_cycle: 15,
					payment_amount: installmentsAmount
				},
				amount_financed: pre_registered_device_id ? preRegAmountFinanced : Math.round(parseInt(price) - initialPaymentAmount),
				sub_store: subStore,
				new_chip_acquired: false,
				store_user: storeUser,
				device_type: productType,
				product_description: productName,
				...(activePromo?.label && { promotional_code: activePromo.label }),
				...(pre_registered_device_id && { pre_registered_device_id: pre_registered_device_id }	)
			};
			const response = await sendPostRequest(deviceData, 'registration/deviceregistration/', token);
			if (response?.success) {
				setPage(15);
				setShowConfNumber(false);
				setShowImei(false);
				setIsLoading(false);
			} else if (response?.status.includes('Error getting device')) {
				setFormError('Número de confirmación incorrecto. Vuelva a intentar con otro número.');
				setIsLoading(false);
			} else if (response?.success === false) {
				setFormError(response.status);
				setIsLoading(false);
			} else {
				setFormError('Ocurrió un problema, intente más tarde.');
				setIsLoading(false);
			}
		} catch (err) {
			setFormError('Ocurrió un problema, intente más tarde.');
			setIsLoading(false);
		}
	};

	const handleTermsAccepted = async () => {
		if (productType === 'other' && !canRegisterOtherProduct) {
			setFormError(otherProductRestrictionMessage);
			return;
		}

		// to-do test this agregar un field del nombre del producto
		if (productType === 'other') {
			setIsLoading(true);
			await registerOtherProduct();
		} else {
			goToPage(7);
		}
	};

	useEffect(() => {
		if (embedded) document.querySelector('[data-embedded-sale-scroll]')?.scrollTo({ top: 0 });
	}, [page, embedded]);

	const goToPage = (x) => {
		setFormError('');
		setLoginError('');
		setPage(x);
	};

	const backToWelcomePage = () => {
		window.history.replaceState({}, '');
		window.location.reload();
	};

	const goToOnlineSales = () => {
		window.location.assign('/ventas-online');
	};

	const searchConfirmationNumber = async () => {
		try {
			setIsSearchingConfirmationNumber(true);
			setConfNumber('');
			setShowConfNumber(false);
			setLoginError('');
			const data = {
				store_name: storeName,
				imei: imeiNumber
			};
			const response = await sendPostRequest(data, 'registration/finddevice/', token);
			if (response?.success) {
				setConfNumber(response.data.nuovo_id);
				setShowConfNumber(true);
			} else if (response?.success === false) {
				setLoginError(response.status);
			} else {
				setLoginError('Estamos teniendo problemas para conectarnos.');
			}
		} catch (e) {
		} finally {
			setIsSearchingConfirmationNumber(false);
		}
	};

	const handleGetBypassCode = async () => {
		setBypassCodeLoading(true);
		setBypassCode('');
		setBypassCodeError('');
		try {
			const result = await unsafeAPI(
				'POST',
				'https://api.krece.app/administration/kandjibypasscode/',
				{ device_imei_no: imeiToRegister },
				userData,
				null
			);
			if (result?.success && result.data) {
				setBypassCode(result.data);
			} else {
				setBypassCodeError(result?.status || 'No se pudo obtener el código de bypass');
			}
		} catch {
			setBypassCodeError('Error al obtener el código de bypass');
		} finally {
			setBypassCodeLoading(false);
		}
	};

	const sendPhoneToken = async () => {
		setFormError('');
		setShowTokenField(false);
		setLoadingButton(true);
		try {
			if (!phoneNumber) {
				showAlert('Por favor introducir número de teléfono.');
				setLoadingButton(false);
				return;
			}
			if (!messType) {
				showAlert('Por favor seleccionar tipo de mensaje.');
				setLoadingButton(false);
				return;
			}
			// Validate phone number format
			if (country === 'peru') {
				const phoneRegex = /^\d{9}$/;
				if (!phoneRegex.test(phoneNumber)) {
					setFormError('Por favor ingrese un número de teléfono de 9 digitos.');
					setLoadingButton(false);
					return;
				}
			} else {
				const phoneRegex = /^\d{10,11}$/;
				if (!phoneRegex.test(phoneNumber)) {
					setFormError('Por favor ingrese un número de teléfono de 10 digitos.');
					setLoadingButton(false);
					return;
				}
			}
			const codeData = {
				phone_number: phoneNumber,
				code_type: messType
			};
			const response = await sendPostRequest(codeData, 'notifications/requestauthcode/', token); //status, success, data
			if (storeEmail === 'test@store.com' || storeEmail === 'testrd@store.com' || storeEmail === 'testperu@store.com') {
				response.success = true;
			}
			if (response?.success) {
				setShowTokenField(true);
				setLoadingButton(false);
				showAlert(`
          <div class="text-center">
            <h3 class="text-lg font-semibold text-primary-400 mb-2 light:text-primary-500">¡Token Enviado!</h3>
            <p class="text-neutral-300">
              Se ha enviado un token por ${messType === 'sms' ? 'SMS' : messType} al ${phoneNumber}.
            </p>
          </div>
        `);
				setTimeLeft(30);
				const timerId = setInterval(() => {
					setTimeLeft((prevTime) => {
						if (prevTime === 1) {
							clearInterval(timerId);
						}
						return prevTime - 1;
					});
				}, 1000);
			} else if (response?.success === false) {
				showAlert(response.status);
				setLoadingButton(false);
			} else {
				showAlert('Estamos teniendo problemas para conectarnos.');
				setLoadingButton(false);
			}
		} catch (error) {
			showAlert('Estamos teniendo problemas para conectarnos.');
			setLoadingButton(false);
		}
	};

	const fillToken = (token) => {
		setPhoneToken(token);
		if (token.length === 6) {
			setShowTokenField(false);
			setLoadingButton(true);
			confirmPhoneTokenFromUser(token);
		} else if (token.length > 6) {
			showAlert('El Token debe ser de 6 digitos.');
		}
	};

	const registerDevice = async ({ verifyOnly = false } = {}) => {
		if (!preRegisteredPromotionReady) {
			setFormError(preRegisteredPromotionError);
			return;
		}
		if (enrollmentRequest.current) return;
		setFormError('');
		if (!imeiToRegister || !imeiToRegister2) {
			setFormError('Por favor rellene todas las casillas para continuar.');
			return;
		}
		if (productType !== 'laptop' && !/^\d{15}$/.test(imeiToRegister)) {
			setFormError('El IMEI debe contener 15 dígitos.');
			return;
		}
		if (imeiToRegister !== imeiToRegister2) {
			setFormError('Los IMEIs introducidos no coinciden.');
			return;
		}
		if (verifyOnly && (deviceManagementProvider !== 'datacultr' || installationSession?.imei !== imeiToRegister)) {
			setFormError('Vuelve a consultar el equipo antes de verificar la instalación.');
			return;
		}
		enrollmentRequest.current = true;
		try {
			setIsLoading(true);
			setLoadingText(verifyOnly ? 'Verificando configuración' : 'Registrando equipo');
			setLoadingSubText(
				verifyOnly
					? 'Mantenga el equipo conectado a internet.'
					: productType === 'laptop'
						? 'Esto puede tardar unos segundos.'
						: 'Esto puede tardar unos segundos, por favor NO encienda el teléfono aún.'
			);
			const completingInstallation = verifyOnly && installationSession?.imei === imeiToRegister;
			const devData = {
				device_imei: imeiToRegister,
				device_type: productType,
				device_price: price,
				supports_installation: true,
				...(completingInstallation && { installation_completed: true, activation_id: installationSession.activationId }),
				...(activePromo?.label && { promotional_code: activePromo.label }),
				...(productManufacturer && { device_manufacturer: productManufacturer }),
				...(pre_registered_device_id && { pre_registered_device_id })
			};
			const startTime = Date.now();
			while (Date.now() - startTime < 3 * 60 * 1000) {
				const controller = new AbortController();
				const timeoutId = setTimeout(() => controller.abort(), 45000);
				let response;
				try {
					response = await sendPostRequest(devData, 'registration/registerimeiv2/', token, controller.signal);
				} finally {
					clearTimeout(timeoutId);
				}
				if (response?.activation_expired) {
					setInstallationSession(null);
					setPage(10);
					setFormError('La sesión de configuración venció. Vuelve a consultar el mismo equipo para continuar.');
					return;
				}
				if (response?.success === false || response?.activating_status === 'failed') {
					setFormError(getEnrollmentError(response?.status));
					return;
				}
				if (response?.activating_status === 'installation_required') {
					if (response.device_management_provider !== 'datacultr' || !response.activation_id) {
						setFormError('No pudimos obtener la configuración del equipo. Contacta a soporte de tiendas.');
						return;
					}
					setDeviceManagementProvider(response.device_management_provider);
					setInstallationSession({ imei: imeiToRegister, activationId: response.activation_id });
					setPage(20);
					if (verifyOnly)
						setFormError('Aún no podemos confirmar la instalación. Revisa la conexión del equipo y vuelve a verificar.');
					return;
				}
				if (verifyOnly && response?.device_management_provider && response.device_management_provider !== 'datacultr') {
					setFormError('La configuración recibida no corresponde al equipo. Vuelve a verificar la instalación.');
					return;
				}
				if (response?.activating_status === 'activated' && response?.device_management_provider) {
					const dmp = response.device_management_provider;
					setDeviceManagementProvider(dmp);
					if (dmp === 'nuovo') goToPage(23);
					else if (dmp === 'absolute' || dmp === 'datacultr') goToPage(19);
					else goToPage(18);
					return;
				}
				// Manual installation gets exactly one request per click, never a polling loop.
				if (verifyOnly) {
					setFormError('No pudimos confirmar la instalación. Revisa el equipo y pulsa Siguiente para reintentar.');
					return;
				}
				if (response?.device_management_provider && response.device_management_provider !== 'datacultr') {
					delete devData.installation_completed;
					delete devData.activation_id;
					setInstallationSession(null);
				}
				await new Promise((resolve) => setTimeout(resolve, 10000));
			}
			setFormError('Aún no podemos confirmar la configuración. Conservamos los datos del equipo; vuelve a verificar.');
		} catch {
			setFormError('No pudimos consultar la configuración del equipo. Reintenta o contacta a soporte de tiendas.');
		} finally {
			enrollmentRequest.current = false;
			setIsLoading(false);
			setLoadingText('');
			setLoadingSubText('');
		}
	};

	const continueDeviceConfirmation = () => {
		setFormError('');
		if (!/^\d{7,}$/.test(deviceID)) {
			setFormError('El número de confirmación debe contener al menos 7 dígitos.');
			return;
		}
		// Nuovo keeps its existing validation in handleSendDeviceID at sale completion.
		goToPage(19);
	};

	const finalizeEnrollmentSale = async () => {
		if (enrollmentRequest.current) return;
		enrollmentRequest.current = true;
		setIsLoading(true);
		setLoadingText('Concretando venta');
		try {
			if (deviceManagementProvider === 'nuovo') await handleSendDeviceID();
			else await callDevReg();
		} finally {
			enrollmentRequest.current = false;
			setIsLoading(false);
			setLoadingText('');
			setLoadingSubText('');
		}
	};

	const callDevReg = async () => {
		if (!preRegisteredPromotionReady) {
			setFormError(preRegisteredPromotionError);
			return;
		}
		try {
			setIsLoading(true);
			setLoadingText('Finalizando registro');

			const deviceData = {
				...get_sale_origin_payload(),
				user_document_id: email,
				device_imei: imeiToRegister,
				seller_store: storeEmail,
				device_price: devicePrice.toFixed(2),
				loan_duration: installments / 2,
				payment_data: {
					loan_payment_cycle: 15,
					payment_amount: installmentsAmount
				},
				device_management_provider: deviceManagementProvider,
				amount_financed: pre_registered_device_id ? preRegAmountFinanced : Math.round(parseInt(price) - initialPaymentAmount),
				sub_store: subStore,
				new_chip_acquired: false,
				store_user: storeUser,
				device_type: productType,
				...(activePromo?.label && { promotional_code: activePromo.label }),
				online_purchase: pre_registered_device_id ? true : isOnline,
				...(productManufacturer && { device_manufacturer: productManufacturer }),
				...(pre_registered_device_id && { pre_registered_device_id: pre_registered_device_id }	)
			};
			const response = await sendPostRequest(deviceData, 'registration/deviceregistration/', token);
			if (response?.success) {
				setPage(15);
				setIsLoading(false);
				setLoadingText('');
				setLoadingSubText('');
			} else if (response?.status.includes('Error getting device')) {
				setFormError(
					'Ocurrió un problema, por favor vuelva a hacer clic en Siguiente, si el problema persiste contacte a servicio técnico.'
				);
				setIsLoading(false);
				setLoadingText('');
				setLoadingSubText('');
			} else if (response?.success === false) {
				setFormError(response.status);
				setIsLoading(false);
				setLoadingText('');
				setLoadingSubText('');
			} else {
				setFormError(
					'Ocurrió un problema, por favor vuelva a hacer clic en Siguiente, si el problema persiste contacte a servicio técnico.'
				);
				setIsLoading(false);
				setLoadingText('');
				setLoadingSubText('');
			}
		} catch (err) {
			setFormError(
				'Ocurrió un problema, por favor vuelva a hacer clic en Siguiente, si el problema persiste contacte a servicio técnico.'
			);
			setIsLoading(false);
			setLoadingText('');
			setLoadingSubText('');
		}
	};

	const confirmPhoneTokenFromUser = async (ptoken) => {
		setFormError('');
		if (!ptoken) {
			setFormError('Llene todos los datos para continuar');
			return;
		}
		try {
			const codeData = {
				confirmation_code: parseInt(ptoken)
			};
			const response = await sendPostRequest(codeData, 'notifications/confirmauthcode/', token);
			if (response?.success) {
				setLoadingButton(false);
				setTokenVerified(true);
			} else if (response?.success === false) {
				showAlert(response.status);
				setLoadingButton(false);
				setShowTokenField(true);
			} else {
				showAlert('Estamos teniendo problemas para conectarnos.');
				setLoadingButton(false);
				setShowTokenField(true);
			}
		} catch (error) {
			showAlert('Estamos teniendo problemas para conectarnos.');
			setLoadingButton(false);
			setShowTokenField(true);
		}
	};

	function showAlert(message) {
		setDialogMessage(message);
		setShowDialog(true);
	}

	const steps = [
		{ label: country === 'peru' ? 'DNI' : 'Cédula', pages: [2] },
		{ label: 'Verificar Identidad', pages: [16, 17, 17.1, 17.2] },
		{ label: 'Información', pages: [3, 8] },
		{ label: 'Producto', pages: [4, 6] },
		{ label: 'Configuración', pages: [7, 10, 11, 12, 13, 14, 18, 20, 22, 23] },
		{ label: 'Contrato', pages: [19] },
		{ label: 'Finalizar', pages: [15] }
	];

	// useEffect(() => {
	// 	if (
	// 		page === 17 &&
	// 		['test@store.com', 'testperu@store.com', 'testrd@store.com', 'testsucursal@store.com', 'test@vendedor.com'].includes(
	// 			userData?.email || ''
	// 		)
	// 	) {
	// 		setIsLoading(false);
	// 		setPage(4);
	// 	}
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [page]);

	if (pre_registered_device_id && page === 2 && isLoading) {
		return <ContainerSpinner size={3} />;
	}

	if (pre_registered_device_id && page === 2 && formError) {
		return (
			<div className="flex h-full flex-col items-center justify-center space-y-6 p-8">
				<div className="rounded-full bg-red-500/10 p-4">
					<AlertTriangle className="h-12 w-12 text-red-500" />
				</div>
				<div className="space-y-2 text-center">
					<h3 className="text-xl font-semibold text-neutral-0">Error al cargar venta</h3>
					<p className="max-w-md text-neutral-300">{formError}</p>
				</div>
				<Button
					className="max-w-fit"
					variant="secondary"
					onClick={() => window.history.back()}
					Icon={<ArrowLeft className="size-5" />}
				>
					Volver
				</Button>
			</div>
		);
	}

	if (!LevelsConfig || !userData || !CountrySettings) return <ContainerSpinner size={3} />;

	if (pre_registered_device_id && page !== 15 && !preRegisteredPromotionReady) {
		return (
			<div className="flex h-full flex-col items-center justify-center space-y-6 p-8">
				<Notice message={preRegisteredPromotionError} variant="warning" />
				{!promotionsLoaded && <ContainerSpinner size={3} />}
				<Button variant="secondary" className="max-w-fit" onClick={() => window.location.reload()}>
					Volver a validar
				</Button>
				<Button variant="secondary" className="max-w-fit" onClick={() => window.history.back()}>
					Volver
				</Button>
			</div>
		);
	}

	return (
		<div className="m-auto flex min-h-full min-w-full flex-col items-center justify-start">
			{!simulator && (!hideStepper || page !== 17.1) && <Stepper currentPage={page} steps={steps} compact={embedded} />}

			<EnrollmentLoadingOverlay title={loadingText} description={loadingSubText} />

			<div
				className={classNames('pos-sale-body m-auto flex h-full w-full flex-col items-center justify-center p-4 md:p-6', {
					'!p-3 md:!p-4': page === 20
				})}
			>
				<div
					className={classNames(
						'pos-sale-card w-[500px] max-w-full rounded-xl border-[1.5px] border-neutral-900 bg-neutral-1000/25 p-6 shadow-lg duration-300 animate-in fade-in slide-in-from-bottom-5 light:bg-white light:shadow-none md:bg-background/50 md:p-8',
						{ 'w-[650px]': page === 3 || page === 8 },
						{ 'w-[800px]': page === 17.2 || (page === 17 && hasCameraAvailable && !disable_liveness_detection) },
						{ 'w-full !max-w-[min(100%,70rem)]': page === 4 || simulator },
						{ '!w-[min(100%,64rem)] !p-4 md:!p-5': page === 20 }
					)}
				>
					{page === 2 && ( //done
						<>
							{render_page_header(
								'Nuevo Registro',
								`Por favor ingrese ${country === 'peru' ? 'el DNI' : 'la cédula'} del cliente realizando la compra`,
								<PlusCircle />
							)}

							<form className="space-y-4" onSubmit={handleCheckCedula}>
								<Input
									name="cedula"
									floatingLabel={`${country === 'peru' ? 'DNI' : 'Cédula'} del cliente`}
									value={cedula}
									onChange={(value) => setCedula(value)}
									placeholder={`Ingrese ${country === 'peru' ? 'el DNI' : 'la cédula'} del cliente`}
									allowedChars="numbers_only"
								/>

								{loginError && <Notice message={loginError} variant="error" />}

								<Button disabled={!cedula} loading={isLoading} type="submit" onClick={handleCheckCedula} variant="primary">
									Continuar
								</Button>
							</form>
						</>
					)}
					{page === 16 && ( //done
						<>
							{render_page_header(
								'Verificación de Identidad',
								'Por favor suba una foto del documento de identidad del cliente',
								<KeyRound />
							)}

							<form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
								<Select
									title="Tipo de documento"
									options={CountrySettings.id_types.map((item, index) => ({
										label: item.name,
										value: index
									}))}
									onSelectedChange={(value) =>
										setDocumentType({
											code: CountrySettings.id_types[value].code,
											country: CountrySettings.id_types[value].country,
											include_back: CountrySettings.id_types[value].include_back
										})
									}
									placeholder="Seleccionar tipo de documento"
									className="w-full bg-foreground"
								/>

								{(() => {
									const requiresBackPhoto = documentType?.include_back === true;

									return (
										<>
											{requiresBackPhoto ? (
												// Two photos required: front and back
												<div className="grid gap-4 md:grid-cols-2">
													{/* Front Photo */}
													<div className="space-y-3">
														<h4 className="text-center text-sm font-medium text-neutral-300">Frente de la Cédula</h4>
														<div className="flex justify-center">
															<div className="flex h-32 items-center justify-center">
																{cedulaVerified ? (
																	<div className="flex h-full flex-col items-center justify-center">
																		<div className="relative">
																			<div className="absolute -inset-1 animate-pulse rounded-full bg-gradient-to-r from-primary-400 to-primary-600 opacity-10 blur"></div>
																			<div className="relative rounded-full bg-neutral-900 p-3">
																				<CircleCheck className="h-5 w-5 text-primary-500" />
																			</div>
																		</div>
																	</div>
																) : showSpinner && !cedulaPhoto ? (
																	<Spinner size={3} />
																) : !cedulaPhoto ? (
																	<IdCard className="size-24 transform text-neutral-400" strokeWidth={1} />
																) : (
																	<div className="flex h-full flex-col items-center justify-center">
																		<div className="relative">
																			<div className="relative rounded-full bg-neutral-900 p-3">
																				<CircleCheck className="h-5 w-5 text-green-500" />
																			</div>
																		</div>
																	</div>
																)}
															</div>
														</div>
														<div className="flex justify-center">
															<input
																name="photoFront"
																type="file"
																accept={ACCEPTED_IMAGE_TYPES_ATTR}
																onChange={(e) => handlePhotoChange(e, false)}
																disabled={showSpinner || cedulaVerified || !documentType || cedulaPhoto}
																ref={fileInputRef}
																className="peer hidden w-full bg-transparent text-sm outline-none placeholder:text-neutral-400"
															/>
															<Button
																Icon={<Camera className="size-5" />}
																disabled={showSpinner || cedulaVerified || !documentType || cedulaPhoto}
																onClick={() => fileInputRef.current.click()}
																variant="primary"
																className="!w-full"
																size="sm"
															>
																{cedulaPhoto ? 'Subida' : 'Subir Frente'}
															</Button>
														</div>
													</div>

													{/* Back Photo */}
													<div className="space-y-3">
														<h4 className="text-center text-sm font-medium text-neutral-300">Reverso de la Cédula</h4>
														<div className="flex justify-center">
															<div className="flex h-32 items-center justify-center">
																{cedulaVerified ? (
																	<div className="flex h-full flex-col items-center justify-center">
																		<div className="relative">
																			<div className="absolute -inset-1 animate-pulse rounded-full bg-gradient-to-r from-primary-400 to-primary-600 opacity-10 blur"></div>
																			<div className="relative rounded-full bg-neutral-900 p-3">
																				<CircleCheck className="h-5 w-5 text-primary-500" />
																			</div>
																		</div>
																	</div>
																) : showSpinner && cedulaPhoto ? (
																	<Spinner size={3} />
																) : !cedulaPhotoBack ? (
																	<IdCard className="size-24 transform text-neutral-400" strokeWidth={1} />
																) : (
																	<div className="flex h-full flex-col items-center justify-center">
																		<div className="relative">
																			<div className="relative rounded-full bg-neutral-900 p-3">
																				<CircleCheck className="h-5 w-5 text-green-500" />
																			</div>
																		</div>
																	</div>
																)}
															</div>
														</div>
														<div className="flex justify-center">
															<input
																name="photoBack"
																type="file"
																accept={ACCEPTED_IMAGE_TYPES_ATTR}
																onChange={(e) => handlePhotoChange(e, true)}
																disabled={showSpinner || cedulaVerified || !documentType || !cedulaPhoto || cedulaPhotoBack}
																ref={fileInputRefBack}
																className="peer hidden w-full bg-transparent text-sm outline-none placeholder:text-neutral-400"
															/>
															<Button
																Icon={<Camera className="size-5" />}
																disabled={showSpinner || cedulaVerified || !documentType || !cedulaPhoto || cedulaPhotoBack}
																onClick={() => fileInputRefBack.current.click()}
																variant="primary"
																className="!w-full"
																size="sm"
															>
																{cedulaPhotoBack ? 'Subida' : 'Subir Reverso'}
															</Button>
														</div>
													</div>
												</div>
											) : (
												// Single photo required
												<>
													<div className="flex justify-center">
														<div className="flex h-32 items-center justify-center">
															{showSpinner ? (
																<Spinner size={3} />
															) : !cedulaVerified ? (
																<IdCard className="size-32 scale-125 transform" strokeWidth={1} />
															) : (
																<div className="flex h-full flex-col items-center justify-center space-y-6 duration-500 animate-in fade-in-0 zoom-in-90">
																	<div className="space-y-2 text-center">
																		<div className="flex items-center justify-center gap-4">
																			<div className="relative">
																				<div className="absolute -inset-1 animate-pulse rounded-full bg-gradient-to-r from-primary-400 to-primary-600 opacity-10 blur"></div>
																				<div className="relative rounded-full bg-neutral-900 p-4">
																					<CircleCheck className="h-6 w-6 text-primary-500" />
																				</div>
																			</div>
																			<h3 className="text-xl font-semibold text-primary-400">¡Verificación Exitosa!</h3>
																		</div>
																		<p className="text-neutral-300">
																			La verificación de identidad se ha completado correctamente.
																		</p>
																	</div>
																</div>
															)}
														</div>
													</div>

													<div className="flex justify-center">
														<input
															name="photo"
															type="file"
															accept={ACCEPTED_IMAGE_TYPES_ATTR}
															onChange={(e) => handlePhotoChange(e, false)}
															disabled={showSpinner || cedulaVerified || !documentType}
															ref={fileInputRef}
															className="peer hidden w-full bg-transparent text-sm outline-none placeholder:text-neutral-400"
														/>
														<Button
															Icon={<Camera className="size-5" />}
															disabled={showSpinner || cedulaVerified || !documentType}
															onClick={() => fileInputRef.current.click()}
															variant="primary"
															className="!w-min"
														>
															Subir Imagen
														</Button>
													</div>
												</>
											)}

											{cedulaVerified && requiresBackPhoto && (
												<div className="mt-4 flex h-full flex-col items-center justify-center space-y-6 duration-500 animate-in fade-in-0 zoom-in-90">
													<div className="space-y-2 text-center">
														<div className="flex items-center justify-center gap-4">
															<div className="relative">
																<div className="absolute -inset-1 animate-pulse rounded-full bg-gradient-to-r from-primary-400 to-primary-600 opacity-10 blur"></div>
																<div className="relative rounded-full bg-neutral-900 p-4">
																	<CircleCheck className="h-6 w-6 text-primary-500" />
																</div>
															</div>
															<h3 className="text-xl font-semibold text-primary-400">¡Verificación Exitosa!</h3>
														</div>
														<p className="text-neutral-300">La verificación de identidad se ha completado correctamente.</p>
													</div>
												</div>
											)}
										</>
									);
								})()}

								{formError && <Notice message={formError} variant="error" />}

								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button
										Icon={<ArrowBack className="size-5" />}
										disabled={showSpinner || cedulaVerified}
										onClick={backToWelcomePage}
										variant="secondary"
									>
										Atrás
									</Button>
								</div>
							</form>
						</>
					)}
					{page === 19 && ( //done
						<>
							{render_page_header('Aceptar Términos y Condiciones', null, <FileText />)}

							<form
								className="space-y-6"
								onSubmit={(e) => {
									e.preventDefault();
									finalizeEnrollmentSale();
								}}
							>
								{productType === 'phone' && deviceManagementProvider !== 'kandji' && (
									<>
										<p className="text-neutral-300">
											Ahora proceda a descargar la app Krece en el equipo en venta escaneando este QR. {deviceManagementProvider !== 'nuovo' && 'Este paso NO es necesario para equipos SAMSUNG'}
										</p>
										<div className="flex items-center gap-12">
											{apkUrl && (
												<>
													<QRCodeSVG
														value={apkUrl}
														size={200}
														bgColor="#ffffff"
														fgColor="#000000"
														level="M"
													/>
													<a
														href={apkUrl}
														target="_blank"
														rel="noopener noreferrer"
														className="text-sm text-primary-400 underline hover:text-primary-300"
													>
														Descargar APK
													</a>
												</>
											)}
										</div>
									</>
								)}

								{(productType === 'iphone' || deviceManagementProvider === 'kandji') && (
									<p className="text-neutral-300">
										{/* Ahora proceda a descargar la app Krece en la AppStore en el equipo en venta. */}
										Para aceptar el contrato, el cliente debe ingresar a{' '}
										<a
											href="https://clientes.krece.app"
											target="_blank"
											rel="noopener noreferrer"
											className="font-medium text-primary-400 underline"
										>
											clientes.krece.app
										</a>
										, la versión web de la app Krece.
									</p>
								)}

								{!isOnline && (
									<div className="space-y-4">
										<p className="text-neutral-300">
											Por último, haga clic en "Generar Contrato", el cliente debe abrir la app Krece y aceptar el contrato.
										</p>

										{anexoAccepted ? (
											<Notice message={'Contrato Aceptado'} variant="success" />
										) : (
											<Button onClick={generateAnexo} variant="secondary" loading={isLoading && !anexoAccepted && !loadingText}>
												{!tokenGenerated ? 'Generar Contrato' : 'Generar Otro Contrato'}
											</Button>
										)}
									</div>
								)}

								{formError && <Notice message={getEnrollmentError(formError)} variant="error" />}

								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button
										disabled={isLoading || (!isOnline && !anexoAccepted && !pre_registered_device_id)}
										loading={isLoading && loadingText}
										type="submit"
										variant="primary"
									>
										Concretar Venta
									</Button>
									<Button
										Icon={<ArrowLeft className="size-5" />}
										disabled={isLoading}
										type="button"
										onClick={() =>
											goToPage(
												deviceManagementProvider === 'nuovo'
													? 14
													: deviceManagementProvider === 'datacultr'
														? 20
														: productType === 'laptop'
															? 10
															: 18
											)
										}
										variant="secondary"
									>
										Atrás
									</Button>
								</div>
							</form>
						</>
					)}
					{page === 20 && (
						<DatacultrEnrollmentInstructions
							key={`${imeiToRegister}:${installationSession?.activationId || ''}`}
							imei={imeiToRegister}
							isLoading={isLoading}
							error={formError}
							onVerify={() => registerDevice({ verifyOnly: true })}
							onBack={() => goToPage(10)}
						/>
					)}
					{page === 18 && (
						<>
							{render_page_header('Configuración de seguridad', null, <Shield />)}
							<form
								className="space-y-6"
								onSubmit={(e) => {
									e.preventDefault();
									if (enrollmentReady) goToPage(19);
								}}
							>
								<EnrollmentIdentity imei={imeiToRegister} deviceType={productType} />
								{deviceManagementProvider === 'kandji' || productType === 'iphone' ? (
									<IphoneEnrollmentInstructions
										imei={imeiToRegister}
										bypassCode={bypassCode}
										bypassCodeLoading={bypassCodeLoading}
										bypassCodeError={bypassCodeError ? getEnrollmentError(bypassCodeError) : ''}
										onGetBypassCode={handleGetBypassCode}
										onCopyBypassCode={() => copy(bypassCode)}
									/>
								) : (
									<ManagedAndroidEnrollmentInstructions />
								)}
								<EnrollmentReady checked={enrollmentReady} onChange={setEnrollmentReady} />
								{formError && <Notice message={getEnrollmentError(formError)} variant="error" />}
								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button
										variant="primary"
										type="submit"
										disabled={!enrollmentReady || isLoading || bypassCodeLoading}
										loading={isLoading}
									>
										Continuar
									</Button>
									<Button
										variant="secondary"
										disabled={isLoading || bypassCodeLoading}
										Icon={<ArrowLeft className="size-5" />}
										onClick={() => goToPage(10)}
									>
										Atrás
									</Button>
								</div>
							</form>
						</>
					)}
					{page === 17 &&
						disable_liveness_detection && ( //done
							<>
								{render_page_header(
									'Verificación Biométrica',
									'Por favor suba una foto tipo pasaporte del cliente',
									<KeyRound />
								)}

								<form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
									<div className="space-y-6">
										<div className="flex justify-center">
											<div className="flex h-32 items-center justify-center">
												{showSpinner ? (
													<Spinner size={3} />
												) : !cedulaVerified ? (
													<ScanFace className="size-28" strokeWidth={1} />
												) : (
													<div className="flex h-full flex-col items-center justify-center space-y-6 duration-500 animate-in fade-in-0 zoom-in-90">
														<div className="space-y-2 text-center">
															<div className="flex items-center justify-center gap-4">
																<div className="relative">
																	<div className="absolute -inset-1 animate-pulse rounded-full bg-gradient-to-r from-primary-400 to-primary-600 opacity-10 blur"></div>
																	<div className="relative rounded-full bg-neutral-900 p-4">
																		<CircleCheck className="h-6 w-6 text-primary-500" />
																	</div>
																</div>
																<h3 className="text-xl font-semibold text-primary-400">¡Verificación Exitosa!</h3>
															</div>
															<p className="text-neutral-300">La verificación biométrica se ha completado correctamente.</p>
														</div>
													</div>
												)}
											</div>
										</div>

										<div className="flex justify-center">
											<input
												name="photo"
												type="file"
												accept={ACCEPTED_IMAGE_TYPES_ATTR}
												onChange={handlePhotoChange2}
												disabled={showSpinner || cedulaVerified}
												ref={fileInputRef}
												className="peer hidden w-full bg-transparent text-sm outline-none placeholder:text-neutral-400"
											/>
											<Button
												Icon={<Camera className="size-5" />}
												disabled={showSpinner || cedulaVerified}
												onClick={() => fileInputRef.current.click()}
												variant="primary"
												className="!w-min"
											>
												Subir Imagen
											</Button>
										</div>

										{formError && <Notice message={formError} variant="error" />}

										<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
											<Button
												Icon={<ArrowLeft className="size-5" />}
												disabled={showSpinner || cedulaVerified}
												type="button"
												onClick={backToWelcomePage}
												variant="secondary"
											>
												Atrás
											</Button>
										</div>
									</div>
								</form>
							</>
						)}
					{page === 17 && !disable_liveness_detection && (
						<>
							{render_page_header(
								'Verificación Biométrica',
								'Para completar este paso necesitamos verificar su identidad usando reconocimiento facial',
								<ScanFace />
							)}
							<div className="space-y-6">
								<div className="grid gap-4 md:gap-6 lg:grid-cols-2 lg:items-stretch">
									{/* Option 1: Continue on this device */}
									{hasCameraAvailable && (
										<div
											className={`group relative flex flex-col overflow-hidden rounded-xl border p-6 backdrop-blur-sm transition-all duration-300 ${
												!cameraTestInProgress && !isCameraFunctional
													? 'pointer-events-none border-red-500/30 bg-gradient-to-br from-red-950/20 to-neutral-950/40'
													: 'border-neutral-700 bg-gradient-to-br from-neutral-950/80 to-neutral-950/40 hover:border-primary-500/60 hover:shadow-xl hover:shadow-primary-500/20'
											}`}
										>
											{/* Subtle gradient overlay */}
											<div
												className={`absolute inset-0 bg-gradient-to-br via-transparent to-transparent opacity-0 transition-opacity duration-300 ${
													!cameraTestInProgress && !isCameraFunctional
														? 'from-red-500/5 group-hover:opacity-50'
														: 'from-primary-500/5 group-hover:opacity-100'
												}`}
											/>

											<div className="relative flex flex-1 flex-col">
												<div className="mb-6 flex items-start gap-4">
													<div
														className={`relative flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl backdrop-blur-sm ${
															!cameraTestInProgress && !isCameraFunctional
																? 'bg-gradient-to-br from-red-500/20 to-red-600/10'
																: 'bg-gradient-to-br from-primary-500/30 to-primary-600/20'
														}`}
													>
														<div
															className={`absolute inset-0 rounded-xl bg-gradient-to-br opacity-0 transition-opacity duration-300 group-hover:opacity-100 ${
																!cameraTestInProgress && !isCameraFunctional
																	? 'from-red-400/20 to-red-600/10'
																	: 'from-primary-400/20 to-primary-600/10'
															}`}
														/>
														{cameraTestInProgress ? (
															<div className="flex space-x-1">
																<div className="h-2 w-2 animate-bounce rounded-full bg-primary-400 [animation-delay:-0.3s] light:bg-primary-600" />
																<div className="h-2 w-2 animate-bounce rounded-full bg-primary-400 [animation-delay:-0.15s] light:bg-primary-600" />
																<div className="h-2 w-2 animate-bounce rounded-full bg-primary-400 light:bg-primary-600" />
															</div>
														) : !isCameraFunctional ? (
															<AlertTriangle className="relative h-6 w-6 text-red-400 transition-colors duration-300 light:text-red-600" />
														) : (
															<Camera className="relative h-6 w-6 text-primary-400 transition-colors duration-300 group-hover:text-primary-300 light:text-primary-600" />
														)}
													</div>
													<div className="flex-1 space-y-3">
														<h3
															className={`text-lg font-semibold transition-colors duration-300 ${
																cameraTestInProgress
																	? 'text-neutral-0 group-hover:text-primary-100 light:group-hover:text-neutral-0'
																	: !isCameraFunctional
																		? 'text-red-400 light:text-red-600'
																		: 'text-neutral-0 group-hover:text-primary-100 light:group-hover:text-neutral-0'
															}`}
														>
															{cameraTestInProgress
																? 'Verificando cámara...'
																: !isCameraFunctional
																	? 'Cámara no disponible'
																	: 'Continuar en este dispositivo'}
														</h3>
														<p
															className={`text-sm leading-relaxed transition-colors duration-300 ${
																cameraTestInProgress
																	? 'text-neutral-300 group-hover:text-neutral-200'
																	: !isCameraFunctional
																		? 'text-red-300 light:text-red-900'
																		: 'text-neutral-300 group-hover:text-neutral-200'
															}`}
														>
															{cameraTestInProgress
																? 'Verificando si este dispositivo tiene cámara y funciona correctamente...'
																: !isCameraFunctional
																	? 'La cámara está bloqueada por el sistema operativo o el navegador. Por favor, habilite los permisos de cámara en el sistema operativo y/o navegador, y recargue la página.'
																	: 'Use la cámara de este dispositivo para realizar la verificación facial de forma rápida y segura.'}
														</p>
													</div>
												</div>

												{/* Push button to bottom */}
												<div className="mt-auto">
													<Button
														disabled={isLoading || cameraTestInProgress || !isCameraFunctional || isLivenessRateLimited}
														variant="secondary"
														className="w-full border-neutral-600 bg-neutral-800/80 text-neutral-100 backdrop-blur-sm transition-all duration-300 hover:border-primary-500/60 hover:bg-primary-500/10 hover:text-primary-100 light:hover:text-primary-700"
														onClick={async () => {
															if (isCameraFunctional && !isLivenessRateLimited) {
																const success = await initializeLiveness();
																if (success) setPage(17.1);
															}
														}}
													>
														{!isCameraFunctional ? (
															<AlertTriangle className="mr-2 h-4 w-4 shrink-0" />
														) : (
															<Camera className="mr-2 h-4 w-4 shrink-0" />
														)}
														<span className="hidden sm:inline">
															{isLivenessRateLimited
																? `Esperar ${livenessRateLimitTimer}s`
																: cameraTestInProgress
																	? 'Verificando...'
																	: !isCameraFunctional
																		? 'Cámara no disponible'
																		: 'Usar cámara de este dispositivo'}
														</span>
														<span className="sm:hidden">
															{isLivenessRateLimited
																? `Esperar ${livenessRateLimitTimer}s`
																: cameraTestInProgress
																	? 'Verificando...'
																	: !isCameraFunctional
																		? 'No disponible'
																		: 'Usar este dispositivo'}
														</span>
													</Button>
												</div>
											</div>
										</div>
									)}

									{/* Option 2: QR code for mobile */}
									<div
										className={`group relative flex flex-col overflow-hidden rounded-xl border border-neutral-700 bg-gradient-to-br from-neutral-950/80 to-neutral-950/40 p-6 backdrop-blur-sm transition-all duration-300 hover:border-neutral-600 hover:shadow-xl hover:shadow-neutral-500/10 ${
											!hasCameraAvailable ? 'col-span-2' : ''
										}`}
									>
										{/* Subtle gradient overlay */}
										<div className="absolute inset-0 bg-gradient-to-br from-neutral-600/5 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

										<div className="relative flex flex-1 flex-col">
											<div className="mb-6 flex items-start gap-4">
												<div className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-neutral-800/80 to-neutral-900/60 backdrop-blur-sm transition-all duration-300 group-hover:from-neutral-700/80 group-hover:to-neutral-800/60 light:bg-neutral-700/50">
													<QrCode className="h-6 w-6 text-neutral-400 transition-colors duration-300 group-hover:text-neutral-300" />
												</div>
												<div className="flex-1 space-y-3">
													<h3 className="text-lg font-semibold text-neutral-0 transition-colors duration-300 group-hover:text-neutral-100">
														Usar dispositivo móvil
													</h3>
													<p className="text-sm leading-relaxed text-neutral-300 transition-colors duration-300 group-hover:text-neutral-200">
														Genere un código QR para completar la verificación desde un teléfono móvil.
													</p>
													{hasCameraAvailable && (
														<div className="flex items-center gap-2 text-xs text-neutral-400 transition-colors duration-300 group-hover:text-neutral-300">
															<Smartphone className="h-4 w-4 flex-shrink-0" />
															<span>Opción alternativa</span>
														</div>
													)}
												</div>
											</div>

											{/* Push button to bottom */}
											<div className="mt-auto">
												<Button
													disabled={isLoading || isLivenessRateLimited}
													variant="ghost"
													className="w-full border border-neutral-600 bg-neutral-800/60 text-neutral-200 backdrop-blur-sm transition-all duration-300 hover:border-neutral-500 hover:bg-neutral-700/80 hover:text-neutral-100 hover:shadow-lg"
													onClick={async () => {
														if (!isLivenessRateLimited) {
															const success = await initializeLiveness();
															if (success) setPage(17.2);
														}
													}}
												>
													<QrCode className="mr-2 h-4 w-4 shrink-0" />
													{isLivenessRateLimited ? `Esperar ${livenessRateLimitTimer}s` : 'Generar código QR'}
												</Button>
											</div>
										</div>
									</div>
								</div>

								{formError && <Notice message={formError} variant="error" />}

								{/* Back button */}
								<div className="flex justify-center">
									<Button
										disabled={isLoading}
										Icon={<ArrowLeft className="size-5" />}
										type="button"
										onClick={backToWelcomePage}
										variant="secondary"
									>
										Atrás
									</Button>
								</div>
							</div>
						</>
					)}
					{page === 17.1 && (
						<>
							{render_page_header(
								'Verificación Biométrica',
								'Para completar este paso necesitamos verificar su identidad usando reconocimiento facial',
								<ScanFace />
							)}
							<div className="space-y-6">
								{/* Success State */}
								{liveness?.results?.valid_user === true &&
								liveness?.results?.user_authenticated === true &&
								liveness?.results?.photo_id_url ? (
									<div className="!mt-4 space-y-10">
										<div className="flex h-full flex-col items-center justify-center space-y-6 duration-500 animate-in fade-in-0 zoom-in-90">
											<div className="space-y-2 text-center">
												<div className="flex items-center justify-center gap-4">
													<div className="relative">
														<div className="absolute -inset-1 animate-pulse rounded-full bg-gradient-to-r from-primary-400 to-primary-600 opacity-10 blur"></div>
														<div className="relative rounded-full bg-neutral-900 p-4">
															<CircleCheck className="h-6 w-6 text-primary-500" />
														</div>
													</div>
													<h3 className="text-xl font-semibold text-primary-400">¡Verificación Exitosa!</h3>
												</div>
												<p className="text-neutral-300">La verificación biométrica se ha completado correctamente.</p>
											</div>
										</div>
									</div>
								) : (
									<>
										<LivenessDetection
											config={{
												isNewUser: !userDocId,
												userIdentificationIdUrl: !userDocId ? cedulaPhoto : undefined,
												livenessSessionId: liveness?.sessionId,
												livenessDocumentId: liveness?.documentId,
												userIdentificationNumber: format_identification_with_prefix(cedula, CountrySettings?.uid_prefix || ''),
												embeddedMode: true,
												onReady: () => {
													setHideStepper(true);
												}
											}}
										/>
										<div className="flex justify-center">
											<Button
												disabled={
													liveness?.results?.valid_user === true &&
													liveness?.results?.user_authenticated === true &&
													liveness?.results?.photo_id_url
												}
												Icon={<ArrowLeft className="size-5" />}
												type="button"
												onClick={() => {
													setLiveness(null);
													setFormError('');
													setPage(17);
												}}
												variant="secondary"
											>
												Atrás
											</Button>
										</div>
									</>
								)}
							</div>
						</>
					)}
					{page === 17.2 && (
						<>
							{render_page_header(
								'Verificación Biométrica',
								'Para completar este paso necesitamos verificar su identidad usando reconocimiento facial',
								<ScanFace />
							)}
							<div className="space-y-6">
								<div className="flex flex-col items-center justify-center gap-6 min-[875px]:flex-row min-[875px]:items-start min-[875px]:gap-4">
									<div className="flex shrink-0 flex-col items-center justify-center gap-3">
										<div className="rounded-xl border border-neutral-700 bg-neutral-950/50 p-4">
											<QRCodeSVG
												value={liveness?.qrUrl}
												bgColor="hsl(var(--twc-neutral-0))"
												fgColor="hsl(var(--twc-neutral-1000))"
												marginSize={2}
												className="size-[200px] rounded-lg"
											/>
										</div>
										<button
											onClick={async () => {
												try {
													copy(liveness?.qrUrl);
													setIsCopySuccess(true);
													setTimeout(() => {
														setIsCopySuccess(false);
													}, 2000);
												} catch (err) {
													console.error('Failed to copy URL:', err);
												}
											}}
											className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-all ${
												isCopySuccess
													? 'border-green-500/50 bg-green-500/20 text-green-400 hover:border-green-500/70 hover:bg-green-500/30'
													: 'border-neutral-600 bg-neutral-800/50 text-neutral-300 hover:border-neutral-500 hover:bg-neutral-700/50 hover:text-neutral-200'
											}`}
											title="Copiar enlace del QR"
										>
											{isCopySuccess ? (
												<>
													<CircleCheck className="size-4" />
													<span>¡Copiado!</span>
												</>
											) : (
												<>
													<Copy className="size-4" />
													<span>Copiar enlace</span>
												</>
											)}
										</button>
									</div>
									<div className="max-w-full grow space-y-6 min-[875px]:h-[233.6px]">
										<div className="relative min-[875px]:h-full">
											<div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary-500/10 via-primary-500/5 to-transparent blur-xl min-[875px]:h-full" />
											<div className="relative overflow-hidden rounded-2xl border border-primary-500/20 bg-gradient-to-br from-neutral-900/80 to-neutral-950/90 p-6 backdrop-blur-sm md:p-8 min-[875px]:h-full">
												<div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary-500/20 via-transparent to-primary-500/20 opacity-50 light:from-primary-500/10 light:to-primary-500/10">
													{liveness?.results?.processing &&
														!liveness?.results?.valid_user &&
														!liveness?.results?.user_authenticated && (
															<div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-primary-500/10 to-transparent" />
														)}
												</div>
												<div className="relative flex h-full min-h-[180px] flex-col min-[875px]:justify-center">
													{/* Success State */}
													{liveness?.results?.valid_user === true &&
													liveness?.results?.user_authenticated === true &&
													liveness?.results?.photo_id_url ? (
														<div className="flex h-full flex-col items-center justify-center space-y-6 duration-500 animate-in fade-in-0 zoom-in-90">
															<div className="space-y-4 text-center">
																<div className="flex items-center justify-center gap-4">
																	<div className="relative">
																		<div className="absolute -inset-1 animate-pulse rounded-full bg-gradient-to-r from-primary-400 to-primary-600 opacity-10 blur"></div>
																		<div className="relative rounded-full bg-neutral-900 p-4">
																			<CircleCheck className="h-6 w-6 text-primary-500" />
																		</div>
																	</div>
																	<h3 className="text-xl font-semibold text-primary-400">¡Verificación Exitosa!</h3>
																</div>
																<p className="text-neutral-300">
																	La verificación biométrica se ha completado correctamente desde el dispositivo móvil.
																</p>
															</div>
														</div>
													) : (
														/* Waiting State */
														<>
															{/* Main content section */}
															<div className="space-y-4">
																<div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:gap-6 sm:text-left">
																	<div className="relative">
																		{liveness?.results?.processing && (
																			<div className="absolute -inset-3 animate-pulse rounded-full bg-primary-500/20 blur" />
																		)}
																		<div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary-500/20 to-primary-600/20 backdrop-blur-sm light:from-primary-500/30 light:to-primary-600/30">
																			{liveness?.results?.processing ? (
																				<div className="flex space-x-1">
																					<div className="h-2 w-2 animate-bounce rounded-full bg-primary-400 [animation-delay:-0.3s] light:bg-primary-600" />
																					<div className="h-2 w-2 animate-bounce rounded-full bg-primary-400 [animation-delay:-0.15s] light:bg-primary-600" />
																					<div className="h-2 w-2 animate-bounce rounded-full bg-primary-400 light:bg-primary-600" />
																				</div>
																			) : (
																				<QrCode className="h-8 w-8 text-primary-400 light:text-primary-600" />
																			)}
																		</div>
																	</div>
																	<div className="flex-1 space-y-2">
																		<h3 className="text-lg font-semibold text-neutral-0 md:text-xl">
																			{liveness?.results?.processing
																				? 'Verificación biométrica en curso'
																				: 'Escanee el código QR'}
																		</h3>
																		<p className="text-sm leading-relaxed text-neutral-300 md:text-base min-[875px]:line-clamp-2">
																			{liveness?.results?.processing
																				? 'La verificación biométrica se está realizando. Por favor espere...'
																				: 'Escanee el código QR con su dispositivo móvil para iniciar la verificación'}
																		</p>
																	</div>
																</div>

																{/* Show error message if there was an attempt but it failed */}
																{(liveness?.results?.valid_user === false ||
																	liveness?.results?.user_authenticated === false ||
																	(liveness?.results?.valid_user === true &&
																		liveness?.results?.user_authenticated === true &&
																		!liveness?.results?.photo_id_url)) && (
																	<div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 animate-in fade-in-50 slide-in-from-top-2">
																		<div className="flex items-center gap-2">
																			<AlertTriangle className="h-4 w-4 flex-shrink-0 text-red-400" />
																			<div>
																				<p className="text-sm font-medium text-red-400 light:text-red-500">
																					Verificación Fallida
																				</p>
																				<p className="text-xs text-red-300/80 light:text-red-500/70">
																					La verificación falló. Vuelva a intentarlo desde el móvil.
																				</p>
																			</div>
																		</div>
																	</div>
																)}
															</div>

															{/* Step indicators - positioned at bottom */}
															{!(liveness?.results?.valid_user === false || liveness?.results?.user_authenticated === false) && (
																<div className="mt-auto flex flex-wrap items-center justify-center gap-2 pt-4 sm:flex-nowrap sm:justify-start">
																	<div
																		className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium ${
																			!liveness?.results?.processing
																				? 'bg-orange-500/20 text-orange-400'
																				: 'bg-neutral-800/50 text-neutral-300'
																		}`}
																	>
																		<div
																			className={`size-2 rounded-full ${
																				!liveness?.results?.processing ? 'animate-pulse bg-orange-400' : 'bg-green-400'
																			}`}
																		/>
																		<span>Paso 1: Escanear QR</span>
																	</div>
																	<div
																		className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium ${
																			liveness?.results?.processing
																				? 'bg-orange-500/20 text-orange-400'
																				: 'bg-neutral-800/30 text-neutral-400'
																		}`}
																	>
																		<div
																			className={`size-2 rounded-full ${
																				liveness?.results?.processing ? 'animate-pulse bg-orange-400' : 'bg-neutral-600'
																			}`}
																		/>
																		<span>Paso 2: Verificar rostro</span>
																	</div>
																</div>
															)}
														</>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>

								<div className="flex justify-center">
									<Button
										disabled={
											liveness?.results?.valid_user === true &&
											liveness?.results?.user_authenticated === true &&
											liveness?.results?.photo_id_url
										}
										Icon={<ArrowLeft className="size-5" />}
										type="button"
										onClick={() => {
											setLiveness(null);
											setFormError('');
											setPage(17);
										}}
										variant="secondary"
									>
										Atrás
									</Button>
								</div>
							</div>
						</>
					)}
					{page === 3 && ( //done
						<>
							{render_page_header('Información de Usuario', 'Por favor complete los datos del cliente', <ClipboardList />)}

							<form onSubmit={handleInformationSubmit} className="space-y-4">
								<div className="space-y-4 rounded-lg border border-neutral-800 bg-neutral-900/50 p-4 md:p-6">
									<Input
										name="firstName"
										floatingLabel="Nombre"
										value={firstName}
										onChange={setFirstName}
										placeholder="Ingrese el nombre"
									/>

									<Input
										name="lastName"
										floatingLabel="Apellidos"
										value={lastName}
										onChange={setLastName}
										placeholder="Ingrese los apellidos"
									/>

									<Input
										name="email"
										type="email"
										floatingLabel="Email"
										value={email}
										onChange={(value) => setEmail(value.toLowerCase().trim())}
										placeholder="Ingrese el email"
										readOnly={false}
									/>

									<Input
										name="email2"
										type="email"
										floatingLabel="Confirmación de Email"
										value={email2}
										onChange={(value) => setEmail2(value.toLowerCase().trim())}
										placeholder="Confirme el email"
										readOnly={false}
									/>

									<DatePicker
										defaultDate={birthDate || undefined}
										floatingLabel="Fecha de Nacimiento"
										onDateChange={(date) => {
											if (date) setBirthDate(date);
										}}
									/>

									<Select
										floatingLabel="Género"
										placeholder="Seleccione el género"
										options={[
											{ label: 'Masculino', value: 'male' },
											{ label: 'Femenino', value: 'female' }
										]}
										defaultValue={gender || undefined}
										onSelectedChange={setGender}
									/>

									<Input
										name="address"
										floatingLabel="Dirección de hogar"
										value={address}
										onChange={setAddress}
										placeholder="Ingrese la dirección"
									/>

									{disableSalePhone2fa && (
										<Input
											name="phoneNumber"
											type="tel"
											floatingLabel="Número de teléfono"
											value={phoneNumber}
											onChange={setPhoneNumber}
											placeholder="Ingrese el número"
										/>
									)}
								</div>

								{!disableSalePhone2fa && (
									<div className="pos-phone-verification space-y-4 rounded-lg border border-neutral-800 bg-neutral-900/50 p-4 md:p-6">
										<h2 className="mb-4 text-xl font-medium tracking-tighter text-neutral-0">
											Verificación de Número de Teléfono
										</h2>

										<div className="pos-phone-fields flex flex-wrap gap-4">
											<div className="min-w-[240px] flex-1 space-y-4">
												<Input
													name="phoneNumber"
													type="tel"
													floatingLabel="Número de teléfono"
													value={phoneNumber}
													onChange={setPhoneNumber}
													placeholder="Ingrese el número"
													readOnly={tokenVerified}
												/>

												{showTokenField && (
													<Input
														name="phoneToken"
														type="tel"
														floatingLabel="Token"
														value={phoneToken}
														onChange={fillToken}
														placeholder="Ingrese el token"
														allowedChars="numbers_only"
													/>
												)}
											</div>

											<div className="w-40">
												<Select
													floatingLabel="Tipo"
													placeholder="Seleccionar"
													options={[
														{ label: 'Whatsapp', value: 'whatsapp' },
														{ label: 'SMS', value: 'sms' }
													]}
													defaultValue={messType || 'sms'}
													onSelectedChange={setMessType}
												/>
											</div>
										</div>

										<div className="pos-phone-status flex items-center justify-between">
											{tokenVerified && !loadingButton ? (
												<div className="flex items-center gap-2 text-primary-400">
													<CircleCheck className="size-5" />
													<span className="font-medium">Verificado</span>
												</div>
											) : timeLeft === 0 ? (
												<Button loading={loadingButton} type="button" onClick={sendPhoneToken} variant="primary">
													Enviar Token
												</Button>
											) : null}

											{timeLeft > 0 && <p className="text-neutral-400">Puede enviar otro token en: {timeLeft}</p>}
										</div>
									</div>
								)}

								{formError && <Notice message={formError} variant="error" />}

								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button
										disabled={!disableSalePhone2fa ? isLoading || !tokenVerified || loadingButton : isLoading}
										loading={isLoading}
										type="submit"
										variant="primary"
									>
										Ingresar
									</Button>
									<Button
										Icon={<ArrowLeft className="size-5" />}
										disabled={loadingButton || isLoading}
										type="button"
										onClick={backToWelcomePage}
										variant="secondary"
									>
										Atrás
									</Button>
								</div>
							</form>
						</>
					)}
					{page === 8 && ( //done
						<>
							{render_page_header(
								'Datos Adicionales',
								'Por favor complete la información adicional del cliente',
								<ClipboardList />
							)}

							<form onSubmit={handleApplicationSubmit} className="space-y-4">
								<div className="space-y-4 rounded-lg border border-neutral-800 bg-neutral-900/50 p-4 md:p-6">
									<Select
										floatingLabel="Ciudad donde vives"
										placeholder="Seleccione la ciudad"
										options={CountrySettings.cities.map((item) => ({
											label: item,
											value: item
										}))}
										defaultValue={city}
										onSelectedChange={(value) => setCity(value)}
									/>

									<Select
										floatingLabel={`¿De cuánto son tus ingresos mensuales?`}
										placeholder="Seleccione el rango"
										options={CountrySettings.salary_ranges.map((item) => ({
											label: item,
											value: item.split(CountrySettings.currency_code)[1]
										}))}
										defaultValue={monthlySalary}
										onSelectedChange={(value) => setMonthlySalary(value)}
									/>

									<Select
										floatingLabel={`Ahorros estimados (en ${CountrySettings.currency_name})`}
										placeholder="Seleccione el rango"
										options={CountrySettings.salary_ranges.map((item) => ({
											label: item,
											value: item.split(CountrySettings?.currency_code ?? '')?.[1]
										}))}
										defaultValue={savings}
										onSelectedChange={(value) => setSavings(value)}
									/>

									<Select
										floatingLabel="Nivel de educación"
										placeholder="Seleccione el nivel"
										options={[
											{ label: 'Primaria', value: 'Primaria' },
											...(country === 'peru' ? [{ label: 'Secundaria', value: 'Secundaria' }] : []),
											{ label: 'Bachillerato', value: 'Bachillerato' },
											{ label: 'Universidad', value: 'Universidad' }
										]}
										defaultValue={educationLevel}
										onSelectedChange={(value) => setEducationLevel(value)}
									/>
								</div>

								<div className="space-y-4 rounded-lg border border-neutral-800 bg-neutral-900/50 p-4 md:p-6">
									<h4 className="text-sm font-medium text-neutral-200">Información del producto que planea comprar</h4>

									<Select
										floatingLabel="Marca del producto"
										placeholder="Seleccione la marca"
										options={getRiskBrandOptions(CountrySettings.brands_allowed?.phone, country)}
										defaultValue={firstDeviceManufacturer}
										onSelectedChange={(value) => setFirstDeviceManufacturer(value)}
									/>

									<Input
										floatingLabel={`Precio estimado del producto (en ${CountrySettings.currency_name})`}
										placeholder="0.00"
										allowedChars="numbers_only"
										value={firstDevicePrice}
										onChange={(value) => setFirstDevicePrice(value)}
										Icon={
											CountrySettings?.currency_code ? (
												<span className="text-sm text-neutral-500">{CountrySettings.currency_code}</span>
											) : undefined
										}
									/>
								</div>

								{formError && <Notice message={formError} variant="error" />}

								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button disabled={isLoading} loading={isLoading} type="submit" variant="primary">
										Ingresar
									</Button>
									<Button
										Icon={<ArrowLeft className="size-5" />}
										disabled={isLoading}
										type="button"
										onClick={backToWelcomePage}
										variant="secondary"
									>
										Atrás
									</Button>
								</div>
							</form>
						</>
					)}
					{page === 4 && ( //done
						<>
							{render_page_header(
								simulator ? 'Simulador de Financiamiento' : '¡Felicitaciones!',
								simulator
									? 'Simula diferentes opciones de financiamiento y niveles'
									: '¡Has sido aprobado para una línea de crédito!',
								!simulator ? <CircleCheck className="size-6" /> : <Calculator className="size-6" />
							)}
							<form onSubmit={handleRegistrationSubmit} className="space-y-6">
								{/* Simulator Cedula Input */}
								{simulator && (
									<div className="mb-6 rounded-xl border border-neutral-800 bg-background p-3 md:p-6">
										<div className="mb-3 flex items-center gap-2 md:mb-4">
											<span className="text-sm font-medium text-neutral-400">
												Buscar Usuario por {country === 'peru' ? 'DNI' : 'Cédula'}
											</span>
											<Popover>
												<PopoverTrigger asChild>
													<button className="text-neutral-400 hover:text-neutral-300">
														<Info className="size-4" />
													</button>
												</PopoverTrigger>
												<PopoverContent className="w-72 rounded-xl border border-neutral-800 bg-background p-3 text-sm text-neutral-300 shadow-md md:w-80">
													Ingrese {country === 'peru' ? 'el DNI' : 'la cédula'} para cargar la información real de un usuario
													registrado en lugar de usar valores manuales.
												</PopoverContent>
											</Popover>
										</div>
										{!simulatorUserLoaded ? (
											<div className="flex flex-col gap-3 sm:flex-row">
												<Input
													name="cedula"
													floatingLabel={`${country === 'peru' ? 'DNI' : 'Cédula'} del usuario`}
													value={simulatorCedula}
													onChange={(value) => setSimulatorCedula(value)}
													placeholder={`Ingrese ${country === 'peru' ? 'el DNI' : 'la cédula'} del usuario`}
													allowedChars="numbers_only"
													containerClassName="w-full"
													onSubmit={() => {
														if (simulatorCedula) {
															handleSimulatorCedulaSubmit();
														}
													}}
												/>

												<Button
													disabled={!simulatorCedula}
													loading={isLoadingSimulatorUser}
													type="button"
													onClick={handleSimulatorCedulaSubmit}
													variant="primary"
													className="h-auto w-full sm:h-[3rem] sm:w-[100px]"
												>
													Buscar
												</Button>
											</div>
										) : (
											<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
												<div className="text-sm text-neutral-300">
													Usuario cargado: <span className="font-medium text-primary-400">{simulatorCedula}</span>
												</div>
												<Button
													type="button"
													onClick={() => {
														setSimulatorCedula('');
														setSimulatorUserLoaded(false);
														setSimulatorError('');
														setUserDocId('');
														// Reset to manual mode
														const credit_line = LevelsConfig?.[country]?.[selectedLevel]?.credit_line;
														const defaultCredit = credit_line || (country === 'peru' ? 700 : country === 'rd' ? 7000 : 300);
														setManualApprovedCredit(defaultCredit);
														setPrimaryCreditLine(defaultCredit);
														setSecondaryCreditLine(defaultCredit);
													}}
													variant="secondary"
													size="sm"
													className="w-full sm:w-auto"
												>
													Limpiar
												</Button>
											</div>
										)}
										{simulatorError && <Notice message={simulatorError} variant="error" className="mt-3" />}
									</div>
								)}

								{/* Top Info Cards: Approved Line, Required Down Payment, Level Info */}
								<div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
									{/* Approved Line Card */}
									<div className="flex flex-col justify-between rounded-xl border border-neutral-800 bg-background p-4 transition-all duration-300 hover:border-primary-500/50 md:p-6 md:pb-5">
										<div className="flex h-full flex-col md:justify-between">
											<div className="flex items-center gap-2">
												<span className="text-sm font-medium text-neutral-400">Línea aprobada</span>
												<Popover>
													<PopoverTrigger asChild>
														<button className="text-neutral-400 hover:text-neutral-300">
															<Info className="size-4" />
														</button>
													</PopoverTrigger>
													<PopoverContent className="w-72 rounded-xl border border-neutral-800 bg-background p-3 text-sm text-neutral-300 shadow-md md:w-80">
														Si desea un equipo que requiere una línea de compra mayor, deberá pagar la diferencia como parte de la
														cuota inicial.
													</PopoverContent>
												</Popover>
											</div>
											{simulator && !simulatorUserLoaded ? (
												<Input
													name="approvedCredit"
													type="number"
													value={manualApprovedCredit ? manualApprovedCredit.toString() : ''}
													onChange={(value) => {
														const numValue = parseInt(value || '0');
														setManualApprovedCredit(numValue);
														setPrimaryCreditLine(numValue);
														setSecondaryCreditLine(numValue);
													}}
													prefix={CountrySettings.currency_code}
													placeholder="Monto"
													className="mt-1"
													allowedChars="numbers_only"
												/>
											) : (
												<p className="mt-1 bg-gradient-to-r from-primary-400 to-primary-600 bg-clip-text text-2xl font-bold text-transparent light:from-primary-500 md:flex md:h-full md:items-center md:text-4xl">
													{approvedCredit === Infinity ? 'Ilimitada' : `${CountrySettings.currency_code}${approvedCredit}`}
												</p>
											)}
										</div>
									</div>

									{/* Required Down Payment Card */}
									<div className="flex flex-col justify-between rounded-xl border border-neutral-800 bg-background p-4 transition-all duration-300 hover:border-primary-500/50 md:p-6 md:pb-5">
										<div className="flex h-full flex-col md:justify-between">
											<span className="block text-sm font-medium text-neutral-400">Inicial requerida</span>
											<p className="mt-1 bg-gradient-to-r from-primary-400 to-primary-600 bg-clip-text text-2xl font-bold text-transparent light:from-primary-500 md:flex md:h-full md:items-center md:text-4xl">
												{(downPaymentOptions?.min ?? 0.4) === (downPaymentOptions?.max ?? 0.4)
													? `${Math.floor((downPaymentOptions?.min ?? 0.4) * 100)}%`
													: `${Math.floor((downPaymentOptions?.min ?? 0.4) * 100)}-${Math.floor((downPaymentOptions?.max ?? 0.4) * 100)}%`}
											</p>
										</div>
									</div>

									{/* Level Information Card or Level Selector in Simulator Mode */}
									{simulator && !simulatorUserLoaded ? (
										<div className="flex flex-col justify-between rounded-xl border border-neutral-800 bg-background p-4 transition-all duration-300 hover:border-primary-500/50 md:p-6 md:pb-5">
											<div className="flex h-full flex-col md:justify-between">
												<span className="block text-sm font-medium text-neutral-400">Nivel de cliente</span>
												<Select
													className="mt-1"
													placeholder="Seleccionar nivel"
													options={[
														{ label: 'Krece Azul', value: 'level_1' },
														{ label: 'Krece Plata', value: 'level_2' },
														{ label: 'Krece Oro', value: 'level_3' },
														{ label: 'Krece Platino', value: 'level_4' }
													]}
													defaultValue={selectedLevel}
													onSelectedChange={setSelectedLevel}
												/>
											</div>
										</div>
									) : (
										userFinancingOptions?.level_information && (
											<div className="flex flex-col justify-between rounded-xl border border-neutral-800 bg-background p-4 transition-all duration-300 hover:border-primary-500/50 md:p-6 md:pb-5">
												{/* Top Section - Always Visible */}
												<div className="flex flex-1 items-start justify-between gap-4">
													<div className="flex-1">
														<h3 className="text-base font-semibold text-neutral-0 md:text-lg">
															{userFinancingOptions.level_information.name}
														</h3>
														<div className="mt-1 flex items-center gap-1.5 text-xs text-neutral-300 light:text-neutral-600 md:text-sm">
															<Award className="h-3.5 w-3.5 flex-shrink-0 text-neutral-200" />
															<span>{levelPoints} puntos</span>
														</div>
													</div>
													{/* Krece Logo - Only component that changes color based on level */}
													<div className="relative">
														<Logo
															size="min"
															style={{
																fill: getLevelColor(userFinancingOptions.level_information.name)
															}}
															className="relative h-8 w-8 flex-shrink-0"
														/>
													</div>
												</div>

												{/* Benefits Button with Popover */}
												<Popover>
													<PopoverTrigger asChild>
														<button className="group mt-4 flex w-full items-center justify-between rounded-md border border-neutral-800 bg-background px-4 py-3 text-left text-xs text-primary-400 transition-all duration-300 hover:border-primary-500/70 hover:bg-primary-500/10 md:text-sm">
															<span className="font-medium">Ver Beneficios</span>
															<Info className="h-4 w-4 flex-shrink-0 text-primary-500 transition-opacity duration-200" />
														</button>
													</PopoverTrigger>
													<PopoverContent
														className="w-72 border border-neutral-700/50 shadow-lg backdrop-blur-sm md:w-80"
														align="start"
														sideOffset={5}
													>
														<div className="space-y-3 text-neutral-200 light:text-neutral-800">
															<div className="flex items-center gap-2">
																<TrendingUp className="h-5 w-5 text-primary-500" />
																<h4 className="font-medium text-neutral-100">Beneficios de tu nivel</h4>
															</div>
															<ul className="space-y-2 pl-4 text-sm text-neutral-300 light:text-neutral-600">
																{userFinancingOptions.level_information.benefits.map((benefit, index) => (
																	<li key={index} className="flex items-start gap-2">
																		<div className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-500"></div>
																		<span>{benefit}</span>
																	</li>
																))}
															</ul>
														</div>
													</PopoverContent>
												</Popover>
											</div>
										)
									)}
								</div>

								{/* Add the promo selection UI here */}
								{promotionsError && (
									<div className="mb-6">
										<Notice message={promotionsError} variant="warning" />
									</div>
								)}
								{shouldBlockPromotionsForMinDownPayment && store_togglable_promotions?.length > 0 && (
									<div className="mb-6">
										<Notice
											message={`Las promociones no están disponibles para este usuario.`}
											variant="warning"
										/>
									</div>
								)}

								{eligibleTogglablePromotions.length > 0 && (
									<div className="mb-6">
										<Accordion.Root type="single" collapsible defaultValue="promos" className="space-y-2 rounded-md">
											<Accordion.Item value="promos" className="overflow-hidden rounded-xl border border-neutral-800">
												<Accordion.Trigger className="flex w-full items-center justify-between bg-background px-4 py-3 text-left md:px-5 md:py-4">
													<div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
														<span className="text-sm font-medium text-neutral-100 md:text-base">Promociones disponibles</span>
														{activePromo && (
															<span className="rounded-full bg-primary-500/20 px-2 py-0.5 text-xs font-medium text-primary-400 light:text-primary-600">
																Activa: {activePromo.label}
															</span>
														)}
													</div>
													<ChevronDown className="h-5 w-5 text-neutral-400 transition-transform duration-200 ease-in-out group-data-[state=open]:rotate-180" />
												</Accordion.Trigger>
												<Accordion.Content className="border-t border-neutral-800 bg-background px-4 py-3 md:px-5 md:py-4">
													<div className="space-y-4">
														<div className="flex flex-wrap gap-3">
															{eligibleTogglablePromotions.map((promo) => (
																	<div
																		key={promo.label}
																		className={classNames(
																			'flex items-center gap-3 rounded-lg border px-3 py-2 transition-all duration-150 md:px-4 md:py-3',
																			activePromo?.label === promo.label
																				? 'border-primary-500/50 bg-primary-500/10'
																				: 'border-neutral-700/50 bg-neutral-800/50 hover:border-neutral-600 light:bg-neutral-800/30'
																		)}
																	>
																		<div className="flex-1">
																			<h4 className="text-xs font-medium text-neutral-100 md:text-sm">Promo {promo.label}</h4>
																		</div>
																		<Switch
																			checked={activePromo?.label === promo.label}
																			onCheckedChange={() => {
																				const activate = activePromo?.label !== promo.label;
																				setSelectedPromo(activate ? promo : null);
																				if (!promo?.product_types?.includes(productType))
																					setProductType(promo?.product_types?.[0] || 'phone');
																			}}
																		/>
																	</div>
																))}
														</div>
														{activePromo && (
															<div className="rounded-lg border border-primary-500/20 bg-primary-500/5 p-3">
																<p className="text-xs text-neutral-200 md:text-sm">{activePromo.description}</p>
															</div>
														)}
													</div>
												</Accordion.Content>
											</Accordion.Item>
										</Accordion.Root>
									</div>
								)}

								<div className="grid gap-6 md:grid-cols-2">
									{/* Product Selection Cards */}
									<div className="space-y-4 rounded-xl border border-neutral-800 bg-background p-4 md:p-5">
										<h3 className="font-medium text-neutral-200">Tipo de producto</h3>
										<div className="grid grid-cols-2 gap-2 md:gap-3">
											<button
												type="button"
												onClick={() => setProductType('phone')}
												disabled={activePromo?.product_types?.includes('phone') === false}
												className={classNames(
													'flex flex-col items-center justify-center rounded-lg border border-neutral-700 bg-neutral-800/50 p-3 transition-all duration-200 hover:border-primary-500/70 disabled:pointer-events-none disabled:opacity-50 light:bg-neutral-800/30 md:p-4',
													{
														'border-primary-500 bg-primary-500/10 light:border-primary-600 light:bg-primary-600/15':
															productType === 'phone'
													}
												)}
											>
												<Smartphone
													className={classNames(
														'mb-1 size-5 md:mb-2 md:size-6',
														productType === 'phone' ? 'text-primary-400 light:text-primary-700' : 'text-neutral-400'
													)}
												/>
												<span
													className={classNames(
														'text-xs font-medium md:text-sm',
														productType === 'phone' ? 'text-primary-400 light:text-primary-700' : 'text-neutral-300'
													)}
												>
													Celular Android
												</span>
												{activePromo?.product_types?.includes('phone') === false && (
													<span className="mt-1 text-[9px] text-neutral-500 md:text-[10px]">No disponible</span>
												)}
											</button>

											{allowIphone && iphoneFinancingOptionsAvailable && (
												<button
													type="button"
													onClick={() => setProductType('iphone')}
													disabled={activePromo?.product_types?.includes('iphone') === false}
													className={classNames(
														'flex flex-col items-center justify-center rounded-lg border border-neutral-700 bg-neutral-800/50 p-3 transition-all duration-200 hover:border-primary-500/70 disabled:pointer-events-none disabled:opacity-50 light:bg-neutral-800/30 md:p-4',
														{
															'border-primary-500 bg-primary-500/10 light:border-primary-600 light:bg-primary-600/15':
																productType === 'iphone'
														}
													)}
												>
													<Smartphone
														className={classNames(
															'mb-1 size-5 md:mb-2 md:size-6',
															productType === 'iphone' ? 'text-primary-400 light:text-primary-700' : 'text-neutral-400'
														)}
													/>
													<span
														className={classNames(
															'text-xs font-medium md:text-sm',
															productType === 'iphone' ? 'text-primary-400 light:text-primary-700' : 'text-neutral-300'
														)}
													>
														iPhone
													</span>
													{activePromo?.product_types?.includes('iphone') === false && (
														<span className="mt-1 text-[9px] text-neutral-500 md:text-[10px]">No disponible</span>
													)}
												</button>
											)}

											<button
												type="button"
												onClick={() => setProductType('tablet')}
												disabled={activePromo?.product_types?.includes('tablet') === false}
												className={classNames(
													'flex flex-col items-center justify-center rounded-lg border border-neutral-700 bg-neutral-800/50 p-3 transition-all duration-200 hover:border-primary-500/70 disabled:pointer-events-none disabled:opacity-50 light:bg-neutral-800/30 md:p-4',
													{
														'border-primary-500 bg-primary-500/10 light:border-primary-600 light:bg-primary-600/15':
															productType === 'tablet'
													}
												)}
											>
												<Tablet
													className={classNames(
														'mb-1 size-5 md:mb-2 md:size-6',
														productType === 'tablet' ? 'text-primary-400 light:text-primary-700' : 'text-neutral-400'
													)}
												/>
												<span
													className={classNames(
														'text-xs font-medium md:text-sm',
														productType === 'tablet' ? 'text-primary-400 light:text-primary-700' : 'text-neutral-300'
													)}
												>
													Tablet
												</span>
												{activePromo?.product_types?.includes('tablet') === false && (
													<span className="mt-1 text-[9px] text-neutral-500 md:text-[10px]">No disponible</span>
												)}
											</button>

											{allowLaptop && laptopFinancingOptionsAvailable && (
												<button
													type="button"
													onClick={() => setProductType('laptop')}
													disabled={activePromo?.product_types?.includes('laptop') === false}
													className={classNames(
														'flex flex-col items-center justify-center rounded-lg border border-neutral-700 bg-neutral-800/50 p-3 transition-all duration-200 hover:border-primary-500/70 disabled:pointer-events-none disabled:opacity-50 light:bg-neutral-800/30 md:p-4',
														{
															'border-primary-500 bg-primary-500/10 light:border-primary-600 light:bg-primary-600/15':
																productType === 'laptop'
														}
													)}
												>
													<Laptop
														className={classNames(
															'mb-1 size-5 md:mb-2 md:size-6',
															productType === 'laptop' ? 'text-primary-400 light:text-primary-700' : 'text-neutral-400'
														)}
													/>
													<span
														className={classNames(
															'text-xs font-medium md:text-sm',
															productType === 'laptop' ? 'text-primary-400 light:text-primary-700' : 'text-neutral-300'
														)}
													>
														Laptop
													</span>
													{activePromo?.product_types?.includes('laptop') === false && (
														<span className="mt-1 text-[9px] text-neutral-500 md:text-[10px]">No disponible</span>
													)}
												</button>
											)}

											<button
												type="button"
												onClick={() => setProductType('other')}
												disabled={activePromo?.product_types?.includes('other') === false || !canRegisterOtherProduct}
												className={classNames(
													'flex flex-col items-center justify-center rounded-lg border border-neutral-700 bg-neutral-800/50 p-3 transition-all duration-200 hover:border-primary-500/70 disabled:pointer-events-none disabled:opacity-50 light:bg-neutral-800/30 md:p-4',
													{
														'border-primary-500 bg-primary-500/10 light:border-primary-600 light:bg-primary-600/15':
															productType === 'other'
													}
												)}
											>
												<ShoppingBag
													className={classNames(
														'mb-1 size-5 md:mb-2 md:size-6',
														productType === 'other' ? 'text-primary-400 light:text-primary-700' : 'text-neutral-400'
													)}
												/>
												<span
													className={classNames(
														'text-xs font-medium md:text-sm',
														productType === 'other' ? 'text-primary-400 light:text-primary-700' : 'text-neutral-300'
													)}
												>
													Otros
												</span>
												{!hasAvailableOtherProductCredit ? (
													<span className="mt-1 text-[9px] text-neutral-500 md:text-[10px]">Sin cupo disponible</span>
												) : (
													(activePromo?.product_types?.includes('other') === false || !userCanPurchaseOthers) && (
														<span className="mt-1 text-[9px] text-neutral-500 md:text-[10px]">No disponible</span>
													)
												)}
											</button>
										</div>

										{/* Product Name (for other) */}
										{productType === 'other' && !simulator && (
											<div className="mt-4 md:mt-6">
												<Input
													floatingLabel={'Nombre del producto'}
													value={productName}
													onChange={(value) => setProductName(value)}
													placeholder="Ingresar nombre..."
													className={!productName ? '!border-red-400 !ring-2 !ring-red-400' : ''}
												/>
											</div>
										)}

										{['phone', 'tablet', 'laptop'].includes(productType) && (
											<div className="mt-4 md:mt-6">
												<Select
													floatingLabel="Marca del producto"
													placeholder="Seleccione la marca"
													options={CountrySettings.brands_allowed[productType === 'laptop' ? 'laptop' : 'phone']
														?.toSorted()
														.map((brand) => ({ label: brand, value: brand }))}
													onSelectedChange={(value) => setProductManufacturer(value)}
													className={!productManufacturer ? '!border-red-400 !ring-2 !ring-red-400' : ''}
												/>
											</div>
										)}
									</div>

									{/* Payment Options */}
									<div className="space-y-4 rounded-xl border border-neutral-800 bg-background p-4 md:p-5">
										<h3 className="font-medium text-neutral-200">Opciones de pago</h3>

										<Input
											className={!price ? '!border-red-400 !ring-2 !ring-red-400' : ''}
											containerClassName="py-2"
											floatingLabel={`Precio de venta (en ${CountrySettings.currency_name})`}
											placeholder="0.00"
											allowedChars="numbers_only"
											value={price ? price.toString() : undefined}
											onChange={(value) => setPrice(value ? Number.parseFloat(value) : 0)}
											validation={(value) => (!value || !Number.parseFloat(value) ? false : true)}
											Icon={
												CountrySettings?.currency_code ? (
													<span className="text-sm text-neutral-500">{CountrySettings.currency_code}</span>
												) : undefined
											}
										/>

										{/* Down Payment Options */}
										{downPaymentOptions?.min < downPaymentOptions?.max ? (
											<div className="space-y-2">
												<div className="flex items-center justify-between">
													<label className="text-xs text-neutral-300 md:text-sm">Inicial deseada</label>
													<span className="text-xs font-bold text-primary-400 light:text-primary-600 md:text-sm">
														{downPaymentPerc * 100}%
													</span>
												</div>
												<Slider
													min={downPaymentOptions?.min ?? 0.4}
													max={downPaymentOptions?.max ?? 0.5}
													step={Math.min(
														downPaymentOptions?.granularity || 0.05,
														(downPaymentOptions?.max ?? 0.5) - (downPaymentOptions?.min ?? 0.4)
													)}
													value={[downPaymentPerc]}
													onValueChange={(value) => setDownPaymentPerc(value[0])}
													className="py-3 md:py-4"
												/>
												<div className="flex justify-between text-[10px] text-neutral-400 md:text-xs">
													<span>{downPaymentOptions?.min * 100}%</span>
													<span>{downPaymentOptions?.max * 100}%</span>
												</div>
											</div>
										) : (
											<div className="space-y-2">
												<div className="flex items-center justify-between">
													<label className="text-xs text-neutral-300 md:text-sm">Inicial requerida</label>
												</div>
												<div className="flex items-center rounded-lg border border-neutral-700/50 bg-neutral-800/30 p-3">
													<div className="flex flex-1 items-center justify-center">
														<span className="text-sm font-medium text-neutral-300">Inicial requerida de</span>
														<span className="ml-1.5 rounded-full bg-primary-500/20 px-2.5 py-1 text-sm font-semibold text-primary-400 light:text-primary-600">
															{parseInt(price) <= maxFinanceableDevicePrice
																? Math.floor(downPaymentPerc * 100)
																: Math.floor((initialPaymentAmount / parseInt(price)) * 100)}
															%
														</span>
													</div>
												</div>
											</div>
										)}

										{/* Installment Options */}
										{paymentOptions?.length > 1 ? (
											<div className="space-y-4 md:space-y-5">
												<div className="space-y-2">
													<div className="flex items-center justify-between">
														<label className="text-xs text-neutral-300 md:text-sm">Número de cuotas</label>
														<span className="text-xs font-bold text-primary-400 light:text-primary-600 md:text-sm">
															{installments} cuotas
														</span>
													</div>
													<Slider
														min={0}
														max={configuredInstallmentOptions.length - 1}
														step={1}
														value={[selectedInstallmentIndex]}
														onValueChange={(value) => setInstallments(configuredInstallmentOptions[value[0]])}
														className="py-3 md:py-4"
													/>
													<div className="flex justify-between text-[10px] text-neutral-400 md:text-xs">
														{configuredInstallmentOptions.map((option) => (
															<span key={option}>{option}</span>
														))}
													</div>
												</div>
											</div>
										) : (
											<div className="space-y-2">
												<div className="flex items-center justify-between">
													<label className="text-xs text-neutral-300 md:text-sm">Número de cuotas</label>
												</div>
												<div className="flex items-center rounded-lg border border-neutral-700/50 bg-neutral-800/30 p-3">
													<div className="flex flex-1 items-center justify-center">
														<span className="text-sm font-medium text-neutral-300">Plan fijo de</span>
														<span className="ml-1.5 rounded-full bg-primary-500/20 px-2.5 py-1 text-sm font-semibold text-primary-400 light:text-primary-600">
															{installments} cuotas
														</span>
													</div>
												</div>
											</div>
										)}
									</div>
								</div>

								{/* Payment Summary */}
								<div className="space-y-3 rounded-xl border border-primary-500/20 bg-primary-500/5 p-4 light:border-primary-600/30 light:bg-primary-600/5 md:p-5">
									<h3 className="text-base font-medium text-primary-400 light:text-black md:text-lg">Resumen de pagos</h3>
									{loadingVariableFactor ? (
										<div className="grid grid-cols-1 gap-3 md:grid-cols-4 md:gap-4">
											{[...Array(4)].map((_, index) => (
												<div key={index} className="animate-pulse space-y-1 rounded-lg bg-neutral-900/50 p-3">
													<div className="h-5 w-3/4 rounded bg-neutral-800"></div>
													<div className="h-7 w-1/2 rounded bg-neutral-800"></div>
												</div>
											))}
										</div>
									) : (
										<div className="grid grid-cols-1 gap-3 md:grid-cols-4 md:gap-4">
											<div className="space-y-1 rounded-lg bg-neutral-900/50 p-3">
												<p className="text-xs text-neutral-400 md:text-sm">
													{price && isDownPaymentValid
														? 'Pagar ahora (' +
															(parseInt(price) <= maxFinanceableDevicePrice
																? Math.floor(downPaymentPerc * 100)
																: Math.floor((initialPaymentAmount / parseInt(price)) * 100)) +
															'%)'
														: 'Pagar ahora'}
												</p>
												<p className="text-lg font-semibold text-neutral-100 md:text-xl">
													{CountrySettings.currency_code}
													{price && isDownPaymentValid ? format_currency(initialPaymentAmount, country === 'rd') : ' --.--'}
												</p>
											</div>
											<div className="space-y-1 rounded-lg bg-neutral-900/50 p-3">
												<p className="text-xs text-neutral-400 md:text-sm">Paga después</p>
												{price && isDownPaymentValid && installments && discountedAmount && discountedPercentage ? (
													<div>
														<div className="flex items-center gap-2">
															<p className="text-sm font-medium text-neutral-400 line-through md:text-base">
																{CountrySettings.currency_code}
																{format_currency(installmentsAmount * installments + discountedAmount, country === 'rd')}
															</p>
															<span className="rounded-full bg-green-500/20 px-2 py-0.5 text-xs font-semibold text-green-400 light:bg-green-500/10 light:text-green-500">
																-{discountedPercentage}%
															</span>
														</div>
														<p className="text-lg font-semibold text-neutral-100 md:text-xl">
															{CountrySettings.currency_code}
															{format_currency(installmentsAmount * installments, country === 'rd')}
														</p>
													</div>
												) : price && isDownPaymentValid && installments ? (
													<p className="text-lg font-semibold text-neutral-100 md:text-xl">
														{CountrySettings.currency_code}
														{format_currency(installmentsAmount * installments, country === 'rd')}
													</p>
												) : (
													<p className="text-lg font-semibold text-neutral-100 md:text-xl">
														{CountrySettings.currency_code}
														{' --.--'}
													</p>
												)}
											</div>
											<div className="space-y-1 rounded-lg bg-neutral-900/50 p-3">
												<p className="text-xs text-neutral-400 md:text-sm">{installments || '-'} cuotas de</p>
												<p className="text-lg font-semibold text-neutral-100 md:text-xl">
													{CountrySettings.currency_code}
													{price && isDownPaymentValid && installments
														? format_currency(installmentsAmount, country === 'rd')
														: ' --.--'}
												</p>
											</div>
											<div className="space-y-1 rounded-lg bg-neutral-900/50 p-3">
												<p className="text-xs text-neutral-400 md:text-sm">Total</p>
												<p className="text-lg font-semibold text-neutral-100 md:text-xl">
													{CountrySettings.currency_code}
													{price && isDownPaymentValid && installments ? format_currency(totalAmount, country === 'rd') : ' --.--'}
												</p>
											</div>
										</div>
									)}
									{price && isDownPaymentValid && installments && discountedAmount && discountedPercentage ? (
										<div className="mt-2 rounded-lg bg-primary-500/10 p-3">
											<div className="flex items-center gap-2">
												<div className="flex-shrink-0">
													<span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500/30">
														<TicketPercent className="size-4" />
													</span>
												</div>
												<p className="text-xs text-neutral-200 md:text-sm">
													<span className="font-semibold text-primary-400 light:text-primary-600">
														¡Descuento nivel {userFinancingOptions.level_information.name}!
													</span>{' '}
													Estás ahorrando{' '}
													<span className="font-bold text-green-400 light:text-green-500">
														{CountrySettings.currency_code}
														{format_currency(discountedAmount, country === 'rd')}
													</span>{' '}
													en esta compra gracias a tu nivel de cliente.
												</p>
											</div>
										</div>
									) : null}
								</div>

								{simulator && (
									<Notice
										message="Esta es una simulación estimada. Los valores definitivos se calculan al momento de la venta."
										variant="info"
									/>
								)}

								{/* Location Information */}

								{!simulator && (
									<div className="space-y-4 rounded-xl border border-neutral-800 bg-background p-4 md:p-5">
										<h3 className="font-medium text-neutral-200">Información de ubicación</h3>
										<div className="grid gap-4 md:grid-cols-2">
											{/* Online/In-store sale */}
											{allowOnline && (
												<div className="space-y-2">
													<Select
														placeholder="Seleccionar tipo..."
														floatingLabel="Tipo de venta"
														options={[
															{ label: 'Online', value: true },
															{ label: 'En tienda física', value: false }
														]}
														defaultValue={isOnline}
														onSelectedChange={(value) => setIsOnline(value)}
													/>
												</div>
											)}
											{/* Store Location */}
											<div className="space-y-2">
												<Select
													disabled={subStore && subStoreList?.length === 1}
													placeholder="Seleccionar local..."
													floatingLabel="Local de venta"
													options={subStoreList.map((item) => ({
														label: item,
														value: item
													}))}
													defaultValue={subStore}
													onSelectedChange={(value) => setSubStore(value)}
													className={!subStore ? '!border-red-400 !ring-2 !ring-red-400' : ''}
												/>
											</div>
										</div>
									</div>
								)}

								{formError && <Notice message={formError} variant="error" />}

								{!simulator && (
									<div className="flex flex-col gap-2 md:flex-row-reverse md:flex-nowrap">
										<Button
											disabled={
												isLoading ||
												!price ||
												!productType ||
												!subStore ||
												!installments ||
												!isDownPaymentValid ||
												(productType === 'other' && (!productName || !canRegisterOtherProduct)) ||
												(['phone', 'tablet'].includes(productType) && !productManufacturer) ||
												loadingVariableFactor ||
												!isFinancingCalculationReady ||
												(!activePromo && !eliteFinancingReady)
											}
											loading={isLoading}
											type="submit"
											variant="primary"
											className="w-full md:w-auto"
										>
											Siguiente
										</Button>
										<Button
											Icon={<ArrowLeft className="size-5" />}
											disabled={isLoading}
											type="button"
											onClick={backToWelcomePage}
											variant="secondary"
											className="w-full md:w-auto"
										>
											Atrás
										</Button>
									</div>
								)}
							</form>
						</>
					)}
					{page === 6 && ( //done
						<>
							{render_page_header(
								'Información de Pagos',
								'A continuación puedes observar todas las cuotas y fechas correspondientes',
								<Receipt />
							)}

							<form
								className="space-y-6"
								onSubmit={(e) => {
									e.preventDefault();
									handleTermsAccepted();
								}}
							>
								<div className="mx-auto max-w-3xl rounded-xl border border-neutral-700/50 bg-neutral-800/25 p-4 backdrop-blur-sm md:p-6">
									<h2 className="mb-6 text-center text-xl font-semibold text-neutral-0">Inicial + {installments} Cuotas</h2>

									<div className="flex items-center justify-between border-b border-neutral-700/50 py-3">
										<span className="font-bold text-neutral-300">Cuota Inicial (Hoy)</span>
										<span className="font-bold text-primary-400">
											{CountrySettings.currency_code}
											{format_currency(initialPaymentAmount, country === 'rd')}
										</span>
									</div>

									<div className="divide-y divide-neutral-700/50">
										{getNextXDates(installments).map((item, index) => (
											<div key={index} className="flex items-center justify-between py-3">
												<span className="text-neutral-300">{item[0] + ' ' + item[1]}</span>
												<span className="font-medium text-primary-400">
													{CountrySettings.currency_code}
													{format_currency(installmentsAmount, country === 'rd')}
												</span>
											</div>
										))}
									</div>
								</div>

								<div className="mx-auto mt-2 max-w-3xl space-y-3 rounded-xl border border-primary-500/20 bg-primary-500/5 p-4 light:border-primary-600/30 light:bg-primary-600/5 md:p-6">
									<h3 className="text-lg font-semibold text-primary-400 light:text-black">Detalle de la Compra</h3>

									<div className="space-y-3">
										<div className="flex items-center justify-between">
											<span className="text-neutral-300">Subtotal</span>
											<span className="font-medium text-primary-400 light:text-primary-600">
												{CountrySettings.currency_code}
												{format_currency(price, country === 'rd')}
											</span>
										</div>

										<div className="flex items-center justify-between">
											<span className="max-w-[60%] text-sm text-neutral-300 md:text-base">
												{country === 'venezuela'
													? 'Servicio de Integración Tecnológica y Avalúo Comercial'
													: 'Cargos Administrativos e Intereses'}
											</span>
											{discountedAmount && discountedPercentage ? (
												<div className="text-right">
													<div className="flex items-center justify-end gap-2">
														<p className="text-sm font-medium text-neutral-400 line-through md:text-base">
															{CountrySettings.currency_code}
															{format_currency(totalAmount - price + discountedAmount, country === 'rd')}
														</p>
													</div>
													<p className="font-medium text-primary-400 light:text-primary-600">
														{CountrySettings.currency_code}
														{format_currency(totalAmount - price, country === 'rd')}
													</p>
												</div>
											) : (
												<span className="font-medium text-primary-400 light:text-primary-600">
													{CountrySettings.currency_code}
													{format_currency(totalAmount - price, country === 'rd')}
												</span>
											)}
										</div>

										<div className="flex items-center justify-between border-t border-neutral-700/50 pt-3">
											<span className="text-lg font-semibold text-neutral-0">Total General</span>
											<span className="text-lg font-bold text-primary-400 light:text-primary-600">
												{CountrySettings.currency_code}
												{format_currency(totalAmount, country === 'rd')}
											</span>
										</div>
									</div>
									{discountedAmount && discountedPercentage && userFinancingOptions?.level_information ? (
										<div className="mt-4 rounded-lg bg-primary-500/10 p-3">
											<div className="flex items-center gap-2">
												<div className="flex-shrink-0">
													<span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500/30">
														<TicketPercent className="size-4" />
													</span>
												</div>
												<p className="text-xs text-neutral-200 md:text-sm">
													<span
														className="font-semibold"
														style={{
															color: getLevelColor(userFinancingOptions.level_information.name, 'text'),
															filter: 'brightness(1.1) contrast(1.1)'
														}}
													>
														¡Descuento nivel {userFinancingOptions.level_information.name}!
													</span>{' '}
													Estás ahorrando{' '}
													<span className="font-bold text-green-400 light:text-green-500">
														{CountrySettings.currency_code}
														{format_currency(discountedAmount, country === 'rd')}
													</span>{' '}
													en esta compra gracias a tu nivel de cliente.
												</p>
											</div>
										</div>
									) : null}
								</div>

								{formError && <Notice message={formError} variant="error" />}

								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button
										disabled={isLoading || (productType === 'other' && !canRegisterOtherProduct)}
										loading={isLoading}
										type="submit"
										variant="primary"
									>
										{productType === 'other' ? 'Finalizar Venta' : 'Siguiente'}
									</Button>
									<Button
										Icon={<ArrowLeft className="size-5" />}
										disabled={isLoading}
										type="button"
										onClick={() => goToPage(4)}
										variant="secondary"
									>
										Atrás
									</Button>
								</div>
							</form>
						</>
					)}
					{page === 7 &&
						(onlineSalesBetaAccess && onlineSaleRegistered ? (
							<>
								{render_page_header(
									'Venta Online Guardada',
									'La venta ha sido registrada para completar la configuración más adelante.',
									<CircleCheck />
								)}
								<div className="space-y-6 rounded-xl border border-primary-500/20 bg-primary-500/5 p-6 text-center light:border-primary-600/20 light:bg-primary-600/5">
									<div className="flex flex-col items-center gap-4">
										<div className="rounded-full bg-neutral-900 p-4">
											<CircleCheck className="h-7 w-7 text-primary-500" />
										</div>
										<div className="space-y-2">
											<h3 className="text-xl font-semibold text-primary-400 light:text-primary-600">
												¡Venta registrada exitosamente!
											</h3>
											<p className="text-neutral-300">
												Podrás continuar con la configuración del dispositivo cuando el cliente esté listo ingresando a la sección
												&quot;Ventas Online&quot;.
											</p>
										</div>
									</div>
									<div className="flex flex-wrap justify-center gap-2">
										<Button type="button" onClick={goToOnlineSales} variant="primary">
											Ir a Ventas Online
										</Button>
										<Button type="button" onClick={backToWelcomePage} variant="secondary" Icon={<ArrowLeft className="size-5" />}>
											Volver al inicio
										</Button>
									</div>
								</div>
							</>
						) : (
							<>
								{render_page_header(
									'Configuración de Seguridad',
									'Ahora, el encargado(a) en la tienda va a configurar nuestro sistema de bloqueo en el dispositivo, lo cual le dará permiso a Krece para bloquear y desbloquear el equipo en caso de que se atrase con sus pagos.',
									<Shield className="h-6 w-6 text-primary-500" />
								)}

								<form
									className="space-y-6"
									onSubmit={(e) => {
										e.preventDefault();
										listenToAnexoDocumentByIdentificationNumber();
										setInstallationSession(null);
										setDeviceManagementProvider('');
										goToPage(productType === 'laptop' ? 22 : 10);
									}}
								>
									{onlineSalesBetaAccess && !pre_registered_device_id && (
										<div className="space-y-3 rounded-lg border border-primary-500/30 bg-primary-500/5 p-4 light:border-primary-600/30 light:bg-primary-600/5">
											<div className="flex items-start gap-3">
												<div className="shrink-0 rounded-full bg-neutral-900 p-2 light:bg-neutral-900/70">
													<Info className="h-5 w-5 text-primary-400 light:text-primary-600" />
												</div>
												<div className="space-y-1">
													<h4 className="text-sm font-semibold text-neutral-0 md:text-base">Guardar venta online</h4>
													<p className="text-sm text-neutral-300">
														Guarda esta venta y finaliza la configuración más adelante. Podrás continuar el proceso desde Ventas
														Online cuando el equipo esté listo para su registro.
													</p>
												</div>
											</div>
											<Button
												type="button"
												variant="primary"
												onClick={handleSaveOnlineSale}
												loading={savingOnlineSale}
												disabled={savingOnlineSale || isLoading}
											>
												Guardar y registrar venta online
											</Button>
										</div>
									)}

									{formError && <Notice message={formError} variant="error" />}

									<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
										<Button disabled={isLoading || savingOnlineSale} loading={isLoading} type="submit" variant="primary">
											Comenzar Configuración
										</Button>

										<Button
											Icon={<ArrowLeft className="size-5" />}
											disabled={isLoading || savingOnlineSale}
											type="button"
											onClick={backToWelcomePage}
											variant="secondary"
										>
											Atrás
										</Button>
									</div>
								</form>
							</>
						))}
					{page === 10 && (
						<>
							{render_page_header(
								'Configuración de seguridad',
								productType === 'laptop'
									? 'Ingrese el serial después de completar la instalación.'
									: 'Para comenzar, asegúrese de que el equipo se encuentre apagado.',
								<Shield />
							)}
							<form
								className="space-y-6"
								onSubmit={(e) => {
									e.preventDefault();
									registerDevice();
								}}
							>
								<Input
									floatingLabel={productType === 'laptop' ? 'Código serial' : 'IMEI del equipo'}
									value={imeiToRegister}
									onChange={(value) => {
										setImeiToRegister(value);
										setInstallationSession(null);
									}}
									placeholder="Ingrese el identificador del equipo"
								/>
								<Input
									floatingLabel={productType === 'laptop' ? 'Confirme el código serial' : 'Confirme el IMEI del equipo'}
									value={imeiToRegister2}
									onChange={setImeiToRegister2}
									placeholder="Confirme el identificador"
									allowPaste={false}
								/>
								{productType !== 'laptop' && (
									<button
										type="button"
										onClick={() => setShowQrScanner(true)}
										className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-neutral-700 bg-neutral-950/50 px-4 py-2.5 text-sm text-neutral-300 transition-colors hover:border-neutral-500 hover:bg-neutral-900/50 hover:text-neutral-100"
									>
										<ScanLine className="size-4" />
										Escanear código QR de la caja
									</button>
								)}
								{showQrScanner && (
									<QrImeiScanner
										onScan={(imei) => {
											setImeiToRegister(imei);
											setImeiToRegister2(imei);
											setInstallationSession(null);
											setShowQrScanner(false);
											setFormError('');
										}}
										onClose={() => setShowQrScanner(false)}
									/>
								)}
								{formError && <Notice message={getEnrollmentError(formError)} variant="error" />}
								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button variant="primary" type="submit" loading={isLoading}>
										Siguiente
									</Button>
									<Button
										variant="secondary"
										disabled={isLoading}
										Icon={<ArrowLeft className="size-5" />}
										onClick={() => goToPage(productType === 'laptop' ? 22 : 7)}
									>
										Atrás
									</Button>
								</div>
							</form>
						</>
					)}
					{page === 22 && (
						<>
							{render_page_header('Preparar la laptop', 'Sigue estos pasos antes de registrar el serial.', <Laptop />)}
							<div className="space-y-6">
								<LaptopEnrollmentInstructions />
								<EnrollmentReady checked={enrollmentReady} onChange={setEnrollmentReady} />
								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button variant="primary" disabled={!enrollmentReady} onClick={() => goToPage(10)}>
										Ingresar serial
									</Button>
									<Button variant="secondary" Icon={<ArrowLeft className="size-5" />} onClick={() => goToPage(7)}>
										Atrás
									</Button>
								</div>
							</div>
						</>
					)}
					{page === 23 && (
						<>
							{render_page_header('Configuración de seguridad', null, <Shield />)}
							<div className="space-y-6">
								<EnrollmentIdentity imei={imeiToRegister} deviceType={productType} />
								<NuovoEnrollmentInstructions country={country} />
								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button variant="primary" onClick={() => goToPage(11)}>
										Continuar con permisos
									</Button>
									<Button variant="secondary" Icon={<ArrowLeft className="size-5" />} onClick={() => goToPage(10)}>
										Atrás
									</Button>
								</div>
								<Button variant="text" onClick={() => goToPage(14)}>
									Ya lo instalé, ingresar código
								</Button>
							</div>
						</>
					)}
					{page === 11 && ( //done
						<>
							{render_page_header('Instrucciones de configuración', 'Haga clic en aceptar cuando se le solicite', <Settings />)}

							<form
								className="space-y-6"
								onSubmit={(e) => {
									e.preventDefault();
									goToPage(12);
								}}
							>
								<div className="pos-enrollment-grid grid grid-cols-3 gap-4">
									<img className="w-full" src={paso2} alt="Paso 2" />
									<img className="w-full" src={paso3} alt="Paso 3" />
									<img className="w-full" src={paso4} alt="Paso 4" />
									<img className="w-full" src={paso5} alt="Paso 5" />
								</div>

								<div className="flex flex-col gap-2">
									<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
										<Button disabled={isLoading} loading={isLoading} type="submit" variant="primary">
											Seguir
										</Button>
										<Button
											Icon={<ArrowLeft className="size-5" />}
											disabled={isLoading}
											type="button"
											onClick={() => goToPage(23)}
											variant="secondary"
										>
											Atrás
										</Button>
									</div>

									<Button disabled={isLoading} type="button" onClick={() => goToPage(14)} variant="secondary" className="w-full">
										Saltar Instrucciones
										<ArrowRight className="size-5" />
									</Button>
								</div>
							</form>
						</>
					)}
					{page === 12 && ( //done
						<>
							{render_page_header(
								'Instrucciones de configuración',
								'Haga clic en "Ajustes adicionales" y después en "PERMITIR"',
								<Settings />
							)}

							<form
								className="space-y-6"
								onSubmit={(e) => {
									e.preventDefault();
									goToPage(13);
								}}
							>
								<SecurityPermissionInstructions step="background" />

								<div className="flex flex-col gap-2">
									<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
										<Button disabled={isLoading} loading={isLoading} type="submit" variant="primary">
											Seguir
										</Button>
										<Button
											Icon={<ArrowLeft className="size-5" />}
											disabled={isLoading}
											type="button"
											onClick={() => goToPage(11)}
											variant="secondary"
										>
											Atrás
										</Button>
									</div>

									<Button disabled={isLoading} type="button" onClick={() => goToPage(14)} variant="secondary" className="w-full">
										Saltar Instrucciones
										<ArrowRight className="size-5" />
									</Button>
								</div>
							</form>
						</>
					)}
					{page === 13 && ( //done
						<>
							{render_page_header(
								'Instrucciones de configuración',
								'Siga las instrucciones que se le muestran en la pantalla y cuando esté listo haga clic en "Configuración Completa"',
								<Settings />
							)}

							<form
								className="space-y-6"
								onSubmit={(e) => {
									e.preventDefault();
									goToPage(14);
								}}
							>
								<SecurityPermissionInstructions step="complete" />

								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button disabled={isLoading} loading={isLoading} type="submit" variant="primary">
										Seguir
									</Button>
									<Button
										Icon={<ArrowLeft className="size-5" />}
										disabled={isLoading}
										type="button"
										onClick={() => goToPage(12)}
										variant="secondary"
									>
										Atrás
									</Button>
								</div>
							</form>
						</>
					)}
					{page === 14 && (
						<>
							{render_page_header(
								'Confirmar el equipo',
								'Introduce el código de confirmación que aparece al finalizar la instalación.',
								<Shield />
							)}
							<form
								className="space-y-6"
								onSubmit={(e) => {
									e.preventDefault();
									continueDeviceConfirmation();
								}}
							>
								<EnrollmentIdentity imei={imeiToRegister} deviceType={productType} />
								<Input
									floatingLabel="Código de confirmación"
									value={deviceID}
									onChange={setDeviceID}
									placeholder="Al menos 7 dígitos"
									allowedChars="numbers_only"
								/>
								<div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-2">
									<Button
										variant="secondary"
										onClick={() => setShowImei(!showImei)}
										className="w-full !border-yellow-500/50 !bg-yellow-500/20 text-xs !text-yellow-200 hover:!bg-yellow-500/30 light:!text-black"
										Icon={<Search className="size-3" />}
									>
										¿No encuentra el código de confirmación?
									</Button>
								</div>
								{showImei && (
									<div className="space-y-4 rounded-lg border border-neutral-800 p-4">
										<Input
											floatingLabel="Número IMEI del equipo"
											value={imeiNumber}
											onChange={setImeiNumber}
											placeholder="Ingrese el número IMEI"
										/>
										{loginError && <Notice message={getEnrollmentError(loginError)} variant="error" />}
										<Button
											Icon={<Search className="size-5" />}
											onClick={searchConfirmationNumber}
											variant="secondary"
											loading={isSearchingConfirmationNumber}
										>
											Buscar
										</Button>
										{showConfNumber && (
											<Notice message={`El código de confirmación de este equipo es: ${confNumber}`} variant="success" />
										)}
									</div>
								)}
								{formError && <Notice message={getEnrollmentError(formError)} variant="error" />}
								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button disabled={isLoading} loading={isLoading} type="submit" variant="primary">
										Continuar
									</Button>
									<Button
										Icon={<ArrowLeft className="size-5" />}
										disabled={isLoading}
										onClick={() => goToPage(23)}
										variant="secondary"
									>
										Atrás
									</Button>
								</div>
							</form>
						</>
					)}
					{page === 15 && ( //done
						<>
							{render_page_header('¡Registro Completado!', null, <CircleCheck />)}

							<div className="mb-8 rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-6">
								<div className="flex items-start gap-4">
									<div className="shrink-0">
										<AlertTriangle className="h-6 w-6 text-yellow-500" />
									</div>
									<div className="space-y-1">
										<h3 className="font-medium text-yellow-500">IMPORTANTE</h3>
										<p className="text-neutral-300">
											Antes de que el cliente se lleve su producto, asegúrese que le llegó un email de confirmación a la tienda,
											eso se utilizará como comprobante en caso de cualquier problema que ocurra.
										</p>
									</div>
								</div>
							</div>

							<form
								className="space-y-6"
								onSubmit={(e) => {
									e.preventDefault();
									backToWelcomePage();
								}}
							>
								<Button type="submit" variant="primary">
									Regresar al Inicio
								</Button>
							</form>
						</>
					)}

					{limitIncreaseDialog && (
						<Dialog
							closable={limitIncreaseDialog.status !== 'requesting'}
							cerrarButton={false}
							disableOverlayClose={true}
							onClose={() => {
								limitIncreaseRetryRef.current = null;
								setLimitIncreaseDialog(null);
							}}
						>
							{(() => {
								const isApproved = limitIncreaseDialog.status === 'approved';
								const isRejected = limitIncreaseDialog.status === 'rejected';
								const StatusIcon = isApproved ? CircleCheck : isRejected ? AlertTriangle : TrendingUp;
								const heading = isApproved
									? 'Aumento aprobado'
									: isRejected
										? 'No pudimos aumentar el límite'
										: 'Límite de ventas alcanzado';
								const description = isApproved
									? `Tu nuevo límite diario es de ${limitIncreaseDialog.newLimit} ventas. Ya puedes continuar con la venta.`
									: isRejected
										? 'Actualmente no podemos aumentar el límite. Contacta a soporte para solicitar un aumento mayor.'
										: 'Llegaste al tope de ventas de tu plan actual. Solicita un aumento para seguir vendiendo sin interrupciones.';
								const shownLimit = isApproved ? limitIncreaseDialog.newLimit : limitIncreaseDialog.dailyLimit;
								const devicesSold = limitIncreaseDialog.devicesSold;
								const hasStats =
									!isRejected && Number.isFinite(devicesSold) && Number.isFinite(shownLimit) && shownLimit > 0;
								const progressPct = hasStats ? Math.min(100, Math.round((devicesSold / shownLimit) * 100)) : 0;

								return (
									<div className="flex w-full max-w-md flex-col gap-5">
										<div
											className={`flex size-12 items-center justify-center rounded-xl ${
												isRejected ? 'bg-red-500/15' : 'bg-primary-500/15'
											}`}
										>
											<StatusIcon
												className={`size-6 ${isRejected ? 'text-red-400' : 'text-primary-400'}`}
												strokeWidth={2}
											/>
										</div>

										<div className="flex flex-col gap-2">
											<h3 className="text-xl font-semibold text-neutral-50">{heading}</h3>
											<p className="text-sm leading-relaxed text-neutral-300">{description}</p>
										</div>

										{hasStats && (
											<div className="flex flex-col gap-2.5 rounded-lg border border-neutral-800 bg-neutral-950/40 p-4">
												<div className="flex items-center justify-between">
													<span className="text-sm text-neutral-400">Ventas de hoy</span>
													<span className="text-sm font-semibold text-neutral-50">
														{devicesSold} / {shownLimit}
													</span>
												</div>
												<div className="h-2 w-full overflow-hidden rounded-full bg-neutral-800">
													<div
														className="h-full rounded-full bg-primary-500 transition-all duration-300"
														style={{ width: `${progressPct}%` }}
													/>
												</div>
											</div>
										)}

										<div className="flex justify-end gap-2">
											{limitIncreaseDialog.status === 'prompt' && (
												<Button
													variant="secondary"
													className="!w-auto"
													onClick={() => {
														limitIncreaseRetryRef.current = null;
														setLimitIncreaseDialog(null);
													}}
												>
													Cancelar
												</Button>
											)}
											{['prompt', 'requesting'].includes(limitIncreaseDialog.status) && (
												<Button
													variant="primary"
													className="!w-auto"
													loading={limitIncreaseDialog.status === 'requesting'}
													onClick={requestDailyLimitIncrease}
												>
													Solicitar aumento
												</Button>
											)}
											{isApproved && (
												<Button variant="primary" className="!w-auto" onClick={continueAfterLimitIncrease}>
													Continuar venta
												</Button>
											)}
											{isRejected && (
												<Button
													variant="primary"
													className="!w-auto"
													onClick={() => {
														limitIncreaseRetryRef.current = null;
														setLimitIncreaseDialog(null);
													}}
												>
													Entendido
												</Button>
											)}
										</div>
									</div>
								);
							})()}
						</Dialog>
					)}
					{showDialog && (
						<Dialog onClose={() => setShowDialog(false)}>
							<div dangerouslySetInnerHTML={{ __html: dialogMessage }} />
						</Dialog>
					)}
					{showConfirmDialog && (
						<Dialog closable={false} title="Confirmar" onClose={() => setShowConfirmDialog(false)}>
							<div className="flex flex-col gap-4">
								<div dangerouslySetInnerHTML={{ __html: dialogMessage }} />
								<div className="flex justify-end gap-2">
									<button
										onClick={() => setShowConfirmDialog(false)}
										className="inline-flex h-[35px] items-center justify-center rounded-lg px-[15px] font-medium leading-none text-neutral-600 transition-colors hover:bg-neutral-900/50 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
									>
										Cancelar
									</button>
									<button
										onClick={() => {
											setShowConfirmDialog(false);
										}}
										className="inline-flex h-[35px] items-center justify-center rounded-lg bg-primary-500 px-[15px] font-medium leading-none text-black transition-colors hover:bg-primary-600 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
									>
										Confirmar
									</button>
								</div>
							</div>
						</Dialog>
					)}
				</div>
			</div>
		</div>
	);

	function render_page_header(title, description, icon) {
		return (
			<div className="pos-page-header mb-4 flex flex-col items-center gap-2 md:mb-6 md:gap-3">
				<div className="relative">
					<div className="absolute -inset-1 rounded-full bg-gradient-to-r from-primary-400 to-primary-600 opacity-10 blur"></div>
					<div className="pos-page-icon relative rounded-full bg-neutral-900 p-4 light:bg-neutral-1000 light:ring-2 light:ring-primary-500/10">
						{icon && React.cloneElement(icon, { className: 'h-6 w-6 text-primary-500' })}
					</div>
				</div>
				<div className="space-y-1 text-center">
					<h1 className="text-2xl font-semibold tracking-tighter text-neutral-0 md:text-3xl">{title}</h1>
					{description && <p className="text-neutral-300">{description}</p>}
				</div>
			</div>
		);
	}
};

export default NewSale;
